import { Token, Percent, ERC20Token, CurrencyAmount as CurrencyAmount$1, PairV1, PairV2, RouteV1 as RouteV1$1, RouteV2 as RouteV2$1, TradeType, Fraction, InsufficientInputAmountError, InsufficientReservesError, Ether, Price, WNATIVE, ZERO, ONE, TradeV1, TradeV2, validateAndParseAddress, sortedInsert } from '@pulsex/sdk';
import axios3 from 'axios';
import _16 from 'lodash';
import { ChainId } from '@pulsex/chains';
import { parseUnits, getContract, encodeFunctionData, decodeFunctionResult, toBytes, hexToString, encodePacked } from 'viem';
import { FeeAmount, Position, TickMath, SqrtPriceMath, SelfPermit, Payments, toHex, NonfungiblePositionManager, Multicall } from '@pulsex/v3-sdk';
import invariant3 from 'tiny-invariant';
import retry7 from 'async-retry';
import NodeCache from 'node-cache';
import FixedReverseHeap from 'mnemonist/fixed-reverse-heap.js';
import Queue from 'mnemonist/queue.js';
import stats from 'stats-lite';
import { multicallByGasLimit } from '@pulsex/multicall';
import { MaxUint256 } from '@pulsex/swap-sdk-core';
import Timeout from 'await-timeout';
import { GraphQLClient, gql } from 'graphql-request';

// src/providers/cache-node.ts
var NodeJSCache = class {
  constructor(nodeCache) {
    this.nodeCache = nodeCache;
  }
  async get(key) {
    return this.nodeCache.get(key);
  }
  async set(key, value) {
    return this.nodeCache.set(key, value);
  }
  async has(key) {
    return this.nodeCache.has(key);
  }
};

// src/util/log.ts
var NullLogger = class {
  constructor() {
    this.ERROR_MESSAGE = 'NullLogger does not support. Instantiate a valid logger using "setGlobalLogger"';
    this.src = true;
  }
  addStream(_stream) {
    throw new Error(this.ERROR_MESSAGE);
  }
  addSerializers(_serializers) {
    throw new Error(this.ERROR_MESSAGE);
  }
  child(_options, _simple) {
    return this;
  }
  reopenFileStreams() {
    throw new Error(this.ERROR_MESSAGE);
  }
  level(_value) {
    return;
  }
  levels(_name, _value) {
    return;
  }
  trace(..._rest) {
    return true;
  }
  debug(..._rest) {
    return true;
  }
  info(..._rest) {
    return true;
  }
  warn(..._rest) {
    return true;
  }
  error(..._rest) {
    return true;
  }
  fatal(..._rest) {
    return true;
  }
  addListener(_event, _listener) {
    throw new Error(this.ERROR_MESSAGE);
  }
  on(_event, _listener) {
    throw new Error(this.ERROR_MESSAGE);
  }
  once(_event, _listener) {
    throw new Error(this.ERROR_MESSAGE);
  }
  removeListener(_event, _listener) {
    throw new Error(this.ERROR_MESSAGE);
  }
  off(_event, _listener) {
    throw new Error(this.ERROR_MESSAGE);
  }
  removeAllListeners(_event) {
    throw new Error(this.ERROR_MESSAGE);
  }
  setMaxListeners(_n) {
    throw new Error(this.ERROR_MESSAGE);
  }
  getMaxListeners() {
    throw new Error(this.ERROR_MESSAGE);
  }
  listeners(_event) {
    throw new Error(this.ERROR_MESSAGE);
  }
  rawListeners(_event) {
    throw new Error(this.ERROR_MESSAGE);
  }
  emit(_event, ..._args) {
    throw new Error(this.ERROR_MESSAGE);
  }
  listenerCount(_event) {
    throw new Error(this.ERROR_MESSAGE);
  }
  prependListener(_event, _listener) {
    throw new Error(this.ERROR_MESSAGE);
  }
  prependOnceListener(_event, _listener) {
    throw new Error(this.ERROR_MESSAGE);
  }
  eventNames() {
    throw new Error(this.ERROR_MESSAGE);
  }
};
var log = new NullLogger();
var setGlobalLogger = (_log) => {
  log = _log;
};

// src/providers/caching-gas-provider.ts
var CachingGasStationProvider = class {
  /**
   * Creates an instance of CachingGasStationProvider.
   * @param chainId The chain id to use.
   * @param gasPriceProvider The provider to use to get the gas price when not in the cache.
   * @param cache Cache instance to hold cached pools.
   */
  constructor(chainId, gasPriceProvider, cache) {
    this.chainId = chainId;
    this.gasPriceProvider = gasPriceProvider;
    this.cache = cache;
    this.GAS_KEY = (chainId) => `gasPrice-${chainId}`;
  }
  async getGasPrice() {
    const cachedGasPrice = await this.cache.get(this.GAS_KEY(this.chainId));
    if (cachedGasPrice) {
      log.info(
        { cachedGasPrice },
        `Got gas station price from local cache: ${cachedGasPrice.gasPriceWei}.`
      );
      return cachedGasPrice;
    }
    log.info("Gas station price local cache miss.");
    const gasPrice = await this.gasPriceProvider.getGasPrice();
    await this.cache.set(this.GAS_KEY(this.chainId), gasPrice);
    return gasPrice;
  }
};

// src/util/metric.ts
var MetricLoggerUnit = /* @__PURE__ */ ((MetricLoggerUnit2) => {
  MetricLoggerUnit2["Seconds"] = "Seconds";
  MetricLoggerUnit2["Microseconds"] = "Microseconds";
  MetricLoggerUnit2["Milliseconds"] = "Milliseconds";
  MetricLoggerUnit2["Bytes"] = "Bytes";
  MetricLoggerUnit2["Kilobytes"] = "Kilobytes";
  MetricLoggerUnit2["Megabytes"] = "Megabytes";
  MetricLoggerUnit2["Gigabytes"] = "Gigabytes";
  MetricLoggerUnit2["Terabytes"] = "Terabytes";
  MetricLoggerUnit2["Bits"] = "Bits";
  MetricLoggerUnit2["Kilobits"] = "Kilobits";
  MetricLoggerUnit2["Megabits"] = "Megabits";
  MetricLoggerUnit2["Gigabits"] = "Gigabits";
  MetricLoggerUnit2["Terabits"] = "Terabits";
  MetricLoggerUnit2["Percent"] = "Percent";
  MetricLoggerUnit2["Count"] = "Count";
  MetricLoggerUnit2["BytesPerSecond"] = "Bytes/Second";
  MetricLoggerUnit2["KilobytesPerSecond"] = "Kilobytes/Second";
  MetricLoggerUnit2["MegabytesPerSecond"] = "Megabytes/Second";
  MetricLoggerUnit2["GigabytesPerSecond"] = "Gigabytes/Second";
  MetricLoggerUnit2["TerabytesPerSecond"] = "Terabytes/Second";
  MetricLoggerUnit2["BitsPerSecond"] = "Bits/Second";
  MetricLoggerUnit2["KilobitsPerSecond"] = "Kilobits/Second";
  MetricLoggerUnit2["MegabitsPerSecond"] = "Megabits/Second";
  MetricLoggerUnit2["GigabitsPerSecond"] = "Gigabits/Second";
  MetricLoggerUnit2["TerabitsPerSecond"] = "Terabits/Second";
  MetricLoggerUnit2["CountPerSecond"] = "Count/Second";
  MetricLoggerUnit2["None"] = "None";
  return MetricLoggerUnit2;
})(MetricLoggerUnit || {});
var IMetric = class {
};
var MetricLogger = class extends IMetric {
  constructor(context) {
    super();
    this.log = log.child(context || {});
  }
  setProperty(key, value) {
    this.log = this.log.child({ [key]: value });
  }
  putDimensions(dimensions) {
    this.log = this.log.child(dimensions);
  }
  putMetric(key, value, unit) {
    this.log.info(
      { key, value, unit },
      `[Metric]: ${key}: ${value} | ${unit ? unit : ""}`
    );
  }
};
var metric = new MetricLogger();
var setGlobalMetric = (_metric) => {
  metric = _metric;
};

// src/providers/caching-token-list-provider.ts
var CachingTokenListProvider = class {
  /**
   * Creates an instance of CachingTokenListProvider.
   * Token metadata (e.g. symbol and decimals) generally don't change so can be cached indefinitely.
   *
   * @param chainId The chain id to use.
   * @param tokenList The token list to get the tokens from.
   * @param tokenCache Cache instance to hold cached tokens.
   */
  constructor(chainId, tokenList, tokenCache) {
    this.tokenCache = tokenCache;
    this.CACHE_KEY = (tokenInfo) => `token-list-token-${this.chainId}/${this.tokenList.name}/${this.tokenList.timestamp}/${this.tokenList.version}/${tokenInfo.address.toLowerCase()}/${tokenInfo.decimals}/${tokenInfo.symbol}/${tokenInfo.name}`;
    this.chainId = chainId;
    this.tokenList = tokenList;
    this.chainToTokenInfos = _16.reduce(
      this.tokenList.tokens,
      (result, tokenInfo) => {
        const chainId2 = tokenInfo.chainId.toString();
        if (!result[chainId2]) {
          result[chainId2] = [];
        }
        result[chainId2].push(tokenInfo);
        return result;
      },
      {}
    );
    this.chainSymbolToTokenInfo = _16.mapValues(
      this.chainToTokenInfos,
      (tokenInfos) => _16.keyBy(tokenInfos, "symbol")
    );
    this.chainAddressToTokenInfo = _16.mapValues(
      this.chainToTokenInfos,
      (tokenInfos) => _16.keyBy(tokenInfos, (tokenInfo) => tokenInfo.address.toLowerCase())
    );
  }
  static async fromTokenListURI(chainId, tokenListURI, tokenCache) {
    const now = Date.now();
    const tokenList = await this.buildTokenList(tokenListURI);
    metric.putMetric(
      "TokenListLoad",
      Date.now() - now,
      "Milliseconds" /* Milliseconds */
    );
    return new CachingTokenListProvider(chainId, tokenList, tokenCache);
  }
  static async buildTokenList(tokenListURI) {
    log.info(`Getting tokenList from ${tokenListURI}.`);
    const response = await axios3.get(tokenListURI);
    log.info(`Got tokenList from ${tokenListURI}.`);
    const { data: tokenList, status } = response;
    if (status != 200) {
      log.error(
        { response },
        `Unabled to get token list from ${tokenListURI}.`
      );
      throw new Error(`Unable to get token list from ${tokenListURI}`);
    }
    return tokenList;
  }
  static async fromTokenList(chainId, tokenList, tokenCache) {
    const now = Date.now();
    const tokenProvider = new CachingTokenListProvider(
      chainId,
      tokenList,
      tokenCache
    );
    metric.putMetric(
      "TokenListLoad",
      Date.now() - now,
      "Milliseconds" /* Milliseconds */
    );
    return tokenProvider;
  }
  async getTokens(_addresses) {
    const addressToToken = {};
    const symbolToToken = {};
    for (const address of _addresses) {
      const token = await this.getTokenByAddress(address);
      if (!token) {
        continue;
      }
      addressToToken[address.toLowerCase()] = token;
      if (!token.symbol) {
        continue;
      }
      symbolToToken[token.symbol.toLowerCase()] = token;
    }
    return {
      getTokenByAddress: (address) => addressToToken[address.toLowerCase()],
      getTokenBySymbol: (symbol) => symbolToToken[symbol.toLowerCase()],
      getAllTokens: () => {
        return Object.values(addressToToken);
      }
    };
  }
  async getTokenBySymbol(_symbol) {
    let symbol = _symbol;
    if (_symbol == "PLS") {
      symbol = "WPLS";
    }
    if (!this.chainSymbolToTokenInfo[this.chainId.toString()]) {
      return void 0;
    }
    const tokenInfo = this.chainSymbolToTokenInfo[this.chainId.toString()][symbol];
    if (!tokenInfo) {
      return void 0;
    }
    const token = await this.buildToken(tokenInfo);
    return token;
  }
  async getTokenByAddress(address) {
    if (!this.chainAddressToTokenInfo[this.chainId.toString()]) {
      return void 0;
    }
    const tokenInfo = this.chainAddressToTokenInfo[this.chainId.toString()][address.toLowerCase()];
    if (!tokenInfo) {
      return void 0;
    }
    const token = await this.buildToken(tokenInfo);
    return token;
  }
  async buildToken(tokenInfo) {
    const cacheKey = this.CACHE_KEY(tokenInfo);
    const cachedToken = await this.tokenCache.get(cacheKey);
    if (cachedToken) {
      return cachedToken;
    }
    const token = new Token(
      this.chainId,
      tokenInfo.address,
      tokenInfo.decimals,
      tokenInfo.symbol,
      tokenInfo.name
    );
    await this.tokenCache.set(cacheKey, token);
    return token;
  }
};
var PULSEX_QUOTER_ADDRESSES = {
  [ChainId.PULSECHAIN]: "0xdd6Aec93F7b76416Ad9e72094C2CE6f4c47C2e02",
  [ChainId.PULSECHAIN_TESTNET]: "0x470bE60eaBB2fdf458BB5887db072193e5CA17D6"
};
var PULSEX_MULTICALL_ADDRESSES = {
  [ChainId.PULSECHAIN]: "0xD77D4b2F85590628Adc0D42189fD3Af4B9679d62",
  [ChainId.PULSECHAIN_TESTNET]: "0xA2313da2da9bf47DCE5f3cd36BE827ECd72aE685"
};
var PULSEX_SWAP_ROUTER_ADDRESSES = {
  [ChainId.PULSECHAIN]: "0xa619F23c632CA9f36CD4Dcea6272E1eA174aAC27",
  [ChainId.PULSECHAIN_TESTNET]: "0xbaE59Ee3b1d472BF67f11a0BC91273a9e71eF4Cf"
};
var TOKEN_VALIDATOR_ADDRESSES = {
  [ChainId.PULSECHAIN]: "0xac40F373Dd1eE9a943C1dfCb652feD469264D9bA",
  [ChainId.PULSECHAIN_TESTNET]: "0xc1b06BC42Cb6476D99847E4d469d9c549c1322d3"
};
var MULTICALL2_ADDRESS = "0xdc6ed7C2EE5ab7fd303334B34F7524Cbb463DE57";
var WETH9 = {};
var ARB_GASINFO_ADDRESS = "0x000000000000000000000000000000000000006C";
var OVM_GASPRICE_ADDRESS = "0x420000000000000000000000000000000000000F";
var V3_CORE_FACTORY_ADDRESSES = {
  [ChainId.PULSECHAIN]: "0x0000000000000000000000000000000000000000",
  [ChainId.PULSECHAIN_TESTNET]: "0x0000000000000000000000000000000000000000"
};
var QUOTER_V2_ADDRESSES = {
  [ChainId.PULSECHAIN]: "0x0000000000000000000000000000000000000000",
  [ChainId.PULSECHAIN_TESTNET]: "0x0000000000000000000000000000000000000000"
};
var PERMIT2_ADDRESS = "0x000000000022D473030F116dDEE9F6B43aC78BA3";
var UNIVERSAL_ROUTER_ADDRESS = "0x3fC91A3afd70395Cd496C647d5a6CC9D4B2b7FAD";
var CurrencyAmount = class extends CurrencyAmount$1 {
};
var MAX_UINT160 = BigInt("0xffffffffffffffffffffffffffffffffffffffff");
function parseAmount(value, currency) {
  const typedValueParsed = parseUnits(value, currency.decimals).toString();
  return CurrencyAmount.fromRawAmount(currency, BigInt(typedValueParsed));
}
function parseFeeAmount(feeAmountStr) {
  switch (feeAmountStr) {
    case "10000":
      return FeeAmount.HIGH;
    case "3000":
      return FeeAmount.MEDIUM;
    case "500":
      return FeeAmount.LOW;
    case "100":
      return FeeAmount.LOWEST;
    default:
      throw new Error(`Fee amount ${feeAmountStr} not supported.`);
  }
}
function unparseFeeAmount(feeAmount) {
  switch (feeAmount) {
    case FeeAmount.HIGH:
      return "10000";
    case FeeAmount.MEDIUM:
      return "3000";
    case FeeAmount.LOW:
      return "500";
    case FeeAmount.LOWEST:
      return "100";
    default:
      throw new Error(`Fee amount ${feeAmount} not supported.`);
  }
}
var SUPPORTED_CHAINS = [
  ChainId.PULSECHAIN,
  ChainId.PULSECHAIN_TESTNET
];
var V1_SUPPORTED = [
  ChainId.PULSECHAIN,
  ChainId.PULSECHAIN_TESTNET
];
var V2_SUPPORTED = [
  ChainId.PULSECHAIN,
  ChainId.PULSECHAIN_TESTNET
];
var ID_TO_CHAIN_ID = (id) => {
  switch (id) {
    case 369:
      return ChainId.PULSECHAIN;
    case 943:
      return ChainId.PULSECHAIN_TESTNET;
    default:
      throw new Error(`Unknown chain id: ${id}`);
  }
};
var ChainName = /* @__PURE__ */ ((ChainName2) => {
  ChainName2["PULSECHAIN"] = "pulsechain";
  ChainName2["PULSECHAIN_TESTNET"] = "pulsechainTestnet";
  return ChainName2;
})(ChainName || {});
var NativeCurrencyName = /* @__PURE__ */ ((NativeCurrencyName2) => {
  NativeCurrencyName2["PULSECHAIN"] = "PLS";
  NativeCurrencyName2["PULSECHAIN_TESTNET"] = "tPLS";
  return NativeCurrencyName2;
})(NativeCurrencyName || {});
var NATIVE_NAMES_BY_ID = {
  [ChainId.PULSECHAIN]: [
    "PLS",
    "Pulse",
    "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
  ],
  [ChainId.PULSECHAIN_TESTNET]: [
    "tPLS",
    "Test Pulse",
    "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee"
  ]
};
var NATIVE_CURRENCY = {
  [ChainId.PULSECHAIN]: "PLS" /* PULSECHAIN */,
  [ChainId.PULSECHAIN_TESTNET]: "tPLS" /* PULSECHAIN_TESTNET */
};
var ID_TO_NETWORK_NAME = (id) => {
  switch (id) {
    case 369:
      return "pulsechain" /* PULSECHAIN */;
    case 943:
      return "pulsechainTestnet" /* PULSECHAIN_TESTNET */;
    default:
      throw new Error(`Unknown chain id: ${id}`);
  }
};
var CHAIN_IDS_LIST = Object.values(ChainId).map(
  (c) => c.toString()
);
var ID_TO_PROVIDER = (id) => {
  switch (id) {
    case ChainId.PULSECHAIN:
      return `https://rpc.pulsechain.com`;
    case ChainId.PULSECHAIN_TESTNET:
      return `https://rpc.v4.testnet.pulsechain.com`;
    default:
      throw new Error(`Chain id: ${id} not supported`);
  }
};
var WRAPPED_NATIVE_CURRENCY = {
  [ChainId.PULSECHAIN]: new Token(
    ChainId.PULSECHAIN,
    "0xA1077a294dDE1B09bB078844df40758a5D0f9a27",
    18,
    "WPLS",
    "Wrapped Pulse"
  ),
  [ChainId.PULSECHAIN_TESTNET]: new Token(
    ChainId.PULSECHAIN_TESTNET,
    "0x70499adEBB11Efd915E3b69E700c331778628707",
    18,
    "WPLS",
    "Wrapped Pulse"
  )
};
var _ExtendedEther = class extends Ether {
  get wrapped() {
    if (this.chainId in WRAPPED_NATIVE_CURRENCY)
      return WRAPPED_NATIVE_CURRENCY[this.chainId];
    throw new Error("Unsupported chain ID");
  }
  static onChain(chainId) {
    return this._cachedExtendedEther[chainId] ?? (this._cachedExtendedEther[chainId] = new _ExtendedEther(chainId));
  }
};
var ExtendedEther = _ExtendedEther;
ExtendedEther._cachedExtendedEther = {};
var cachedNativeCurrency = {};
function nativeOnChain(chainId) {
  if (cachedNativeCurrency[chainId] != void 0) {
    return cachedNativeCurrency[chainId];
  } else {
    cachedNativeCurrency[chainId] = ExtendedEther.onChain(chainId);
  }
  return cachedNativeCurrency[chainId];
}

// src/routers/sdk/constants.ts
var MSG_SENDER = "0x0000000000000000000000000000000000000001";
var ADDRESS_THIS = "0x0000000000000000000000000000000000000002";
var V1_FEE_PATH_PLACEHOLDER = 1;
var V2_FEE_PATH_PLACEHOLDER = 2;

// src/abis/IApproveAndCall.ts
var approveAndCallAbi = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      }
    ],
    "name": "approveMax",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      }
    ],
    "name": "approveMaxMinusOne",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      }
    ],
    "name": "approveZeroThenMax",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      }
    ],
    "name": "approveZeroThenMaxMinusOne",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "bytes",
        "name": "data",
        "type": "bytes"
      }
    ],
    "name": "callPositionManager",
    "outputs": [
      {
        "internalType": "bytes",
        "name": "result",
        "type": "bytes"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "getApprovalType",
    "outputs": [
      {
        "internalType": "enum IApproveAndCall.ApprovalType",
        "name": "",
        "type": "uint8"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "token0",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "token1",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "tokenId",
            "type": "uint256"
          },
          {
            "internalType": "uint256",
            "name": "amount0Min",
            "type": "uint256"
          },
          {
            "internalType": "uint256",
            "name": "amount1Min",
            "type": "uint256"
          }
        ],
        "internalType": "struct IApproveAndCall.IncreaseLiquidityParams",
        "name": "params",
        "type": "tuple"
      }
    ],
    "name": "increaseLiquidity",
    "outputs": [
      {
        "internalType": "bytes",
        "name": "result",
        "type": "bytes"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "token0",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "token1",
            "type": "address"
          },
          {
            "internalType": "uint24",
            "name": "fee",
            "type": "uint24"
          },
          {
            "internalType": "int24",
            "name": "tickLower",
            "type": "int24"
          },
          {
            "internalType": "int24",
            "name": "tickUpper",
            "type": "int24"
          },
          {
            "internalType": "uint256",
            "name": "amount0Min",
            "type": "uint256"
          },
          {
            "internalType": "uint256",
            "name": "amount1Min",
            "type": "uint256"
          },
          {
            "internalType": "address",
            "name": "recipient",
            "type": "address"
          }
        ],
        "internalType": "struct IApproveAndCall.MintParams",
        "name": "params",
        "type": "tuple"
      }
    ],
    "name": "mint",
    "outputs": [
      {
        "internalType": "bytes",
        "name": "result",
        "type": "bytes"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  }
];

// src/routers/sdk/approveAndCall.ts
function isMint(options) {
  return Object.keys(options).some((k) => k === "recipient");
}
var _ApproveAndCall = class {
  /**
   * Cannot be constructed.
   */
  constructor() {
  }
  static encodeApproveMax(token) {
    return encodeFunctionData({
      abi: _ApproveAndCall.ABI,
      functionName: "approveMax",
      args: [token.address]
    });
  }
  static encodeApproveMaxMinusOne(token) {
    return encodeFunctionData({
      abi: _ApproveAndCall.ABI,
      functionName: "approveMaxMinusOne",
      args: [token.address]
    });
  }
  static encodeApproveZeroThenMax(token) {
    return encodeFunctionData({
      abi: _ApproveAndCall.ABI,
      functionName: "approveZeroThenMax",
      args: [token.address]
    });
  }
  static encodeApproveZeroThenMaxMinusOne(token) {
    return encodeFunctionData({
      abi: _ApproveAndCall.ABI,
      functionName: "approveZeroThenMaxMinusOne",
      args: [token.address]
    });
  }
  static encodeCallPositionManager(calldatas) {
    invariant3(calldatas.length > 0, "NULL_CALLDATA");
    if (calldatas.length == 1) {
      return encodeFunctionData({
        abi: _ApproveAndCall.ABI,
        functionName: "callPositionManager",
        args: calldatas
      });
    }
    const encodedMulticall = encodeFunctionData({
      abi: NonfungiblePositionManager.ABI,
      functionName: "multicall",
      args: [calldatas]
    });
    return encodeFunctionData({
      abi: _ApproveAndCall.ABI,
      functionName: "callPositionManager",
      args: [encodedMulticall]
    });
  }
  /**
   * Encode adding liquidity to a position in the nft manager contract
   * @param position Forcasted position with expected amount out from swap
   * @param minimalPosition Forcasted position with custom minimal token amounts
   * @param addLiquidityOptions Options for adding liquidity
   * @param slippageTolerance Defines maximum slippage
   */
  static encodeAddLiquidity(position, minimalPosition, addLiquidityOptions, slippageTolerance) {
    let { amount0: amount0Min, amount1: amount1Min } = position.mintAmountsWithSlippage(slippageTolerance);
    if (minimalPosition.amount0.quotient < amount0Min) {
      amount0Min = minimalPosition.amount0.quotient;
    }
    if (minimalPosition.amount1.quotient < amount1Min) {
      amount1Min = minimalPosition.amount1.quotient;
    }
    if (isMint(addLiquidityOptions)) {
      return encodeFunctionData({
        abi: _ApproveAndCall.ABI,
        functionName: "mint",
        args: [
          {
            token0: position.pool.token0.address,
            token1: position.pool.token1.address,
            fee: position.pool.fee,
            tickLower: position.tickLower,
            tickUpper: position.tickUpper,
            amount0Min,
            amount1Min,
            recipient: addLiquidityOptions.recipient
          }
        ]
      });
    }
    return encodeFunctionData({
      abi: _ApproveAndCall.ABI,
      functionName: "increaseLiquidity",
      args: [
        {
          token0: position.pool.token0.address,
          token1: position.pool.token1.address,
          amount0Min,
          amount1Min,
          tokenId: BigInt(addLiquidityOptions.tokenId)
        }
      ]
    });
  }
  static encodeApprove(token, approvalType) {
    switch (approvalType) {
      case 1 /* MAX */:
        return _ApproveAndCall.encodeApproveMax(token.wrapped);
      case 2 /* MAX_MINUS_ONE */:
        return _ApproveAndCall.encodeApproveMaxMinusOne(token.wrapped);
      case 3 /* ZERO_THEN_MAX */:
        return _ApproveAndCall.encodeApproveZeroThenMax(token.wrapped);
      case 4 /* ZERO_THEN_MAX_MINUS_ONE */:
        return _ApproveAndCall.encodeApproveZeroThenMaxMinusOne(token.wrapped);
      default:
        throw "Error: invalid ApprovalType";
    }
  }
};
var ApproveAndCall = _ApproveAndCall;
ApproveAndCall.ABI = approveAndCallAbi;

// src/abis/IMulticallExtended.ts
var multicallExtendedAbi = [
  {
    "inputs": [
      {
        "internalType": "bytes32",
        "name": "previousBlockhash",
        "type": "bytes32"
      },
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "results",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "results",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "results",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  }
];

// src/routers/sdk/multicallExtended.ts
function validateAndParseBytes32(bytes32) {
  if (!bytes32.match(/^0x[0-9a-fA-F]{64}$/)) {
    throw new Error(`${bytes32} is not valid bytes32.`);
  }
  return bytes32.toLowerCase();
}
var _MulticallExtended = class {
  /**
   * Cannot be constructed.
   */
  constructor() {
  }
  static encodeMulticall(calldatas, validation) {
    if (typeof validation === "undefined") {
      return Multicall.encodeMulticall(calldatas);
    }
    if (!Array.isArray(calldatas)) {
      calldatas = [calldatas];
    }
    if (typeof validation === "string" && validation.startsWith("0x")) {
      const previousBlockhash = validateAndParseBytes32(validation);
      return encodeFunctionData({
        abi: _MulticallExtended.ABI,
        functionName: "multicall",
        args: [previousBlockhash, calldatas]
      });
    }
    const deadline = BigInt(validation);
    return encodeFunctionData({
      abi: _MulticallExtended.ABI,
      functionName: "multicall",
      args: [deadline, calldatas]
    });
  }
};
var MulticallExtended = _MulticallExtended;
MulticallExtended.ABI = multicallExtendedAbi;

// src/abis/IPeripheryPaymentsWithFeeExtended.ts
var peripheryPaymentsWithFeeExtendedAbi = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "pull",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "refundETH",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      }
    ],
    "name": "sweepToken",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      }
    ],
    "name": "sweepToken",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "sweepTokenWithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "sweepTokenWithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      }
    ],
    "name": "unwrapWETH9",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      }
    ],
    "name": "unwrapWETH9",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "unwrapWETH9WithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "unwrapWETH9WithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "wrapETH",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  }
];
function encodeFeeBips(fee) {
  return fee.multiply(1e4).quotient;
}
var _PaymentsExtended = class {
  /**
   * Cannot be constructed.
   */
  constructor() {
  }
  static encodeUnwrapWETH9(amountMinimum, recipient, feeOptions) {
    if (typeof recipient === "string") {
      return Payments.encodeUnwrapWETH9(amountMinimum, recipient, feeOptions);
    }
    if (!!feeOptions) {
      const feeBips = encodeFeeBips(feeOptions.fee);
      const feeRecipient = validateAndParseAddress(feeOptions.recipient);
      return encodeFunctionData({
        abi: _PaymentsExtended.ABI,
        functionName: "unwrapWETH9WithFee",
        args: [amountMinimum, feeBips, feeRecipient]
      });
    }
    return encodeFunctionData({
      abi: _PaymentsExtended.ABI,
      functionName: "unwrapWETH9",
      args: [amountMinimum]
    });
  }
  static encodeSweepToken(token, amountMinimum, recipient, feeOptions) {
    if (typeof recipient === "string") {
      return Payments.encodeSweepToken(token, amountMinimum, recipient, feeOptions);
    }
    if (!!feeOptions) {
      const feeBips = encodeFeeBips(feeOptions.fee);
      const feeRecipient = validateAndParseAddress(feeOptions.recipient);
      return encodeFunctionData({
        abi: _PaymentsExtended.ABI,
        functionName: "sweepTokenWithFee",
        args: [token.address, amountMinimum, feeBips, feeRecipient]
      });
    }
    return encodeFunctionData({
      abi: _PaymentsExtended.ABI,
      functionName: "sweepToken",
      args: [token.address, amountMinimum]
    });
  }
  static encodePull(token, amount) {
    return encodeFunctionData({ abi: _PaymentsExtended.ABI, functionName: "pull", args: [token.address, amount] });
  }
  static encodeWrapETH(amount) {
    return encodeFunctionData({ abi: _PaymentsExtended.ABI, functionName: "wrapETH", args: [amount] });
  }
};
var PaymentsExtended = _PaymentsExtended;
PaymentsExtended.ABI = peripheryPaymentsWithFeeExtendedAbi;

// src/abis/ISwapRouter02.ts
var swapRouter02Abi = [
  {
    "inputs": [
      {
        "internalType": "bytes32",
        "name": "previousBlockhash",
        "type": "bytes32"
      },
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "results",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "results",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "results",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermit",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "nonce",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "expiry",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermitAllowed",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "nonce",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "expiry",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermitAllowedIfNecessary",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermitIfNecessary",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountOutMin",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapExactTokensForTokensV1",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountOutMin",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapExactTokensForTokensV2",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountInMax",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapTokensForExactTokensV1",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountInMax",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapTokensForExactTokensV2",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  }
];
var MixedRouteSDK = class {
  /**
   * Creates an instance of route.
   * @param pools An array of `TPool` objects (pools or pairs), ordered by the route the swap will take
   * @param input The input token
   * @param output The output token
   */
  constructor(pools, input, output) {
    this._midPrice = null;
    invariant3(pools.length > 0, "POOLS");
    const chainId = pools[0].chainId;
    const allOnSameChain = pools.every((pool) => pool.chainId === chainId);
    invariant3(allOnSameChain, "CHAIN_IDS");
    const wrappedInput = input.wrapped;
    invariant3(pools[0].involvesToken(wrappedInput), "INPUT");
    invariant3(pools[pools.length - 1].involvesToken(output.wrapped), "OUTPUT");
    const tokenPath = [wrappedInput];
    for (const [i, pool] of pools.entries()) {
      const currentInputToken = tokenPath[i];
      invariant3(currentInputToken.equals(pool.token0) || currentInputToken.equals(pool.token1), "PATH");
      const nextToken = currentInputToken.equals(pool.token0) ? pool.token1 : pool.token0;
      tokenPath.push(nextToken);
    }
    this.pools = pools;
    this.path = tokenPath;
    this.input = input;
    this.output = output ?? tokenPath[tokenPath.length - 1];
  }
  get chainId() {
    return this.pools[0].chainId;
  }
  /**
   * Returns the mid price of the route
   */
  get midPrice() {
    if (this._midPrice !== null)
      return this._midPrice;
    const price = this.pools.slice(1).reduce(
      ({ nextInput, price: price2 }, pool) => {
        return nextInput.equals(pool.token0) ? {
          nextInput: pool.token1,
          price: price2.multiply(pool.token0Price)
        } : {
          nextInput: pool.token0,
          price: price2.multiply(pool.token1Price)
        };
      },
      this.pools[0].token0.equals(this.input.wrapped) ? {
        nextInput: this.pools[0].token1,
        price: this.pools[0].token0Price
      } : {
        nextInput: this.pools[0].token0,
        price: this.pools[0].token1Price
      }
    ).price;
    return this._midPrice = new Price(this.input, this.output, price.denominator, price.numerator);
  }
};
function tradeComparator(a, b) {
  invariant3(a.inputAmount.currency.equals(b.inputAmount.currency), "INPUT_CURRENCY");
  invariant3(a.outputAmount.currency.equals(b.outputAmount.currency), "OUTPUT_CURRENCY");
  if (a.outputAmount.equalTo(b.outputAmount)) {
    if (a.inputAmount.equalTo(b.inputAmount)) {
      const aHops = a.swaps.reduce((total, cur) => total + cur.route.path.length, 0);
      const bHops = b.swaps.reduce((total, cur) => total + cur.route.path.length, 0);
      return aHops - bHops;
    }
    if (a.inputAmount.lessThan(b.inputAmount)) {
      return -1;
    } else {
      return 1;
    }
  } else {
    if (a.outputAmount.lessThan(b.outputAmount)) {
      return 1;
    } else {
      return -1;
    }
  }
}
var MixedRouteTrade = class {
  /**
   * @deprecated Deprecated in favor of 'swaps' property. If the trade consists of multiple routes
   * this will return an error.
   *
   * When the trade consists of just a single route, this returns the route of the trade,
   * i.e. which pools the trade goes through.
   */
  get route() {
    invariant3(this.swaps.length == 1, "MULTIPLE_ROUTES");
    return this.swaps[0].route;
  }
  /**
   * The input amount for the trade assuming no slippage.
   */
  get inputAmount() {
    if (this._inputAmount) {
      return this._inputAmount;
    }
    const inputCurrency = this.swaps[0].inputAmount.currency;
    const totalInputFromRoutes = this.swaps.map(({ inputAmount }) => inputAmount).reduce((total, cur) => total.add(cur), CurrencyAmount$1.fromRawAmount(inputCurrency, 0));
    this._inputAmount = totalInputFromRoutes;
    return this._inputAmount;
  }
  /**
   * The output amount for the trade assuming no slippage.
   */
  get outputAmount() {
    if (this._outputAmount) {
      return this._outputAmount;
    }
    const outputCurrency = this.swaps[0].outputAmount.currency;
    const totalOutputFromRoutes = this.swaps.map(({ outputAmount }) => outputAmount).reduce((total, cur) => total.add(cur), CurrencyAmount$1.fromRawAmount(outputCurrency, 0));
    this._outputAmount = totalOutputFromRoutes;
    return this._outputAmount;
  }
  /**
   * The price expressed in terms of output amount/input amount.
   */
  get executionPrice() {
    return this._executionPrice ?? (this._executionPrice = new Price(
      this.inputAmount.currency,
      this.outputAmount.currency,
      this.inputAmount.quotient,
      this.outputAmount.quotient
    ));
  }
  /**
   * Returns the percent difference between the route's mid price and the price impact
   */
  get priceImpact() {
    if (this._priceImpact) {
      return this._priceImpact;
    }
    let spotOutputAmount = CurrencyAmount$1.fromRawAmount(this.outputAmount.currency, 0);
    for (const { route, inputAmount } of this.swaps) {
      const midPrice = route.midPrice;
      spotOutputAmount = spotOutputAmount.add(midPrice.quote(inputAmount));
    }
    const priceImpact = spotOutputAmount.subtract(this.outputAmount).divide(spotOutputAmount);
    this._priceImpact = new Percent(priceImpact.numerator, priceImpact.denominator);
    return this._priceImpact;
  }
  /**
   * Constructs a trade by simulating swaps through the given route
   * @template TInput The input token, either Ether or an ERC-20.
   * @template TOutput The output token, either Ether or an ERC-20.
   * @template TTradeType The type of the trade, either exact in or exact out.
   * @param route route to swap through
   * @param amount the amount specified, either input or output, depending on tradeType
   * @param tradeType whether the trade is an exact input or exact output swap
   * @returns The route
   */
  static async fromRoute(route, amount, tradeType) {
    const amounts = new Array(route.path.length);
    let inputAmount;
    let outputAmount;
    invariant3(tradeType === TradeType.EXACT_INPUT, "TRADE_TYPE");
    invariant3(amount.currency.equals(route.input), "INPUT");
    amounts[0] = amount.wrapped;
    for (let i = 0; i < route.path.length - 1; i++) {
      const pool = route.pools[i];
      const [outputAmount2] = await pool.getOutputAmount(amounts[i]);
      amounts[i + 1] = outputAmount2;
    }
    inputAmount = CurrencyAmount$1.fromFractionalAmount(route.input, amount.numerator, amount.denominator);
    outputAmount = CurrencyAmount$1.fromFractionalAmount(
      route.output,
      amounts[amounts.length - 1].numerator,
      amounts[amounts.length - 1].denominator
    );
    return new MixedRouteTrade({
      routes: [{ inputAmount, outputAmount, route }],
      tradeType
    });
  }
  /**
   * Constructs a trade from routes by simulating swaps
   *
   * @template TInput The input token, either Ether or an ERC-20.
   * @template TOutput The output token, either Ether or an ERC-20.
   * @template TTradeType The type of the trade, either exact in or exact out.
   * @param routes the routes to swap through and how much of the amount should be routed through each
   * @param tradeType whether the trade is an exact input or exact output swap
   * @returns The trade
   */
  static async fromRoutes(routes, tradeType) {
    const populatedRoutes = [];
    invariant3(tradeType === TradeType.EXACT_INPUT, "TRADE_TYPE");
    for (const { route, amount } of routes) {
      const amounts = new Array(route.path.length);
      let inputAmount;
      let outputAmount;
      invariant3(amount.currency.equals(route.input), "INPUT");
      inputAmount = CurrencyAmount$1.fromFractionalAmount(route.input, amount.numerator, amount.denominator);
      amounts[0] = CurrencyAmount$1.fromFractionalAmount(route.input.wrapped, amount.numerator, amount.denominator);
      for (let i = 0; i < route.path.length - 1; i++) {
        const pool = route.pools[i];
        const [outputAmount2] = await pool.getOutputAmount(amounts[i]);
        amounts[i + 1] = outputAmount2;
      }
      outputAmount = CurrencyAmount$1.fromFractionalAmount(
        route.output,
        amounts[amounts.length - 1].numerator,
        amounts[amounts.length - 1].denominator
      );
      populatedRoutes.push({ route, inputAmount, outputAmount });
    }
    return new MixedRouteTrade({
      routes: populatedRoutes,
      tradeType
    });
  }
  /**
   * Creates a trade without computing the result of swapping through the route. Useful when you have simulated the trade
   * elsewhere and do not have any tick data
   * @template TInput The input token, either Ether or an ERC-20
   * @template TOutput The output token, either Ether or an ERC-20
   * @template TTradeType The type of the trade, either exact in or exact out
   * @param constructorArguments The arguments passed to the trade constructor
   * @returns The unchecked trade
   */
  static createUncheckedTrade(constructorArguments) {
    return new MixedRouteTrade({
      ...constructorArguments,
      routes: [
        {
          inputAmount: constructorArguments.inputAmount,
          outputAmount: constructorArguments.outputAmount,
          route: constructorArguments.route
        }
      ]
    });
  }
  /**
   * Creates a trade without computing the result of swapping through the routes. Useful when you have simulated the trade
   * elsewhere and do not have any tick data
   * @template TInput The input token, either Ether or an ERC-20
   * @template TOutput The output token, either Ether or an ERC-20
   * @template TTradeType The type of the trade, either exact in or exact out
   * @param constructorArguments The arguments passed to the trade constructor
   * @returns The unchecked trade
   */
  static createUncheckedTradeWithMultipleRoutes(constructorArguments) {
    return new MixedRouteTrade(constructorArguments);
  }
  /**
   * Construct a trade by passing in the pre-computed property values
   * @param routes The routes through which the trade occurs
   * @param tradeType The type of trade, exact input or exact output
   */
  constructor({
    routes,
    tradeType
  }) {
    const inputCurrency = routes[0].inputAmount.currency;
    const outputCurrency = routes[0].outputAmount.currency;
    invariant3(
      routes.every(({ route }) => inputCurrency.wrapped.equals(route.input.wrapped)),
      "INPUT_CURRENCY_MATCH"
    );
    invariant3(
      routes.every(({ route }) => outputCurrency.wrapped.equals(route.output.wrapped)),
      "OUTPUT_CURRENCY_MATCH"
    );
    const numPools = routes.map(({ route }) => route.pools.length).reduce((total, cur) => total + cur, 0);
    const poolAddressSet = /* @__PURE__ */ new Set();
    for (const { route } of routes) {
      for (const pool of route.pools) {
        pool instanceof PairV1 ? poolAddressSet.add(PairV1.getAddress(pool.token0, pool.token1)) : poolAddressSet.add(PairV2.getAddress(pool.token0, pool.token1));
      }
    }
    invariant3(numPools == poolAddressSet.size, "POOLS_DUPLICATED");
    invariant3(tradeType === TradeType.EXACT_INPUT, "TRADE_TYPE");
    this.swaps = routes;
    this.tradeType = tradeType;
  }
  /**
   * Get the minimum amount that must be received from this trade for the given slippage tolerance
   * @param slippageTolerance The tolerance of unfavorable slippage from the execution price of this trade
   * @returns The amount out
   */
  minimumAmountOut(slippageTolerance, amountOut = this.outputAmount) {
    invariant3(!slippageTolerance.lessThan(ZERO), "SLIPPAGE_TOLERANCE");
    const slippageAdjustedAmountOut = new Fraction(ONE).add(slippageTolerance).invert().multiply(amountOut.quotient).quotient;
    return CurrencyAmount$1.fromRawAmount(amountOut.currency, slippageAdjustedAmountOut);
  }
  /**
   * Get the maximum amount in that can be spent via this trade for the given slippage tolerance
   * @param slippageTolerance The tolerance of unfavorable slippage from the execution price of this trade
   * @returns The amount in
   */
  maximumAmountIn(slippageTolerance, amountIn = this.inputAmount) {
    invariant3(!slippageTolerance.lessThan(ZERO), "SLIPPAGE_TOLERANCE");
    return amountIn;
  }
  /**
   * Return the execution price after accounting for slippage tolerance
   * @param slippageTolerance the allowed tolerated slippage
   * @returns The execution price
   */
  worstExecutionPrice(slippageTolerance) {
    return new Price(
      this.inputAmount.currency,
      this.outputAmount.currency,
      this.maximumAmountIn(slippageTolerance).quotient,
      this.minimumAmountOut(slippageTolerance).quotient
    );
  }
  /**
   * Given a list of pools, and a fixed amount in, returns the top `maxNumResults` trades that go from an input token
   * amount to an output token, making at most `maxHops` hops.
   * Note this does not consider aggregation, as routes are linear. It's possible a better route exists by splitting
   * the amount in among multiple routes.
   * @param pools the pools to consider in finding the best trade
   * @param nextAmountIn exact amount of input currency to spend
   * @param currencyOut the desired currency out
   * @param maxNumResults maximum number of results to return
   * @param maxHops maximum number of hops a returned trade can make, e.g. 1 hop goes through a single pool
   * @param currentPools used in recursion; the current list of pools
   * @param currencyAmountIn used in recursion; the original value of the currencyAmountIn parameter
   * @param bestTrades used in recursion; the current list of best trades
   * @returns The exact in trade
   */
  static async bestTradeExactIn(pools, currencyAmountIn, currencyOut, { maxNumResults = 3, maxHops = 3 } = {}, currentPools = [], nextAmountIn = currencyAmountIn, bestTrades = []) {
    invariant3(pools.length > 0, "POOLS");
    invariant3(maxHops > 0, "MAX_HOPS");
    invariant3(currencyAmountIn === nextAmountIn || currentPools.length > 0, "INVALID_RECURSION");
    const amountIn = nextAmountIn.wrapped;
    const tokenOut = currencyOut.wrapped;
    for (let i = 0; i < pools.length; i++) {
      const pool = pools[i];
      if (!pool.token0.equals(amountIn.currency) && !pool.token1.equals(amountIn.currency))
        continue;
      if (pool instanceof PairV1) {
        if (pool.reserve0.equalTo(ZERO) || pool.reserve1.equalTo(ZERO))
          continue;
      }
      if (pool instanceof PairV2) {
        if (pool.reserve0.equalTo(ZERO) || pool.reserve1.equalTo(ZERO))
          continue;
      }
      let amountOut;
      try {
        ;
        [amountOut] = await pool.getOutputAmount(amountIn);
      } catch (error) {
        if (error.isInsufficientInputAmountError) {
          continue;
        }
        throw error;
      }
      if (amountOut.currency.isToken && amountOut.currency.equals(tokenOut)) {
        sortedInsert(
          bestTrades,
          await MixedRouteTrade.fromRoute(
            new MixedRouteSDK([...currentPools, pool], currencyAmountIn.currency, currencyOut),
            currencyAmountIn,
            TradeType.EXACT_INPUT
          ),
          maxNumResults,
          tradeComparator
        );
      } else if (maxHops > 1 && pools.length > 1) {
        const poolsExcludingThisPool = pools.slice(0, i).concat(pools.slice(i + 1, pools.length));
        await MixedRouteTrade.bestTradeExactIn(
          poolsExcludingThisPool,
          currencyAmountIn,
          currencyOut,
          {
            maxNumResults,
            maxHops: maxHops - 1
          },
          [...currentPools, pool],
          amountOut,
          bestTrades
        );
      }
    }
    return bestTrades;
  }
};
var RouteV1 = class extends RouteV1$1 {
  constructor(v1Route) {
    super(v1Route.pairs, v1Route.input, v1Route.output);
    this.protocol = "V1" /* V1 */;
    this.pools = this.pairs;
  }
};
var RouteV2 = class extends RouteV2$1 {
  constructor(v2Route) {
    super(v2Route.pairs, v2Route.input, v2Route.output);
    this.protocol = "V2" /* V2 */;
    this.pools = this.pairs;
  }
};
var MixedRoute = class extends MixedRouteSDK {
  constructor(mixedRoute) {
    super(mixedRoute.pools, mixedRoute.input, mixedRoute.output);
    this.protocol = "MIXED" /* MIXED */;
  }
};

// src/routers/sdk/entities/trade.ts
var Trade = class {
  //  construct a trade across v1 and v2 routes from pre-computed amounts
  constructor({
    v1Routes,
    v2Routes,
    tradeType,
    mixedRoutes
  }) {
    this.swaps = [];
    this.routes = [];
    for (const { routev1, inputAmount, outputAmount } of v1Routes) {
      const route = new RouteV1(routev1);
      this.routes.push(route);
      this.swaps.push({
        route,
        inputAmount,
        outputAmount
      });
    }
    for (const { routev2, inputAmount, outputAmount } of v2Routes) {
      const route = new RouteV2(routev2);
      this.routes.push(route);
      this.swaps.push({
        route,
        inputAmount,
        outputAmount
      });
    }
    if (mixedRoutes) {
      for (const { mixedRoute, inputAmount, outputAmount } of mixedRoutes) {
        const route = new MixedRoute(mixedRoute);
        this.routes.push(route);
        this.swaps.push({
          route,
          inputAmount,
          outputAmount
        });
      }
    }
    this.tradeType = tradeType;
    const inputCurrency = this.swaps[0].inputAmount.currency;
    const outputCurrency = this.swaps[0].outputAmount.currency;
    invariant3(
      this.swaps.every(({ route }) => inputCurrency.wrapped.equals(route.input.wrapped)),
      "INPUT_CURRENCY_MATCH"
    );
    invariant3(
      this.swaps.every(({ route }) => outputCurrency.wrapped.equals(route.output.wrapped)),
      "OUTPUT_CURRENCY_MATCH"
    );
    const numPools = this.swaps.map(({ route }) => route.pools.length).reduce((total, cur) => total + cur, 0);
    const poolAddressSet = /* @__PURE__ */ new Set();
    for (const { route } of this.swaps) {
      for (const pool of route.pools) {
        if (pool instanceof PairV1) {
          poolAddressSet.add(PairV1.getAddress(pool.token0, pool.token1));
        } else if (pool instanceof PairV2) {
          poolAddressSet.add(PairV2.getAddress(pool.token0, pool.token1));
        } else {
          throw new Error("Unexpected pool type in route when constructing trade object");
        }
      }
    }
    invariant3(numPools == poolAddressSet.size, "POOLS_DUPLICATED");
  }
  get inputAmount() {
    if (this._inputAmount) {
      return this._inputAmount;
    }
    const inputCurrency = this.swaps[0].inputAmount.currency;
    const totalInputFromRoutes = this.swaps.map(({ inputAmount }) => inputAmount).reduce((total, cur) => total.add(cur), CurrencyAmount$1.fromRawAmount(inputCurrency, 0));
    this._inputAmount = totalInputFromRoutes;
    return this._inputAmount;
  }
  get outputAmount() {
    if (this._outputAmount) {
      return this._outputAmount;
    }
    const outputCurrency = this.swaps[0].outputAmount.currency;
    const totalOutputFromRoutes = this.swaps.map(({ outputAmount }) => outputAmount).reduce((total, cur) => total.add(cur), CurrencyAmount$1.fromRawAmount(outputCurrency, 0));
    this._outputAmount = totalOutputFromRoutes;
    return this._outputAmount;
  }
  /**
   * The price expressed in terms of output amount/input amount.
   */
  get executionPrice() {
    return this._executionPrice ?? (this._executionPrice = new Price(
      this.inputAmount.currency,
      this.outputAmount.currency,
      this.inputAmount.quotient,
      this.outputAmount.quotient
    ));
  }
  /**
   * Returns the percent difference between the route's mid price and the price impact
   */
  get priceImpact() {
    if (this._priceImpact) {
      return this._priceImpact;
    }
    let spotOutputAmount = CurrencyAmount$1.fromRawAmount(this.outputAmount.currency, 0);
    for (const { route, inputAmount } of this.swaps) {
      const midPrice = route.midPrice;
      spotOutputAmount = spotOutputAmount.add(midPrice.quote(inputAmount));
    }
    const priceImpact = spotOutputAmount.subtract(this.outputAmount).divide(spotOutputAmount);
    this._priceImpact = new Percent(priceImpact.numerator, priceImpact.denominator);
    return this._priceImpact;
  }
  /**
   * Get the minimum amount that must be received from this trade for the given slippage tolerance
   * @param slippageTolerance The tolerance of unfavorable slippage from the execution price of this trade
   * @returns The amount out
   */
  minimumAmountOut(slippageTolerance, amountOut = this.outputAmount) {
    invariant3(!slippageTolerance.lessThan(ZERO), "SLIPPAGE_TOLERANCE");
    if (this.tradeType === TradeType.EXACT_OUTPUT) {
      return amountOut;
    } else {
      const slippageAdjustedAmountOut = new Fraction(ONE).add(slippageTolerance).invert().multiply(amountOut.quotient).quotient;
      return CurrencyAmount$1.fromRawAmount(amountOut.currency, slippageAdjustedAmountOut);
    }
  }
  /**
   * Get the maximum amount in that can be spent via this trade for the given slippage tolerance
   * @param slippageTolerance The tolerance of unfavorable slippage from the execution price of this trade
   * @returns The amount in
   */
  maximumAmountIn(slippageTolerance, amountIn = this.inputAmount) {
    invariant3(!slippageTolerance.lessThan(ZERO), "SLIPPAGE_TOLERANCE");
    if (this.tradeType === TradeType.EXACT_INPUT) {
      return amountIn;
    } else {
      const slippageAdjustedAmountIn = new Fraction(ONE).add(slippageTolerance).multiply(amountIn.quotient).quotient;
      return CurrencyAmount$1.fromRawAmount(amountIn.currency, slippageAdjustedAmountIn);
    }
  }
  /**
   * Return the execution price after accounting for slippage tolerance
   * @param slippageTolerance the allowed tolerated slippage
   * @returns The execution price
   */
  worstExecutionPrice(slippageTolerance) {
    return new Price(
      this.inputAmount.currency,
      this.outputAmount.currency,
      this.maximumAmountIn(slippageTolerance).quotient,
      this.minimumAmountOut(slippageTolerance).quotient
    );
  }
  static async fromRoutes(v1Routes, v2Routes, tradeType, mixedRoutes) {
    const populatedV1Routes = [];
    const populatedV2Routes = [];
    const populatedMixedRoutes = [];
    for (const { routev1, amount } of v1Routes) {
      const v1Trade = new TradeV1(routev1, amount, tradeType);
      const { inputAmount, outputAmount } = v1Trade;
      populatedV1Routes.push({
        routev1,
        inputAmount,
        outputAmount
      });
    }
    for (const { routev2, amount } of v2Routes) {
      const v2Trade = new TradeV2(routev2, amount, tradeType);
      const { inputAmount, outputAmount } = v2Trade;
      populatedV2Routes.push({
        routev2,
        inputAmount,
        outputAmount
      });
    }
    if (mixedRoutes) {
      for (const { mixedRoute, amount } of mixedRoutes) {
        const mixedRouteTrade = await MixedRouteTrade.fromRoute(mixedRoute, amount, tradeType);
        const { inputAmount, outputAmount } = mixedRouteTrade;
        populatedMixedRoutes.push({
          mixedRoute,
          inputAmount,
          outputAmount
        });
      }
    }
    return new Trade({
      v1Routes: populatedV1Routes,
      v2Routes: populatedV2Routes,
      mixedRoutes: populatedMixedRoutes,
      tradeType
    });
  }
  static async fromRoute(route, amount, tradeType) {
    let v1Routes = [];
    let v2Routes = [];
    let mixedRoutes = [];
    if (route instanceof RouteV1$1) {
      const v1Trade = new TradeV1(route, amount, tradeType);
      const { inputAmount, outputAmount } = v1Trade;
      v1Routes = [{ routev1: route, inputAmount, outputAmount }];
    } else if (route instanceof RouteV2$1) {
      const v2Trade = new TradeV2(route, amount, tradeType);
      const { inputAmount, outputAmount } = v2Trade;
      v2Routes = [{ routev2: route, inputAmount, outputAmount }];
    } else if (route instanceof MixedRouteSDK) {
      const mixedRouteTrade = await MixedRouteTrade.fromRoute(route, amount, tradeType);
      const { inputAmount, outputAmount } = mixedRouteTrade;
      mixedRoutes = [{ mixedRoute: route, inputAmount, outputAmount }];
    } else {
      throw new Error("Invalid route type");
    }
    return new Trade({
      v1Routes,
      v2Routes,
      mixedRoutes,
      tradeType
    });
  }
};
var partitionMixedRouteByProtocol = (route) => {
  let acc = [];
  let left = 0;
  let right = 0;
  while (right < route.pools.length) {
    if (route.pools[left] instanceof PairV1 && route.pools[right] instanceof PairV2 || route.pools[left] instanceof PairV2 && route.pools[right] instanceof PairV1) {
      acc.push(route.pools.slice(left, right));
      left = right;
    }
    right++;
    if (right === route.pools.length) {
      acc.push(route.pools.slice(left, right));
    }
  }
  return acc;
};
var getOutputOfPools = (pools, firstInputToken) => {
  const { inputToken: outputToken } = pools.reduce(
    ({ inputToken }, pool) => {
      if (!pool.involvesToken(inputToken))
        throw new Error("PATH");
      const outputToken2 = pool.token0.equals(inputToken) ? pool.token1 : pool.token0;
      return {
        inputToken: outputToken2
      };
    },
    { inputToken: firstInputToken }
  );
  return outputToken;
};

// src/routers/sdk/swapRouter.ts
var ZERO3 = 0n;
var REFUND_ETH_PRICE_IMPACT_THRESHOLD = new Percent(50n, 100n);
var _SwapRouter = class {
  /**
   * Cannot be constructed.
   */
  constructor() {
  }
  /**
   * @notice Generates the calldata for a Swap with a V1 Route.
   * @param trade The V1Trade to encode.
   * @param options SwapOptions to use for the trade.
   * @param routerMustCustody Flag for whether funds should be sent to the router
   * @param performAggregatedSlippageCheck Flag for whether we want to perform an aggregated slippage check
   * @returns A string array of calldatas for the trade.
   */
  static encodeV1Swap(trade, options, routerMustCustody, performAggregatedSlippageCheck) {
    const amountIn = trade.maximumAmountIn(options.slippageTolerance).quotient;
    const amountOut = trade.minimumAmountOut(options.slippageTolerance).quotient;
    const path = trade.route.path.map((token) => token.address);
    const recipient = routerMustCustody ? ADDRESS_THIS : typeof options.recipient === "undefined" ? MSG_SENDER : validateAndParseAddress(options.recipient);
    if (trade.tradeType === TradeType.EXACT_INPUT) {
      const exactInputParams = [amountIn, performAggregatedSlippageCheck ? 0n : amountOut, path, recipient];
      return encodeFunctionData({
        abi: _SwapRouter.ABI,
        functionName: "swapExactTokensForTokensV1",
        args: exactInputParams
      });
    }
    const exactOutputParams = [amountOut, amountIn, path, recipient];
    return encodeFunctionData({
      abi: _SwapRouter.ABI,
      functionName: "swapTokensForExactTokensV1",
      args: exactOutputParams
    });
  }
  /**
   * @notice Generates the calldata for a Swap with a V2 Route.
   * @param trade The V2Trade to encode.
   * @param options SwapOptions to use for the trade.
   * @param routerMustCustody Flag for whether funds should be sent to the router
   * @param performAggregatedSlippageCheck Flag for whether we want to perform an aggregated slippage check
   * @returns A string array of calldatas for the trade.
   */
  static encodeV2Swap(trade, options, routerMustCustody, performAggregatedSlippageCheck) {
    const amountIn = trade.maximumAmountIn(options.slippageTolerance).quotient;
    const amountOut = trade.minimumAmountOut(options.slippageTolerance).quotient;
    const path = trade.route.path.map((token) => token.address);
    const recipient = routerMustCustody ? ADDRESS_THIS : typeof options.recipient === "undefined" ? MSG_SENDER : validateAndParseAddress(options.recipient);
    if (trade.tradeType === TradeType.EXACT_INPUT) {
      const exactInputParams = [amountIn, performAggregatedSlippageCheck ? 0n : amountOut, path, recipient];
      return encodeFunctionData({
        abi: _SwapRouter.ABI,
        functionName: "swapExactTokensForTokensV2",
        args: exactInputParams
      });
    }
    const exactOutputParams = [amountOut, amountIn, path, recipient];
    return encodeFunctionData({
      abi: _SwapRouter.ABI,
      functionName: "swapTokensForExactTokensV2",
      args: exactOutputParams
    });
  }
  /**
   * @notice Generates the calldata for a MixedRouteSwap. Since single hop routes are not MixedRoutes, we will instead generate
   *         them via the existing encodeV3Swap and encodeV2Swap methods.
   * @param trade The MixedRouteTrade to encode.
   * @param options SwapOptions to use for the trade.
   * @param routerMustCustody Flag for whether funds should be sent to the router
   * @param performAggregatedSlippageCheck Flag for whether we want to perform an aggregated slippage check
   * @returns A string array of calldatas for the trade.
   */
  static encodeMixedRouteSwap(trade, options, routerMustCustody, performAggregatedSlippageCheck) {
    const calldatas = [];
    invariant3(trade.tradeType === TradeType.EXACT_INPUT, "TRADE_TYPE");
    for (const { route, inputAmount, outputAmount } of trade.swaps) {
      const amountIn = trade.maximumAmountIn(options.slippageTolerance, inputAmount).quotient;
      const amountOut = trade.minimumAmountOut(options.slippageTolerance, outputAmount).quotient;
      const singleHop = route.pools.length === 1;
      const recipient = routerMustCustody ? ADDRESS_THIS : typeof options.recipient === "undefined" ? MSG_SENDER : validateAndParseAddress(options.recipient);
      const mixedRouteIsAllV1 = (route2) => {
        return route2.pools.every((pool) => pool instanceof PairV1);
      };
      if (singleHop) {
        if (mixedRouteIsAllV1(route)) {
          const path = route.path.map((token) => token.address);
          const exactInputParams = [amountIn, performAggregatedSlippageCheck ? 0n : amountOut, path, recipient];
          calldatas.push(encodeFunctionData({
            abi: _SwapRouter.ABI,
            functionName: "swapExactTokensForTokensV1",
            args: exactInputParams
          }));
        } else {
          const path = route.path.map((token) => token.address);
          const exactInputParams = [amountIn, performAggregatedSlippageCheck ? 0n : amountOut, path, recipient];
          calldatas.push(encodeFunctionData({
            abi: _SwapRouter.ABI,
            functionName: "swapExactTokensForTokensV2",
            args: exactInputParams
          }));
        }
      } else {
        const sections = partitionMixedRouteByProtocol(route);
        const isLastSectionInRoute = (i) => {
          return i === sections.length - 1;
        };
        let outputToken;
        let inputToken = route.input.wrapped;
        for (let i = 0; i < sections.length; i++) {
          const section = sections[i];
          outputToken = getOutputOfPools(section, inputToken);
          const newRouteOriginal = new MixedRouteSDK(
            [...section],
            section[0].token0.equals(inputToken) ? section[0].token0 : section[0].token1,
            outputToken
          );
          const newRoute = new MixedRoute(newRouteOriginal);
          inputToken = outputToken;
          if (mixedRouteIsAllV1(newRoute)) {
            const exactInputParams = [
              i == 0 ? amountIn : 0n,
              // amountIn
              !isLastSectionInRoute(i) ? 0n : amountOut,
              // amountOutMin
              newRoute.path.map((token) => token.address),
              // path
              isLastSectionInRoute(i) ? recipient : ADDRESS_THIS
              // to
            ];
            calldatas.push(encodeFunctionData({
              abi: _SwapRouter.ABI,
              functionName: "swapExactTokensForTokensV1",
              args: exactInputParams
            }));
          } else {
            const exactInputParams = [
              i == 0 ? amountIn : 0n,
              // amountIn
              !isLastSectionInRoute(i) ? 0n : amountOut,
              // amountOutMin
              newRoute.path.map((token) => token.address),
              // path
              isLastSectionInRoute(i) ? recipient : ADDRESS_THIS
              // to
            ];
            calldatas.push(encodeFunctionData({
              abi: _SwapRouter.ABI,
              functionName: "swapExactTokensForTokensV2",
              args: exactInputParams
            }));
          }
        }
      }
    }
    return calldatas;
  }
  static encodeSwaps(trades, options, isSwapAndAdd) {
    if (trades instanceof Trade) {
      invariant3(
        trades.swaps.every(
          (swap) => swap.route.protocol == "V1" /* V1 */ || swap.route.protocol == "V2" /* V2 */ || swap.route.protocol == "MIXED" /* MIXED */
        ),
        "UNSUPPORTED_PROTOCOL"
      );
      let individualTrades = [];
      for (const { route, inputAmount, outputAmount } of trades.swaps) {
        if (route.protocol == "V1" /* V1 */) {
          individualTrades.push(
            new TradeV1(
              route,
              trades.tradeType == TradeType.EXACT_INPUT ? inputAmount : outputAmount,
              trades.tradeType
            )
          );
        } else if (route.protocol == "V2" /* V2 */) {
          individualTrades.push(
            new TradeV2(
              route,
              trades.tradeType == TradeType.EXACT_INPUT ? inputAmount : outputAmount,
              trades.tradeType
            )
          );
        } else if (route.protocol == "MIXED" /* MIXED */) {
          individualTrades.push(
            /// we can change the naming of this function on MixedRouteTrade if needed
            MixedRouteTrade.createUncheckedTrade({
              route,
              inputAmount,
              outputAmount,
              tradeType: trades.tradeType
            })
          );
        } else {
          throw new Error("UNSUPPORTED_TRADE_PROTOCOL");
        }
      }
      trades = individualTrades;
    }
    if (!Array.isArray(trades)) {
      trades = [trades];
    }
    const numberOfTrades = trades.reduce(
      (numberOfTrades2, trade) => numberOfTrades2 + (trade instanceof MixedRouteTrade ? trade.swaps.length : 1),
      0
    );
    const sampleTrade = trades[0];
    invariant3(
      trades.every((trade) => trade.inputAmount.currency.equals(sampleTrade.inputAmount.currency)),
      "TOKEN_IN_DIFF"
    );
    invariant3(
      trades.every((trade) => trade.outputAmount.currency.equals(sampleTrade.outputAmount.currency)),
      "TOKEN_OUT_DIFF"
    );
    invariant3(
      trades.every((trade) => trade.tradeType === sampleTrade.tradeType),
      "TRADE_TYPE_DIFF"
    );
    const calldatas = [];
    const inputIsNative = sampleTrade.inputAmount.currency.isNative;
    const outputIsNative = sampleTrade.outputAmount.currency.isNative;
    const performAggregatedSlippageCheck = sampleTrade.tradeType === TradeType.EXACT_INPUT && numberOfTrades > 2;
    const routerMustCustody = outputIsNative || !!options.fee || !!isSwapAndAdd || performAggregatedSlippageCheck;
    if (options.inputTokenPermit) {
      invariant3(sampleTrade.inputAmount.currency.isToken, "NON_TOKEN_PERMIT");
      calldatas.push(SelfPermit.encodePermit(sampleTrade.inputAmount.currency, options.inputTokenPermit));
    }
    for (const trade of trades) {
      if (trade instanceof TradeV1) {
        calldatas.push(_SwapRouter.encodeV1Swap(trade, options, routerMustCustody, performAggregatedSlippageCheck));
      } else if (trade instanceof TradeV2) {
        calldatas.push(_SwapRouter.encodeV2Swap(trade, options, routerMustCustody, performAggregatedSlippageCheck));
      } else if (trade instanceof MixedRouteTrade) {
        for (const calldata of _SwapRouter.encodeMixedRouteSwap(
          trade,
          options,
          routerMustCustody,
          performAggregatedSlippageCheck
        )) {
          calldatas.push(calldata);
        }
      } else {
        throw new Error("Unsupported trade object");
      }
    }
    const ZERO_IN = CurrencyAmount$1.fromRawAmount(sampleTrade.inputAmount.currency, 0);
    const ZERO_OUT = CurrencyAmount$1.fromRawAmount(sampleTrade.outputAmount.currency, 0);
    const minimumAmountOut = trades.reduce(
      (sum, trade) => sum.add(trade.minimumAmountOut(options.slippageTolerance)),
      ZERO_OUT
    );
    const quoteAmountOut = trades.reduce(
      (sum, trade) => sum.add(trade.outputAmount),
      ZERO_OUT
    );
    const totalAmountIn = trades.reduce(
      (sum, trade) => sum.add(trade.maximumAmountIn(options.slippageTolerance)),
      ZERO_IN
    );
    return {
      calldatas,
      sampleTrade,
      routerMustCustody,
      inputIsNative,
      outputIsNative,
      totalAmountIn,
      minimumAmountOut,
      quoteAmountOut
    };
  }
  /**
   * Produces the on-chain method name to call and the hex encoded parameters to pass as arguments for a given trade.
   * @param trades to produce call parameters for
   * @param options options for the call parameters
   */
  static swapCallParameters(trades, options) {
    const {
      calldatas,
      sampleTrade,
      routerMustCustody,
      inputIsNative,
      outputIsNative,
      totalAmountIn,
      minimumAmountOut
    } = _SwapRouter.encodeSwaps(trades, options);
    if (routerMustCustody) {
      if (outputIsNative) {
        calldatas.push(PaymentsExtended.encodeUnwrapWETH9(minimumAmountOut.quotient, options.recipient, options.fee));
      } else {
        calldatas.push(
          PaymentsExtended.encodeSweepToken(
            sampleTrade.outputAmount.currency.wrapped,
            minimumAmountOut.quotient,
            options.recipient,
            options.fee
          )
        );
      }
    }
    if (inputIsNative && (sampleTrade.tradeType === TradeType.EXACT_OUTPUT || _SwapRouter.riskOfPartialFill(trades))) {
      calldatas.push(Payments.encodeRefundETH());
    }
    return {
      calldata: MulticallExtended.encodeMulticall(calldatas, options.deadlineOrPreviousBlockhash),
      value: toHex(inputIsNative ? totalAmountIn.quotient : ZERO3)
    };
  }
  /**
   * Produces the on-chain method name to call and the hex encoded parameters to pass as arguments for a given trade.
   * @param trades to produce call parameters for
   * @param options options for the call parameters
   */
  static swapAndAddCallParameters(trades, options, position, addLiquidityOptions, tokenInApprovalType, tokenOutApprovalType) {
    const {
      calldatas,
      inputIsNative,
      outputIsNative,
      sampleTrade,
      totalAmountIn: totalAmountSwapped,
      quoteAmountOut,
      minimumAmountOut
    } = _SwapRouter.encodeSwaps(trades, options, true);
    if (options.outputTokenPermit) {
      invariant3(quoteAmountOut.currency.isToken, "NON_TOKEN_PERMIT_OUTPUT");
      calldatas.push(SelfPermit.encodePermit(quoteAmountOut.currency, options.outputTokenPermit));
    }
    const chainId = sampleTrade.route.chainId;
    const zeroForOne = position.pool.token0.wrapped.address === totalAmountSwapped.currency.wrapped.address;
    const { positionAmountIn, positionAmountOut } = _SwapRouter.getPositionAmounts(position, zeroForOne);
    const tokenIn = inputIsNative ? WNATIVE[chainId] : positionAmountIn.currency.wrapped;
    const tokenOut = outputIsNative ? WNATIVE[chainId] : positionAmountOut.currency.wrapped;
    const amountOutRemaining = positionAmountOut.subtract(quoteAmountOut.wrapped);
    if (amountOutRemaining.greaterThan(CurrencyAmount$1.fromRawAmount(positionAmountOut.currency, 0))) {
      outputIsNative ? calldatas.push(PaymentsExtended.encodeWrapETH(amountOutRemaining.quotient)) : calldatas.push(PaymentsExtended.encodePull(tokenOut, amountOutRemaining.quotient));
    }
    inputIsNative ? calldatas.push(PaymentsExtended.encodeWrapETH(positionAmountIn.quotient)) : calldatas.push(PaymentsExtended.encodePull(tokenIn, positionAmountIn.quotient));
    if (tokenInApprovalType !== 0 /* NOT_REQUIRED */)
      calldatas.push(ApproveAndCall.encodeApprove(tokenIn, tokenInApprovalType));
    if (tokenOutApprovalType !== 0 /* NOT_REQUIRED */)
      calldatas.push(ApproveAndCall.encodeApprove(tokenOut, tokenOutApprovalType));
    const minimalPosition = Position.fromAmounts({
      pool: position.pool,
      tickLower: position.tickLower,
      tickUpper: position.tickUpper,
      amount0: zeroForOne ? position.amount0.quotient.toString() : minimumAmountOut.quotient.toString(),
      amount1: zeroForOne ? minimumAmountOut.quotient.toString() : position.amount1.quotient.toString(),
      useFullPrecision: false
    });
    calldatas.push(
      ApproveAndCall.encodeAddLiquidity(position, minimalPosition, addLiquidityOptions, options.slippageTolerance)
    );
    if (inputIsNative) {
      calldatas.push(PaymentsExtended.encodeUnwrapWETH9(ZERO3));
    } else {
      calldatas.push(PaymentsExtended.encodeSweepToken(tokenIn, ZERO3));
    }
    if (outputIsNative) {
      calldatas.push(PaymentsExtended.encodeUnwrapWETH9(ZERO3));
    } else {
      calldatas.push(PaymentsExtended.encodeSweepToken(tokenOut, ZERO3));
    }
    let value;
    if (inputIsNative) {
      value = totalAmountSwapped.wrapped.add(positionAmountIn.wrapped).quotient;
    } else if (outputIsNative) {
      value = amountOutRemaining.quotient;
    } else {
      value = ZERO3;
    }
    return {
      calldata: MulticallExtended.encodeMulticall(calldatas, options.deadlineOrPreviousBlockhash),
      value: toHex(value.toString())
    };
  }
  // if price impact is very high, there's a chance of hitting max/min prices resulting in a partial fill of the swap
  static riskOfPartialFill(trades) {
    if (Array.isArray(trades)) {
      return trades.some((trade) => {
        return _SwapRouter.v3TradeWithHighPriceImpact(trade);
      });
    } else {
      return _SwapRouter.v3TradeWithHighPriceImpact(trades);
    }
  }
  static v3TradeWithHighPriceImpact(trade) {
    return !(trade instanceof TradeV1 || trade instanceof TradeV2) && trade.priceImpact.greaterThan(REFUND_ETH_PRICE_IMPACT_THRESHOLD);
  }
  static getPositionAmounts(position, zeroForOne) {
    const { amount0, amount1 } = position.mintAmounts;
    const currencyAmount0 = CurrencyAmount$1.fromRawAmount(position.pool.token0, amount0);
    const currencyAmount1 = CurrencyAmount$1.fromRawAmount(position.pool.token1, amount1);
    const [positionAmountIn, positionAmountOut] = zeroForOne ? [currencyAmount0, currencyAmount1] : [currencyAmount1, currencyAmount0];
    return { positionAmountIn, positionAmountOut };
  }
};
var SwapRouter = _SwapRouter;
SwapRouter.ABI = swapRouter02Abi;
function encodeMixedRouteToPath(route) {
  const firstInputToken = route.input.wrapped;
  const { path, types } = route.pools.reduce(
    ({ inputToken, path: path2, types: types2 }, pool, index) => {
      const outputToken = pool.token0.equals(inputToken) ? pool.token1 : pool.token0;
      if (index === 0) {
        return {
          inputToken: outputToken,
          types: ["address", "uint24", "address"],
          path: [inputToken.address, pool instanceof PairV1 ? V1_FEE_PATH_PLACEHOLDER : V2_FEE_PATH_PLACEHOLDER, outputToken.address]
        };
      } else {
        return {
          inputToken: outputToken,
          types: [...types2, "uint24", "address"],
          path: [...path2, pool instanceof PairV1 ? V1_FEE_PATH_PLACEHOLDER : V2_FEE_PATH_PLACEHOLDER, outputToken.address]
        };
      }
    },
    { inputToken: firstInputToken, path: [], types: [] }
  );
  return encodePacked(types, path);
}

// src/util/protocols.ts
var TO_PROTOCOL = (protocol) => {
  switch (protocol.toLowerCase()) {
    case "v1":
      return "V1" /* V1 */;
    case "v2":
      return "V2" /* V2 */;
    case "mixed":
      return "MIXED" /* MIXED */;
    default:
      throw new Error(`Unknown protocol: {id}`);
  }
};
var routeToString = (route) => {
  const routeStr = [];
  const tokens = route.path;
  const tokenPath = _16.map(tokens, (token) => `${token.symbol}`);
  const pools = route.protocol === "MIXED" /* MIXED */ ? route.pools : route.pairs;
  const poolFeePath = _16.map(pools, (pool) => {
    return `${pool instanceof PairV1 ? ` -- [${PairV1.getAddress(
      pool.token0,
      pool.token1
    )}]` : ` -- [${PairV2.getAddress(
      pool.token0,
      pool.token1
    )}]`} --> `;
  });
  for (let i = 0; i < tokenPath.length; i++) {
    routeStr.push(tokenPath[i]);
    if (i < poolFeePath.length) {
      routeStr.push(poolFeePath[i]);
    }
  }
  return routeStr.join("");
};
var routeAmountsToString = (routeAmounts) => {
  const total = _16.reduce(
    routeAmounts,
    (total2, cur) => {
      return total2.add(cur.amount);
    },
    CurrencyAmount.fromRawAmount(routeAmounts[0].amount.currency, 0)
  );
  const routeStrings = _16.map(routeAmounts, ({ protocol, route, amount }) => {
    const portion = amount.divide(total);
    const percent = new Percent(portion.numerator, portion.denominator);
    return `[${protocol == "MIXED" /* MIXED */ ? "V1 + V2" : protocol}] ${percent.toFixed(2)}% = ${routeToString(route)}`;
  });
  return _16.join(routeStrings, ", ");
};
var routeAmountToString = (routeAmount) => {
  const { route, amount } = routeAmount;
  return `${amount.toExact()} = ${routeToString(route)}`;
};
var poolToString = (p) => {
  return `${p.token0.symbol}/${p.token1.symbol}`;
};

// src/abis/ERC20.ts
var erc20Abi = [
  {
    "inputs": [
      {
        "internalType": "string",
        "name": "name_",
        "type": "string"
      },
      {
        "internalType": "string",
        "name": "symbol_",
        "type": "string"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "constructor"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "Approval",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "from",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "Transfer",
    "type": "event"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      }
    ],
    "name": "allowance",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "approve",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "account",
        "type": "address"
      }
    ],
    "name": "balanceOf",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "decimals",
    "outputs": [
      {
        "internalType": "uint8",
        "name": "",
        "type": "uint8"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "subtractedValue",
        "type": "uint256"
      }
    ],
    "name": "decreaseAllowance",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "addedValue",
        "type": "uint256"
      }
    ],
    "name": "increaseAllowance",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "name",
    "outputs": [
      {
        "internalType": "string",
        "name": "",
        "type": "string"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "symbol",
    "outputs": [
      {
        "internalType": "string",
        "name": "",
        "type": "string"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "totalSupply",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "transfer",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "sender",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "transferFrom",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

// src/providers/token-provider.ts
var eUSDC_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0x15D38573d2feeb82e7ad5187aB8c1D52810B1f07",
  6,
  "USDC",
  "USDC from Ethereum"
);
var eUSDT_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0x0Cb6F5a34ad42ec934882A05265A7d5F59b51A2f",
  6,
  "USDT",
  "Tether USD from Ethereum"
);
var eWBTC_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0xb17D901469B9208B17d916112988A3FeD19b5cA1",
  8,
  "WBTC",
  "Wrapped BTC from Ethereum"
);
var eDAI_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0xefD766cCb38EaF1dfd701853BFCe31359239F305",
  18,
  "DAI",
  "Dai Stablecoin from Ethereum"
);
var PLSX_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0x95B303987A60C71504D99Aa1b13B4DA07b0790ab",
  18,
  "PLSX",
  "PulseX"
);
var eHEX_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0x57fde0a71132198BBeC939B98976993d8D89D225",
  8,
  "HEX",
  "HEX from Ethereum"
);
var HEX_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39",
  8,
  "HEX",
  "HEX"
);
var INC_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0x2fa878Ab3F87CC1C9737Fc071108F904c0B0C95d",
  18,
  "INC",
  "Incentive"
);
var WETH_PULSECHAIN = new ERC20Token(
  ChainId.PULSECHAIN,
  "0x02DcdD04e3F455D838cd1249292C58f3B79e3C3C",
  18,
  "WETH",
  "WETH from Ethereum"
);
var PLSX_TESTNET = new ERC20Token(
  ChainId.PULSECHAIN_TESTNET,
  "0x8a810ea8B121d08342E9e7696f4a9915cBE494B7",
  18,
  "PLSX",
  "PulseX"
);
var INC_TESTNET = new ERC20Token(
  ChainId.PULSECHAIN_TESTNET,
  "0x6eFAfcb715F385c71d8AF763E8478FeEA6faDF63",
  18,
  "INC",
  "Incentive"
);
var HEX_TESTNET = new ERC20Token(
  ChainId.PULSECHAIN_TESTNET,
  "0x2b591e99afE9f32eAA6214f7B7629768c40Eeb39",
  8,
  "HEX",
  "HEX"
);
var tDAI_TESTNET = new ERC20Token(
  ChainId.PULSECHAIN_TESTNET,
  "0x826e4e896CC2f5B371Cd7Bb0bd929DB3e3DB67c0",
  18,
  "tDAI",
  "DAI Mock"
);
var TokenProvider = class {
  constructor(chainId, multicall2Provider) {
    this.chainId = chainId;
    this.multicall2Provider = multicall2Provider;
  }
  async getTokenSymbol(addresses, providerConfig) {
    let result;
    try {
      result = await this.multicall2Provider.callSameFunctionOnMultipleContracts({
        addresses,
        abi: erc20Abi,
        functionName: "symbol",
        providerConfig
      });
    } catch (error) {
      log.error(
        { addresses },
        `TokenProvider.getTokenSymbol[string] failed with error ${error}. Trying with bytes32.`
      );
      const bytes32Abi = [
        {
          inputs: [],
          name: "symbol",
          outputs: [
            {
              internalType: "bytes32",
              name: "",
              type: "bytes32"
            }
          ],
          stateMutability: "view",
          type: "function"
        }
      ];
      try {
        result = await this.multicall2Provider.callSameFunctionOnMultipleContracts({
          addresses,
          abi: bytes32Abi,
          functionName: "symbol",
          providerConfig,
          bytes32SymbolCall: true
        });
      } catch (error2) {
        log.fatal(
          { addresses },
          `TokenProvider.getTokenSymbol[bytes32] failed with error ${error2}.`
        );
        throw new Error(
          "[TokenProvider.getTokenSymbol] Impossible to fetch token symbol."
        );
      }
    }
    return { result };
  }
  async getTokenDecimals(addresses, providerConfig) {
    return this.multicall2Provider.callSameFunctionOnMultipleContracts({
      addresses,
      abi: erc20Abi,
      functionName: "decimals",
      providerConfig
    });
  }
  async getTokens(_addresses, providerConfig) {
    const addressToToken = {};
    const symbolToToken = {};
    const addresses = _16(_addresses).map((address) => address.toLowerCase()).uniq().value();
    if (addresses.length > 0) {
      const [symbolsResult, decimalsResult] = await Promise.all([
        this.getTokenSymbol(addresses, providerConfig),
        this.getTokenDecimals(addresses, providerConfig)
      ]);
      const { results: symbols } = symbolsResult.result;
      const { results: decimals } = decimalsResult;
      for (let i = 0; i < addresses.length; i++) {
        const address = addresses[i];
        const symbolResult = symbols[i];
        const decimalResult = decimals[i];
        if (!symbolResult?.success || !decimalResult?.success) {
          log.info(
            {
              symbolResult,
              decimalResult
            },
            `Dropping token with address ${address} as symbol or decimal are invalid`
          );
          continue;
        }
        const symbol = symbolResult.result;
        const decimal = decimalResult.result;
        addressToToken[address.toLowerCase()] = new ERC20Token(
          this.chainId,
          address,
          decimal,
          symbol
        );
        symbolToToken[symbol.toLowerCase()] = addressToToken[address.toLowerCase()];
      }
      log.info(
        `Got token symbol and decimals for ${Object.values(addressToToken).length} out of ${addresses.length} tokens on-chain ${providerConfig ? `as of: ${providerConfig?.blockNumber}` : ""}`
      );
    }
    return {
      getTokenByAddress: (address) => {
        return addressToToken[address.toLowerCase()];
      },
      getTokenBySymbol: (symbol) => {
        return symbolToToken[symbol.toLowerCase()];
      },
      getAllTokens: () => {
        return Object.values(addressToToken);
      }
    };
  }
};
var DAI_ON = (chainId) => {
  switch (chainId) {
    case ChainId.PULSECHAIN:
      return eDAI_PULSECHAIN;
    default:
      throw new Error(`Chain id: ${chainId} not supported`);
  }
};
var USDT_ON = (chainId) => {
  switch (chainId) {
    case ChainId.PULSECHAIN:
      return eUSDT_PULSECHAIN;
    default:
      throw new Error(`Chain id: ${chainId} not supported`);
  }
};
var USDC_ON = (chainId) => {
  switch (chainId) {
    case ChainId.PULSECHAIN:
      return eUSDC_PULSECHAIN;
    default:
      throw new Error(`Chain id: ${chainId} not supported`);
  }
};
var WNATIVE_ON = (chainId) => {
  return WRAPPED_NATIVE_CURRENCY[chainId];
};

// src/providers/caching-token-provider.ts
var CACHE_SEED_TOKENS = {
  [ChainId.PULSECHAIN]: {
    eUSDC: eUSDC_PULSECHAIN,
    eUSDT: eUSDT_PULSECHAIN,
    eDAI: eDAI_PULSECHAIN,
    eWBTC: eWBTC_PULSECHAIN,
    PLSX: PLSX_PULSECHAIN,
    eHEX: eHEX_PULSECHAIN,
    HEX: HEX_PULSECHAIN,
    INC: INC_PULSECHAIN,
    WETH: WETH_PULSECHAIN,
    WPLS: WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN]
  },
  [ChainId.PULSECHAIN_TESTNET]: {
    WPLS: WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN_TESTNET],
    PLSX: PLSX_TESTNET,
    INC: INC_TESTNET,
    HEX: HEX_TESTNET,
    tDAI: tDAI_TESTNET
  }
  // Currently we do not have providers for Moonbeam mainnet or Gnosis testnet
};
var CachingTokenProviderWithFallback = class {
  constructor(chainId, tokenCache, primaryTokenProvider, fallbackTokenProvider) {
    this.chainId = chainId;
    this.tokenCache = tokenCache;
    this.primaryTokenProvider = primaryTokenProvider;
    this.fallbackTokenProvider = fallbackTokenProvider;
    this.CACHE_KEY = (chainId, address) => `token-${chainId}-${address}`;
  }
  async getTokens(_addresses) {
    const seedTokens = CACHE_SEED_TOKENS[this.chainId];
    if (seedTokens) {
      for (const token of Object.values(seedTokens)) {
        await this.tokenCache.set(
          this.CACHE_KEY(this.chainId, token.address.toLowerCase()),
          token
        );
      }
    }
    const addressToToken = {};
    const symbolToToken = {};
    const addresses = _16(_addresses).map((address) => address.toLowerCase()).uniq().value();
    const addressesToFindInPrimary = [];
    const addressesToFindInSecondary = [];
    for (const address of addresses) {
      if (await this.tokenCache.has(this.CACHE_KEY(this.chainId, address))) {
        addressToToken[address.toLowerCase()] = await this.tokenCache.get(
          this.CACHE_KEY(this.chainId, address)
        );
        symbolToToken[addressToToken[address].symbol] = await this.tokenCache.get(this.CACHE_KEY(this.chainId, address));
      } else {
        addressesToFindInPrimary.push(address);
      }
    }
    log.info(
      { addressesToFindInPrimary },
      `Found ${addresses.length - addressesToFindInPrimary.length} out of ${addresses.length} tokens in local cache. ${addressesToFindInPrimary.length > 0 ? `Checking primary token provider for ${addressesToFindInPrimary.length} tokens` : ``}
      `
    );
    if (addressesToFindInPrimary.length > 0) {
      const primaryTokenAccessor = await this.primaryTokenProvider.getTokens(
        addressesToFindInPrimary
      );
      for (const address of addressesToFindInPrimary) {
        const token = primaryTokenAccessor.getTokenByAddress(address);
        if (token) {
          addressToToken[address.toLowerCase()] = token;
          symbolToToken[addressToToken[address].symbol] = token;
          await this.tokenCache.set(
            this.CACHE_KEY(this.chainId, address.toLowerCase()),
            addressToToken[address]
          );
        } else {
          addressesToFindInSecondary.push(address);
        }
      }
      log.info(
        { addressesToFindInSecondary },
        `Found ${addressesToFindInPrimary.length - addressesToFindInSecondary.length} tokens in primary. ${this.fallbackTokenProvider ? `Checking secondary token provider for ${addressesToFindInSecondary.length} tokens` : `No fallback token provider specified. About to return.`}`
      );
    }
    if (this.fallbackTokenProvider && addressesToFindInSecondary.length > 0) {
      const secondaryTokenAccessor = await this.fallbackTokenProvider.getTokens(
        addressesToFindInSecondary
      );
      for (const address of addressesToFindInSecondary) {
        const token = secondaryTokenAccessor.getTokenByAddress(address);
        if (token) {
          addressToToken[address.toLowerCase()] = token;
          symbolToToken[addressToToken[address].symbol] = token;
          await this.tokenCache.set(
            this.CACHE_KEY(this.chainId, address.toLowerCase()),
            addressToToken[address]
          );
        }
      }
    }
    return {
      getTokenByAddress: (address) => {
        return addressToToken[address.toLowerCase()];
      },
      getTokenBySymbol: (symbol) => {
        return symbolToToken[symbol.toLowerCase()];
      },
      getAllTokens: () => {
        return Object.values(addressToToken);
      }
    };
  }
};

// src/providers/gas-price-provider.ts
var IGasPriceProvider = class {
};

// src/providers/eip-1559-gas-price-provider.ts
var DEFAULT_PRIORITY_FEE_PERCENTILE = 50;
var DEFAULT_BLOCKS_TO_LOOK_BACK = 4;
var EIP1559GasPriceProvider = class extends IGasPriceProvider {
  constructor(provider, priorityFeePercentile = DEFAULT_PRIORITY_FEE_PERCENTILE, blocksToConsider = DEFAULT_BLOCKS_TO_LOOK_BACK) {
    super();
    this.provider = provider;
    this.priorityFeePercentile = priorityFeePercentile;
    this.blocksToConsider = blocksToConsider;
  }
  async getGasPrice() {
    const feeHistoryRaw = await this.provider.getFeeHistory({
      blockCount: this.blocksToConsider,
      blockTag: "latest",
      rewardPercentiles: [this.priorityFeePercentile]
    });
    const feeHistory = {
      baseFeePerGas: _16.map(feeHistoryRaw.baseFeePerGas, (b) => b),
      gasUsedRatio: feeHistoryRaw.gasUsedRatio,
      oldestBlock: feeHistoryRaw.oldestBlock,
      reward: _16.map(feeHistoryRaw.reward, (b) => b[0])
    };
    const nextBlockBaseFeePerGas = feeHistory.baseFeePerGas[feeHistory.baseFeePerGas.length - 1];
    const averagePriorityFeePerGas = _16.reduce(
      feeHistory.reward,
      (sum, cur) => sum + cur,
      BigInt(0)
    ) / BigInt(feeHistory.reward.length);
    log.info(
      {
        feeHistory,
        feeHistoryReadable: {
          baseFeePerGas: _16.map(feeHistory.baseFeePerGas, (f) => f.toString()),
          oldestBlock: feeHistory.oldestBlock.toString(),
          reward: _16.map(feeHistory.reward, (r) => r.toString())
        },
        nextBlockBaseFeePerGas: nextBlockBaseFeePerGas.toString(),
        averagePriorityFeePerGas: averagePriorityFeePerGas.toString()
      },
      "Got fee history from provider and computed gas estimate"
    );
    const gasPriceWei = nextBlockBaseFeePerGas + averagePriorityFeePerGas;
    const blockNumber = feeHistory.oldestBlock + BigInt(this.blocksToConsider);
    log.info(
      `Estimated gas price in wei: ${gasPriceWei.toString()} as of block ${blockNumber.toString()}`
    );
    return { gasPriceWei };
  }
};

// src/abis/TokenValidator.ts
var tokenValidatorAbi = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "_factoryV1",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "_factoryV2",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "_WETH9",
        "type": "address"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "constructor"
  },
  {
    "inputs": [],
    "name": "WETH9",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address[]",
        "name": "tokens",
        "type": "address[]"
      },
      {
        "internalType": "address[]",
        "name": "baseTokens",
        "type": "address[]"
      },
      {
        "internalType": "uint256",
        "name": "amountToBorrow",
        "type": "uint256"
      }
    ],
    "name": "batchValidate",
    "outputs": [
      {
        "internalType": "enum ITokenValidator.Status[]",
        "name": "isFotResults",
        "type": "uint8[]"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "factoryV1",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "factoryV2",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amount0",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      },
      {
        "internalType": "bytes",
        "name": "data",
        "type": "bytes"
      }
    ],
    "name": "uniswapV2Call",
    "outputs": [],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "address[]",
        "name": "baseTokens",
        "type": "address[]"
      },
      {
        "internalType": "uint256",
        "name": "amountToBorrow",
        "type": "uint256"
      }
    ],
    "name": "validate",
    "outputs": [
      {
        "internalType": "enum ITokenValidator.Status",
        "name": "",
        "type": "uint8"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

// src/providers/token-validator-provider.ts
var DEFAULT_ALLOWLIST = /* @__PURE__ */ new Set([
  // RYOSHI. Does not allow transfers between contracts so fails validation.
  "0x777E2ae845272a2F540ebf6a3D03734A5a8f618e".toLowerCase()
]);
var TokenValidationResult = /* @__PURE__ */ ((TokenValidationResult2) => {
  TokenValidationResult2[TokenValidationResult2["UNKN"] = 0] = "UNKN";
  TokenValidationResult2[TokenValidationResult2["FOT"] = 1] = "FOT";
  TokenValidationResult2[TokenValidationResult2["STF"] = 2] = "STF";
  return TokenValidationResult2;
})(TokenValidationResult || {});
var AMOUNT_TO_FLASH_BORROW = "1000";
var GAS_LIMIT_PER_VALIDATE = 1e6;
var TokenValidatorProvider = class {
  constructor(chainId, multicall2Provider, tokenValidationCache, tokenValidatorAddress = TOKEN_VALIDATOR_ADDRESSES[chainId], gasLimitPerCall = GAS_LIMIT_PER_VALIDATE, amountToFlashBorrow = AMOUNT_TO_FLASH_BORROW, allowList = DEFAULT_ALLOWLIST) {
    this.chainId = chainId;
    this.multicall2Provider = multicall2Provider;
    this.tokenValidationCache = tokenValidationCache;
    this.tokenValidatorAddress = tokenValidatorAddress;
    this.gasLimitPerCall = gasLimitPerCall;
    this.amountToFlashBorrow = amountToFlashBorrow;
    this.allowList = allowList;
    this.CACHE_KEY = (chainId, address) => `token-${chainId}-${address}`;
    this.BASES = [WRAPPED_NATIVE_CURRENCY[this.chainId].address];
  }
  async validateTokens(tokens, providerConfig) {
    const tokenAddressToToken = _16.keyBy(tokens, "address");
    const addressesRaw = _16(tokens).map((token) => token.address).uniq().value();
    const addresses = [];
    const tokenToResult = {};
    for (const address of addressesRaw) {
      if (await this.tokenValidationCache.has(
        this.CACHE_KEY(this.chainId, address)
      )) {
        tokenToResult[address.toLowerCase()] = await this.tokenValidationCache.get(
          this.CACHE_KEY(this.chainId, address)
        );
      } else {
        addresses.push(address);
      }
    }
    log.info(
      `Got token validation results for ${addressesRaw.length - addresses.length} tokens from cache. Getting ${addresses.length} on-chain.`
    );
    const functionParams = _16(addresses).map((address) => [address, this.BASES, this.amountToFlashBorrow]).value();
    const multicallResult = await this.multicall2Provider.callSameFunctionOnContractWithMultipleParams({
      address: this.tokenValidatorAddress,
      abi: tokenValidatorAbi,
      functionName: "validate",
      functionParams,
      providerConfig,
      additionalConfig: {
        gasLimitPerCallOverride: this.gasLimitPerCall
      }
    });
    for (let i = 0; i < multicallResult.results.length; i++) {
      const resultWrapper = multicallResult.results[i];
      const tokenAddress = addresses[i];
      const token = tokenAddressToToken[tokenAddress];
      if (this.allowList.has(token.address.toLowerCase())) {
        tokenToResult[token.address.toLowerCase()] = 0 /* UNKN */;
        await this.tokenValidationCache.set(
          this.CACHE_KEY(this.chainId, token.address.toLowerCase()),
          tokenToResult[token.address.toLowerCase()]
        );
        continue;
      }
      if (!resultWrapper.success) {
        log.info(
          { result: resultWrapper },
          `Failed to validate token ${token.symbol}`
        );
        continue;
      }
      const validationResult = resultWrapper.result[0];
      tokenToResult[token.address.toLowerCase()] = validationResult;
      await this.tokenValidationCache.set(
        this.CACHE_KEY(this.chainId, token.address.toLowerCase()),
        tokenToResult[token.address.toLowerCase()]
      );
    }
    return {
      getValidationByToken: (token) => tokenToResult[token.address.toLowerCase()]
    };
  }
};

// src/abis/IPulseXPair.ts
var pulsexPairABI = [
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "Approval",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "sender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount0",
        "type": "uint256"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount1",
        "type": "uint256"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "senderOrigin",
        "type": "address"
      }
    ],
    "name": "Burn",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "sender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount0",
        "type": "uint256"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount1",
        "type": "uint256"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "senderOrigin",
        "type": "address"
      }
    ],
    "name": "Mint",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "sender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount0In",
        "type": "uint256"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount1In",
        "type": "uint256"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount0Out",
        "type": "uint256"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "amount1Out",
        "type": "uint256"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "Swap",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": false,
        "internalType": "uint112",
        "name": "reserve0",
        "type": "uint112"
      },
      {
        "indexed": false,
        "internalType": "uint112",
        "name": "reserve1",
        "type": "uint112"
      }
    ],
    "name": "Sync",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "from",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "Transfer",
    "type": "event"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "DOMAIN_SEPARATOR",
    "outputs": [
      {
        "internalType": "bytes32",
        "name": "",
        "type": "bytes32"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "MINIMUM_LIQUIDITY",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "pure",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "PERMIT_TYPEHASH",
    "outputs": [
      {
        "internalType": "bytes32",
        "name": "",
        "type": "bytes32"
      }
    ],
    "payable": false,
    "stateMutability": "pure",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      }
    ],
    "name": "allowance",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "approve",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      }
    ],
    "name": "balanceOf",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "senderOrigin",
        "type": "address"
      }
    ],
    "name": "burn",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amount0",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amount1",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "decimals",
    "outputs": [
      {
        "internalType": "uint8",
        "name": "",
        "type": "uint8"
      }
    ],
    "payable": false,
    "stateMutability": "pure",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "factory",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "getReserves",
    "outputs": [
      {
        "internalType": "uint112",
        "name": "reserve0",
        "type": "uint112"
      },
      {
        "internalType": "uint112",
        "name": "reserve1",
        "type": "uint112"
      },
      {
        "internalType": "uint32",
        "name": "blockTimestampLast",
        "type": "uint32"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "name": "initialize",
    "outputs": [],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "kLast",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "senderOrigin",
        "type": "address"
      }
    ],
    "name": "mint",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "liquidity",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "name",
    "outputs": [
      {
        "internalType": "string",
        "name": "",
        "type": "string"
      }
    ],
    "payable": false,
    "stateMutability": "pure",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      }
    ],
    "name": "nonces",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "permit",
    "outputs": [],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "price0CumulativeLast",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "price1CumulativeLast",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "skim",
    "outputs": [],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amount0Out",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amount1Out",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "internalType": "bytes",
        "name": "data",
        "type": "bytes"
      }
    ],
    "name": "swap",
    "outputs": [],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "symbol",
    "outputs": [
      {
        "internalType": "string",
        "name": "",
        "type": "string"
      }
    ],
    "payable": false,
    "stateMutability": "pure",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [],
    "name": "sync",
    "outputs": [],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "token0",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "token1",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": true,
    "inputs": [],
    "name": "totalSupply",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "payable": false,
    "stateMutability": "view",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "transfer",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "constant": false,
    "inputs": [
      {
        "internalType": "address",
        "name": "from",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "transferFrom",
    "outputs": [
      {
        "internalType": "bool",
        "name": "",
        "type": "bool"
      }
    ],
    "payable": false,
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

// src/providers/v2/pool-provider.ts
var V2PoolProvider = class {
  /**
   * Creates an instance of V2PoolProvider.
   * @param chainId The chain id to use.
   * @param multicall2Provider The multicall provider to use to get the pools.
   * @param retryOptions The retry options for each call to the multicall.
   */
  constructor(chainId, multicall2Provider, retryOptions = {
    retries: 2,
    minTimeout: 50,
    maxTimeout: 500
  }) {
    this.chainId = chainId;
    this.multicall2Provider = multicall2Provider;
    this.retryOptions = retryOptions;
    // Computing pool addresses is slow as it requires hashing, encoding etc.
    // Addresses never change so can always be cached.
    this.POOL_ADDRESS_CACHE = {};
  }
  async getPools(tokenPairs, providerConfig) {
    const poolAddressSet = /* @__PURE__ */ new Set();
    const sortedTokenPairs = [];
    const sortedPoolAddresses = [];
    for (const tokenPair of tokenPairs) {
      const [tokenA, tokenB] = tokenPair;
      const { poolAddress, token0, token1 } = this.getPoolAddress(
        tokenA,
        tokenB
      );
      if (poolAddressSet.has(poolAddress)) {
        continue;
      }
      poolAddressSet.add(poolAddress);
      sortedTokenPairs.push([token0, token1]);
      sortedPoolAddresses.push(poolAddress);
    }
    log.debug(
      `getPools called with ${tokenPairs.length} token pairs. Deduped down to ${poolAddressSet.size}`
    );
    const reservesResults = await this.getPoolsData(
      sortedPoolAddresses,
      "getReserves",
      providerConfig
    );
    log.info(
      `Got reserves for ${poolAddressSet.size} pools ${providerConfig?.blockNumber ? `as of block: ${await providerConfig?.blockNumber}.` : ``}`
    );
    const poolAddressToPool = {};
    const invalidPools = [];
    for (let i = 0; i < sortedPoolAddresses.length; i++) {
      const reservesResult = reservesResults[i];
      if (!reservesResult?.success) {
        const [token02, token12] = sortedTokenPairs[i];
        invalidPools.push([token02, token12]);
        continue;
      }
      const [token0, token1] = sortedTokenPairs[i];
      const [reserve0, reserve1] = reservesResult.result;
      const pool = new PairV2(
        CurrencyAmount.fromRawAmount(token0, reserve0.toString()),
        CurrencyAmount.fromRawAmount(token1, reserve1.toString())
      );
      const poolAddress = sortedPoolAddresses[i];
      poolAddressToPool[poolAddress] = pool;
    }
    if (invalidPools.length > 0) {
      log.info(
        {
          invalidPools: _16.map(
            invalidPools,
            ([token0, token1]) => `${token0.symbol}/${token1.symbol}`
          )
        },
        `${invalidPools.length} pools invalid after checking their slot0 and liquidity results. Dropping.`
      );
    }
    const poolStrs = _16.map(Object.values(poolAddressToPool), poolToString);
    log.debug({ poolStrs }, `Found ${poolStrs.length} valid pools`);
    return {
      getPool: (tokenA, tokenB) => {
        const { poolAddress } = this.getPoolAddress(tokenA, tokenB);
        return poolAddressToPool[poolAddress];
      },
      getPoolByAddress: (address) => poolAddressToPool[address],
      getAllPools: () => Object.values(poolAddressToPool)
    };
  }
  getPoolAddress(tokenA, tokenB) {
    const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA];
    const cacheKey = `${this.chainId}/${token0.address}/${token1.address}`;
    const cachedAddress = this.POOL_ADDRESS_CACHE[cacheKey];
    if (cachedAddress) {
      return { poolAddress: cachedAddress, token0, token1 };
    }
    const poolAddress = PairV2.getAddress(token0, token1);
    this.POOL_ADDRESS_CACHE[cacheKey] = poolAddress;
    return { poolAddress, token0, token1 };
  }
  async getPoolsData(poolAddresses, functionName, providerConfig) {
    const { results, blockNumber } = await retry7(async () => {
      return this.multicall2Provider.callSameFunctionOnMultipleContracts({
        addresses: poolAddresses,
        abi: pulsexPairABI,
        functionName,
        providerConfig
      });
    }, this.retryOptions);
    log.debug(`Pool data fetched as of block ${blockNumber}`);
    return results;
  }
};
var V1PoolProvider = class {
  /**
   * Creates an instance of V1PoolProvider.
   * @param chainId The chain id to use.
   * @param multicall2Provider The multicall provider to use to get the pools.
   * @param retryOptions The retry options for each call to the multicall.
   */
  constructor(chainId, multicall2Provider, retryOptions = {
    retries: 2,
    minTimeout: 50,
    maxTimeout: 500
  }) {
    this.chainId = chainId;
    this.multicall2Provider = multicall2Provider;
    this.retryOptions = retryOptions;
    // Computing pool addresses is slow as it requires hashing, encoding etc.
    // Addresses never change so can always be cached.
    this.POOL_ADDRESS_CACHE = {};
  }
  async getPools(tokenPairs, providerConfig) {
    const poolAddressSet = /* @__PURE__ */ new Set();
    const sortedTokenPairs = [];
    const sortedPoolAddresses = [];
    for (const tokenPair of tokenPairs) {
      const [tokenA, tokenB] = tokenPair;
      const { poolAddress, token0, token1 } = this.getPoolAddress(
        tokenA,
        tokenB
      );
      if (poolAddressSet.has(poolAddress)) {
        continue;
      }
      poolAddressSet.add(poolAddress);
      sortedTokenPairs.push([token0, token1]);
      sortedPoolAddresses.push(poolAddress);
    }
    log.debug(
      `getPools called with ${tokenPairs.length} token pairs. Deduped down to ${poolAddressSet.size}`
    );
    const reservesResults = await this.getPoolsData(
      sortedPoolAddresses,
      "getReserves",
      providerConfig
    );
    log.info(
      `Got reserves for ${poolAddressSet.size} pools ${providerConfig?.blockNumber ? `as of block: ${await providerConfig?.blockNumber}.` : ``}`
    );
    const poolAddressToPool = {};
    const invalidPools = [];
    for (let i = 0; i < sortedPoolAddresses.length; i++) {
      const reservesResult = reservesResults[i];
      if (!reservesResult?.success) {
        const [token02, token12] = sortedTokenPairs[i];
        invalidPools.push([token02, token12]);
        continue;
      }
      const [token0, token1] = sortedTokenPairs[i];
      const [reserve0, reserve1] = reservesResult.result;
      const pool = new PairV1(
        CurrencyAmount.fromRawAmount(token0, reserve0.toString()),
        CurrencyAmount.fromRawAmount(token1, reserve1.toString())
      );
      const poolAddress = sortedPoolAddresses[i];
      poolAddressToPool[poolAddress] = pool;
    }
    if (invalidPools.length > 0) {
      log.info(
        {
          invalidPools: _16.map(
            invalidPools,
            ([token0, token1]) => `${token0.symbol}/${token1.symbol}`
          )
        },
        `${invalidPools.length} pools invalid after checking their slot0 and liquidity results. Dropping.`
      );
    }
    const poolStrs = _16.map(Object.values(poolAddressToPool), poolToString);
    log.debug({ poolStrs }, `Found ${poolStrs.length} valid pools`);
    return {
      getPool: (tokenA, tokenB) => {
        const { poolAddress } = this.getPoolAddress(tokenA, tokenB);
        return poolAddressToPool[poolAddress];
      },
      getPoolByAddress: (address) => poolAddressToPool[address],
      getAllPools: () => Object.values(poolAddressToPool)
    };
  }
  getPoolAddress(tokenA, tokenB) {
    const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA];
    const cacheKey = `${this.chainId}/${token0.address}/${token1.address}`;
    const cachedAddress = this.POOL_ADDRESS_CACHE[cacheKey];
    if (cachedAddress) {
      return { poolAddress: cachedAddress, token0, token1 };
    }
    const poolAddress = PairV1.getAddress(token0, token1);
    this.POOL_ADDRESS_CACHE[cacheKey] = poolAddress;
    return { poolAddress, token0, token1 };
  }
  async getPoolsData(poolAddresses, functionName, providerConfig) {
    const { results, blockNumber } = await retry7(async () => {
      return this.multicall2Provider.callSameFunctionOnMultipleContracts({
        addresses: poolAddresses,
        abi: pulsexPairABI,
        functionName,
        providerConfig
      });
    }, this.retryOptions);
    log.debug(`Pool data fetched as of block ${blockNumber}`);
    return results;
  }
};
function buildTrade(tokenInCurrency, tokenOutCurrency, tradeType, routeAmounts) {
  const v1RouteAmounts = _16.filter(
    routeAmounts,
    (routeAmount) => routeAmount.protocol === "V1" /* V1 */
  );
  const v2RouteAmounts = _16.filter(
    routeAmounts,
    (routeAmount) => routeAmount.protocol === "V2" /* V2 */
  );
  const mixedRouteAmounts = _16.filter(
    routeAmounts,
    (routeAmount) => routeAmount.protocol === "MIXED" /* MIXED */
  );
  const v1Routes = _16.map(
    v1RouteAmounts,
    (routeAmount) => {
      const { route, amount, quote } = routeAmount;
      if (tradeType == TradeType.EXACT_INPUT) {
        const amountCurrency = CurrencyAmount.fromFractionalAmount(
          tokenInCurrency,
          amount.numerator,
          amount.denominator
        );
        const quoteCurrency = CurrencyAmount.fromFractionalAmount(
          tokenOutCurrency,
          quote.numerator,
          quote.denominator
        );
        const routeV1SDK = new RouteV1$1(
          route.pairs,
          amountCurrency.currency,
          quoteCurrency.currency
        );
        return {
          routev1: routeV1SDK,
          inputAmount: amountCurrency,
          outputAmount: quoteCurrency
        };
      } else {
        const quoteCurrency = CurrencyAmount.fromFractionalAmount(
          tokenInCurrency,
          quote.numerator,
          quote.denominator
        );
        const amountCurrency = CurrencyAmount.fromFractionalAmount(
          tokenOutCurrency,
          amount.numerator,
          amount.denominator
        );
        const routeV1SDK = new RouteV1$1(
          route.pairs,
          quoteCurrency.currency,
          amountCurrency.currency
        );
        return {
          routev1: routeV1SDK,
          inputAmount: quoteCurrency,
          outputAmount: amountCurrency
        };
      }
    }
  );
  const v2Routes = _16.map(
    v2RouteAmounts,
    (routeAmount) => {
      const { route, amount, quote } = routeAmount;
      if (tradeType == TradeType.EXACT_INPUT) {
        const amountCurrency = CurrencyAmount.fromFractionalAmount(
          tokenInCurrency,
          amount.numerator,
          amount.denominator
        );
        const quoteCurrency = CurrencyAmount.fromFractionalAmount(
          tokenOutCurrency,
          quote.numerator,
          quote.denominator
        );
        const routeV2SDK = new RouteV2$1(
          route.pairs,
          amountCurrency.currency,
          quoteCurrency.currency
        );
        return {
          routev2: routeV2SDK,
          inputAmount: amountCurrency,
          outputAmount: quoteCurrency
        };
      } else {
        const quoteCurrency = CurrencyAmount.fromFractionalAmount(
          tokenInCurrency,
          quote.numerator,
          quote.denominator
        );
        const amountCurrency = CurrencyAmount.fromFractionalAmount(
          tokenOutCurrency,
          amount.numerator,
          amount.denominator
        );
        const routeV2SDK = new RouteV2$1(
          route.pairs,
          quoteCurrency.currency,
          amountCurrency.currency
        );
        return {
          routev2: routeV2SDK,
          inputAmount: quoteCurrency,
          outputAmount: amountCurrency
        };
      }
    }
  );
  const mixedRoutes = _16.map(
    mixedRouteAmounts,
    (routeAmount) => {
      const { route, amount, quote } = routeAmount;
      if (tradeType != TradeType.EXACT_INPUT) {
        throw new Error(
          "Mixed routes are only supported for exact input trades"
        );
      }
      const amountCurrency = CurrencyAmount.fromFractionalAmount(
        tokenInCurrency,
        amount.numerator,
        amount.denominator
      );
      const quoteCurrency = CurrencyAmount.fromFractionalAmount(
        tokenOutCurrency,
        quote.numerator,
        quote.denominator
      );
      const routeRaw = new MixedRouteSDK(
        route.pools,
        amountCurrency.currency,
        quoteCurrency.currency
      );
      return {
        mixedRoute: routeRaw,
        inputAmount: amountCurrency,
        outputAmount: quoteCurrency
      };
    }
  );
  const trade = new Trade({ v1Routes, v2Routes, mixedRoutes, tradeType });
  return trade;
}
function buildSwapMethodParameters(trade, swapConfig, chainId) {
  if (swapConfig.type == 1 /* SWAP_ROUTER_02 */) {
    const { recipient, slippageTolerance, deadline, inputTokenPermit } = swapConfig;
    return {
      ...SwapRouter.swapCallParameters(trade, {
        recipient,
        slippageTolerance,
        deadlineOrPreviousBlockhash: deadline,
        inputTokenPermit
      }),
      to: PULSEX_SWAP_ROUTER_ADDRESSES[chainId]
    };
  }
  throw new Error(`Unsupported swap type ${swapConfig}`);
}

// src/util/unsupported-tokens.ts
var UNSUPPORTED_TOKENS = {
  name: "Unsupported Token List",
  timestamp: "2021-01-05T20:47:02.923Z",
  version: {
    major: 1,
    minor: 0,
    patch: 0
  },
  tags: {},
  logoURI: "ipfs://QmNa8mQkrNKp1WEEeGjFezDmDeodkWRevGFN8JCV7b4Xir",
  keywords: ["uniswap", "unsupported", "broken"],
  tokens: [
    {
      name: "USD Token",
      address: "0xd233d1f6fd11640081abb8db125f722b5dc729dc",
      symbol: "USD",
      decimals: 9,
      chainId: 1,
      logoURI: ""
    },
    {
      name: "UNI HODL",
      address: "0x4bf5dc91E2555449293D7824028Eb8Fe5879B689",
      symbol: "UniH",
      decimals: 18,
      chainId: 1,
      logoURI: ""
    },
    {
      name: "Gold Tether",
      address: "0x4922a015c4407F87432B179bb209e125432E4a2A",
      symbol: "XAUt",
      decimals: 6,
      chainId: 1,
      logoURI: "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0x4922a015c4407F87432B179bb209e125432E4a2A/logo.png"
    },
    {
      name: "Grump Cat",
      address: "0x93B2FfF814FCaEFFB01406e80B4Ecd89Ca6A021b",
      symbol: "GRUMPY",
      decimals: 9,
      chainId: 1,
      logoURI: "https://raw.githubusercontent.com/trustwallet/assets/master/blockchains/ethereum/assets/0x4922a015c4407F87432B179bb209e125432E4a2A/logo.png"
    },
    {
      name: "apeUSD-UMA Synthetic USD (Dec 2021)",
      address: "0xfA5e27893aee4805283D86e4283Da64F8c72dd56",
      symbol: "apeUSD-UMA-DEC21",
      decimals: 18,
      chainId: 1,
      logoURI: ""
    },
    {
      chainId: 1,
      address: "0xc6b11850241c5127eab73af4b6c68bc267cbbff4",
      name: "oWETHp Put 360 DEC2520",
      symbol: "oWETH-360P-12/25/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oYFIp Put 25000 OCT0220",
      address: "0x452b421be5b30f0c6ad8c3f03c06bdaab4f5c56c",
      symbol: "oYFI-25000P-10/02/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oWETHp Put 360 OCT3020",
      address: "0x0578779e746d7186253a36cf651ea786acfcf087",
      symbol: "oWETH-360P-10/30/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "ETHc Call 500 OCT3020",
      address: "0xf9aba2e43fb19184408ea3b572a0fd672946f87b",
      symbol: "oETH-500C-10/30/20",
      decimals: 6
    },
    {
      chainId: 1,
      name: "oBALp Put 22 OCT0220",
      address: "0xdb0991dfc7e828b5a2837dc82d68e16490562c8d",
      symbol: "oBAL-22P-10/02/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oCOMPp Put 150 SEP2520",
      address: "0xe951ebe6b4420ab3f4844cf36dedd263d095b416",
      symbol: "oCOMP-150P-09/25/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oCRVp Put 3 SEP252020",
      address: "0x9215bd49b59748419eac6bad9dbe247df06ebdb9",
      symbol: "oCRV-3P-09/25/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oETHp Put 180 SEP2520",
      address: "0xE3A2c34Fa2F59ffa95C4ACd1E5663633d45Bc3AD",
      symbol: "oETH-180P-09/25/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oETHc Call 400 SEP2520",
      address: "0x05977EBC26825C0CD6097E0Ad7204721516711Eb",
      symbol: "oETH-400C-09/25/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oWETHp Put 380 SEP1820",
      address: "0x31f88266301b08631f9f0e33fd5c43c2a5d1e5b2",
      symbol: "oWETH-380P-09/18/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oYFIp Put 8500 SEP1820",
      address: "0xd1cec2f67fdc4c60e0963515dfc3343f31e32e47",
      symbol: "oYFI-8500P-09/18/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oWETHp Put 370 SEP1120",
      address: "0x15844029b2c2bf24506e9937739a9a912f1e4354",
      symbol: "oWETH-370P-09/11/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oWETHp Put 400 SEP0420",
      address: "0x5562c33c383f6386be4f6dcdbd35a3a99bbcfde6",
      symbol: "oWETH-400P-09/04/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "oETHp Put 200 AUG2820",
      address: "0x3CBFC1397deF0602c2d211c70A1c0c38CEDB5448",
      symbol: "oWETH-400P-09/04/20",
      decimals: 7
    },
    {
      chainId: 1,
      name: "Opyn cDai Insurance",
      symbol: "ocDai",
      address: "0x98cc3bd6af1880fcfda17ac477b2f612980e5e33",
      decimals: 8
    },
    {
      chainId: 1,
      name: "Opyn cUSDC Insurance",
      symbol: "ocUSDC",
      address: "0x8ED9f862363fFdFD3a07546e618214b6D59F03d4",
      decimals: 8
    },
    {
      chainId: 1,
      address: "0x176C674Ee533C6139B0dc8b458D72A93dCB3e705",
      symbol: "iAAVE",
      name: "Synth Inverse Aave",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iAAVE.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x8A8079c7149B8A1611e5C5d978DCA3bE16545F83",
      symbol: "iADA",
      name: "Synth Inverse Cardano",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iADA.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xAFD870F32CE54EfdBF677466B612bf8ad164454B",
      symbol: "iBNB",
      name: "Synth Inverse Binance Coin",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iBNB.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xD6014EA05BDe904448B743833dDF07c3C7837481",
      symbol: "iBTC",
      name: "Synth Inverse Bitcoin",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iBTC.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x336213e1DDFC69f4701Fc3F86F4ef4A160c1159d",
      symbol: "iCEX",
      name: "Synth Inverse Centralised Exchange Index",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iCEX.svg",
      tags: ["index", "inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x6345728B1ccE16E6f8C509950b5c84FFF88530d9",
      symbol: "iCOMP",
      name: "Synth Inverse Compound",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iCOMP.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xCB98f42221b2C251A4E74A1609722eE09f0cc08E",
      symbol: "iDASH",
      name: "Synth Inverse Dash",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iDASH.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x14d10003807AC60d07BB0ba82cAeaC8d2087c157",
      symbol: "iDEFI",
      name: "Synth Inverse DeFi Index",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iDEFI.svg",
      tags: ["index", "inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x46a97629C9C1F58De6EC18C7F536e7E6d6A6ecDe",
      symbol: "iDOT",
      name: "Synth Inverse Polkadot",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iDOT.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xF4EebDD0704021eF2a6Bbe993fdf93030Cd784b4",
      symbol: "iEOS",
      name: "Synth Inverse EOS",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iEOS.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xd50c1746D835d2770dDA3703B69187bFfeB14126",
      symbol: "iETC",
      name: "Synth Inverse Ethereum Classic",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iETC.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xA9859874e1743A32409f75bB11549892138BBA1E",
      symbol: "iETH",
      name: "Synth Inverse Ether",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iETH.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x2d7aC061fc3db53c39fe1607fB8cec1B2C162B01",
      symbol: "iLINK",
      name: "Synth Inverse Chainlink",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iLINK.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x79da1431150C9b82D2E5dfc1C68B33216846851e",
      symbol: "iLTC",
      name: "Synth Inverse Litecoin",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iLTC.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xA5a5DF41883Cdc00c4cCC6E8097130535399d9a3",
      symbol: "iOIL",
      name: "Synth Inverse Perpetual Oil Futures",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iOIL.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x0fEd38108bdb8e62ef7b5680E8E0726E2F29e0De",
      symbol: "iREN",
      name: "Synth Inverse Ren",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iREN.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xC5807183a9661A533CB08CbC297594a0B864dc12",
      symbol: "iTRX",
      name: "Synth Inverse TRON",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iTRX.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x36A00FF9072570eF4B9292117850B8FE08d96cce",
      symbol: "iUNI",
      name: "Synth Inverse Uniswap",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iUNI.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x4AdF728E2Df4945082cDD6053869f51278fae196",
      symbol: "iXMR",
      name: "Synth Inverse Monero",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iXMR.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x27269b3e45A4D3E79A3D6BFeE0C8fB13d0D711A6",
      symbol: "iXRP",
      name: "Synth Inverse Ripple",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iXRP.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x8deef89058090ac5655A99EEB451a4f9183D1678",
      symbol: "iXTZ",
      name: "Synth Inverse Tezos",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iXTZ.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0x592244301CeA952d6daB2fdC1fE6bd9E53917306",
      symbol: "iYFI",
      name: "Synth Inverse yearn.finance",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/iYFI.svg",
      tags: ["inverse", "synth"]
    },
    {
      chainId: 1,
      address: "0xcD39b5434a0A92cf47D1F567a7dF84bE356814F0",
      symbol: "s1INCH",
      name: "Synth 1inch",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/s1INCH.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x7537AAe01f3B218DAE75e10d952473823F961B87",
      symbol: "sAAPL",
      name: "Synth Apple",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sAAPL.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xd2dF355C19471c8bd7D8A3aa27Ff4e26A21b4076",
      symbol: "sAAVE",
      name: "Synth Aave",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sAAVE.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xe36E2D3c7c34281FA3bC737950a68571736880A1",
      symbol: "sADA",
      name: "Synth Cardano",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sADA.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x9CF7E61853ea30A41b02169391b393B901eac457",
      symbol: "sAMZN",
      name: "Synth Amazon",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sAMZN.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xF48e200EAF9906362BB1442fca31e0835773b8B4",
      symbol: "sAUD",
      name: "Synth Australian Dollars",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sAUD.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x617aeCB6137B5108D1E7D4918e3725C8cEbdB848",
      symbol: "sBNB",
      name: "Synth Binance Coin",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sBNB.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xfE18be6b3Bd88A2D2A7f928d00292E7a9963CfC6",
      symbol: "sBTC",
      name: "Synth Bitcoin",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sBTC.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xeABACD844A196D7Faf3CE596edeBF9900341B420",
      symbol: "sCEX",
      name: "Synth Centralised Exchange Index",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sCEX.svg",
      tags: ["index", "synth"]
    },
    {
      chainId: 1,
      address: "0x0F83287FF768D1c1e17a42F44d644D7F22e8ee1d",
      symbol: "sCHF",
      name: "Synth Swiss Franc",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sCHF.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x9EeF4CA7aB9fa8bc0650127341C2d3F707a40f8A",
      symbol: "sCOIN",
      name: "Synth Coinbase",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sCOIN.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xEb029507d3e043DD6C87F2917C4E82B902c35618",
      symbol: "sCOMP",
      name: "Synth Compound",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sCOMP.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xD38aEb759891882e78E957c80656572503D8c1B1",
      symbol: "sCRV",
      name: "Synth Curve DAO Token",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sCRV.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xfE33ae95A9f0DA8A845aF33516EDc240DCD711d6",
      symbol: "sDASH",
      name: "Synth Dash",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sDASH.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xe1aFe1Fd76Fd88f78cBf599ea1846231B8bA3B6B",
      symbol: "sDEFI",
      name: "Synth DeFi Index",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sDEFI.svg",
      tags: ["index", "synth"]
    },
    {
      chainId: 1,
      address: "0x1715AC0743102BF5Cd58EfBB6Cf2dC2685d967b6",
      symbol: "sDOT",
      name: "Synth Polkadot",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sDOT.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x88C8Cf3A212c0369698D13FE98Fcb76620389841",
      symbol: "sEOS",
      name: "Synth EOS",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sEOS.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x22602469d704BfFb0936c7A7cfcD18f7aA269375",
      symbol: "sETC",
      name: "Synth Ethereum Classic",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sETC.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x5e74C9036fb86BD7eCdcb084a0673EFc32eA31cb",
      symbol: "sETH",
      name: "Synth Ether",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sETH.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xD71eCFF9342A5Ced620049e616c5035F1dB98620",
      symbol: "sEUR",
      name: "Synth Euros",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sEUR.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xf50B5e535F62a56A9BD2d8e2434204E726c027Fa",
      symbol: "sFB",
      name: "Synth Facebook",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sFB.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x23348160D7f5aca21195dF2b70f28Fce2B0be9fC",
      symbol: "sFTSE",
      name: "Synth FTSE 100 Index",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sFTSE.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x97fe22E7341a0Cd8Db6F6C021A24Dc8f4DAD855F",
      symbol: "sGBP",
      name: "Synth Pound Sterling",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sGBP.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xC63B8ECCE56aB9C46184eC6aB85e4771fEa4c8AD",
      symbol: "sGOOG",
      name: "Synth Alphabet",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sGOOG.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xF6b1C627e95BFc3c1b4c9B825a032Ff0fBf3e07d",
      symbol: "sJPY",
      name: "Synth Japanese Yen",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sJPY.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x269895a3dF4D73b077Fc823dD6dA1B95f72Aaf9B",
      symbol: "sKRW",
      name: "Synth South Korean Won",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sKRW.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xbBC455cb4F1B9e4bFC4B73970d360c8f032EfEE6",
      symbol: "sLINK",
      name: "Synth Chainlink",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sLINK.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xC14103C2141E842e228FBaC594579e798616ce7A",
      symbol: "sLTC",
      name: "Synth Litecoin",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sLTC.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x745a824D6aBBD236AA794b5530062778A6Ad7523",
      symbol: "sMSFT",
      name: "Synth Microsoft",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sMSFT.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x5A7E3c07604EB515C16b36cd51906a65f021F609",
      symbol: "sNFLX",
      name: "Synth Netflix",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sNFLX.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x757de3ac6B830a931eF178C6634c5C551773155c",
      symbol: "sNIKKEI",
      name: "Synth Nikkei 225 Index",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sNIKKEI.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x6d16cF3EC5F763d4d99cB0B0b110eefD93B11B56",
      symbol: "sOIL",
      name: "Synth Perpetual Oil Futures",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sOIL.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xD31533E8d0f3DF62060e94B3F1318137bB6E3525",
      symbol: "sREN",
      name: "Synth Ren",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sREN.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x0352557B007A4Aae1511C114409b932F06F9E2f4",
      symbol: "sRUNE",
      name: "Synth THORChain",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sRUNE.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xf2E08356588EC5cd9E437552Da87C0076b4970B0",
      symbol: "sTRX",
      name: "Synth TRON",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sTRX.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x918dA91Ccbc32B7a6A0cc4eCd5987bbab6E31e6D",
      symbol: "sTSLA",
      name: "Synth Tesla",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sTSLA.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x30635297E450b930f8693297eBa160D9e6c8eBcf",
      symbol: "sUNI",
      name: "Synth Uniswap",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sUNI.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x6A22e5e94388464181578Aa7A6B869e00fE27846",
      symbol: "sXAG",
      name: "Synth Silver Ounce",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sXAG.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x261EfCdD24CeA98652B9700800a13DfBca4103fF",
      symbol: "sXAU",
      name: "Synth Gold Ounce",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sXAU.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x5299d6F7472DCc137D7f3C4BcfBBB514BaBF341A",
      symbol: "sXMR",
      name: "Synth Monero",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sXMR.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0xa2B0fDe6D710e201d0d608e924A484d1A5fEd57c",
      symbol: "sXRP",
      name: "Synth Ripple",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sXRP.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x2e59005c5c0f0a4D77CcA82653d48b46322EE5Cd",
      symbol: "sXTZ",
      name: "Synth Tezos",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sXTZ.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x992058B7DB08F9734d84485bfbC243C4ee6954A7",
      symbol: "sYFI",
      name: "Synth yearn.finance",
      decimals: 18,
      logoURI: "https://raw.githubusercontent.com/Synthetixio/synthetix-assets/v2.0.10/synths/sYFI.svg",
      tags: ["synth"]
    },
    {
      chainId: 1,
      address: "0x81ab848898b5ffD3354dbbEfb333D5D183eEDcB5",
      name: "yUSD Synthetic Expiring 1 September 2020",
      symbol: "yUSDSEP20",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xB2FdD60AD80ca7bA89B9BAb3b5336c2601C020b4",
      name: "yUSD Synthetic Expiring 1 October 2020",
      symbol: "yUSDOCT20",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x208d174775dc39fe18b1b374972f77ddec6c0f73",
      name: "uUSDrBTC Synthetic Expiring 1 Oct 2020",
      symbol: "uUSDrBTC-OCT",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xf06ddacf71e2992e2122a1a0168c6967afdf63ce",
      name: "uUSDrBTC Synthetic Expiring 31 Dec 2020",
      symbol: "uUSDrBTC-DEC",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xd16c79c8a39d44b2f3eb45d2019cd6a42b03e2a9",
      name: "uUSDwETH Synthetic Expiring 31 Dec 2020",
      symbol: "uUSDwETH-DEC",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x3d995510f8d82c2ea341845932b5ddde0bead9a3",
      name: "uGAS-JAN21 Token Expiring 31 Jan 2021",
      symbol: "uGAS-JAN21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x90f802c7e8fb5d40b0de583e34c065a3bd2020d8",
      name: "YD-ETH-MAR21 Token Expiring 31 Mar 2021",
      symbol: "YD-ETH-MAR21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x002f0b1a71c5730cf2f4da1970a889207bdb6d0d",
      name: "YD-BTC-MAR21 Token Expiring 31 Mar 2021",
      symbol: "YD-BTC-MAR21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x1062ad0e59fa67fa0b27369113098cc941dd0d5f",
      name: "UMA 35 Call Expirying 30 Apr 2021",
      symbol: "UMAc35-0421",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xf93340b1a3adf7eedcaec25fae8171d4b736e89f",
      name: "pxUSD Synthetic USD Expiring 1 April 2021",
      symbol: "pxUSD_MAR2021",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x84bd083b1c8bf929f39c98bc17cf518f40154f58",
      name: "Mario Cash Synthetic Token Expiring 15 January 2021",
      symbol: "Mario Cash-JAN-2021",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x81fab276aec924fbde190cf379783526d413cf70",
      name: "uGAS-FEB21 Token Expiring 28 Feb 2021",
      symbol: "uGAS-FEB21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x4e110603e70b0b5f1c403ee543b37e1f1244cf28",
      name: "uGAS-MAR21 Token Expiring 31 Mar 2021",
      symbol: "uGAS-MAR21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xcf55a7f92d5e0c6683debbc1fc20c0a6e056df13",
      name: "Zelda Elastic Cash",
      symbol: "Zelda Elastic Cash",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x654eebac62240e6c56bab5f6adf7cfa74a894510",
      name: "Zelda Spring Nuts Cash",
      symbol: "Zelda Spring Nuts Cash",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xa48920cc1ad85d8ea13af5d7be180c0338c306dd",
      name: "Zelda Summer Nuts Cash",
      symbol: "Zelda Summer Nuts Cash",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x249a198d59b57fda5dda90630febc86fd8c7594c",
      name: "Zelda Whirlwind Cash",
      symbol: "Zelda Whirlwind Cash",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x5ed1406873c9eb91f6f9a67ac4e152387c1132e7",
      name: "Zelda Reinforced Cash",
      symbol: "Zelda Reinforced Cash",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x8104c9f13118320eefe5fbea8a44d600b85981ef",
      name: "Mini Mario Summer Cash",
      symbol: "Mini Mario Summer Cash",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x69746c719e59674b147df25f50e7cfa0673cb625",
      name: "Mini Mario Spring Cash",
      symbol: "Mini Mario Spring Cash",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x6b1257641d18791141f025eab36fb567c4b564ff",
      name: "Bitcoin Dominance Token 31 March 2021",
      symbol: "BTCDOM-MAR2021",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x4e83b6287588a96321b2661c5e041845ff7814af",
      name: "Altcoin Dominance Token 31 March 2021",
      symbol: "ALTDOM-MAR2021",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x59fec83ec709c893aedd1a144cf1828eb04127cd",
      name: "pxGOLD Synthetic GOLD Expiring 31 May 2021",
      symbol: "pxGOLD_MAY2021",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x89337BFb7938804c3776C9FB921EccAf5ab76758",
      name: "Compound Annualized Rate Future Expiring 28 March 2021",
      symbol: "CAR-USDC-MAR21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xec58d3aefc9aaa2e0036fa65f70d569f49d9d1ed",
      name: "uSTONKS Index Token April 2021",
      symbol: "uSTONKS_APR21",
      decimals: 6
    },
    {
      chainId: 1,
      address: "0xa6B9d7E3d76cF23549293Fb22c488E0Ea591A44e",
      name: "uGAS-JUN21 Token Expiring 30 Jun 2021",
      symbol: "uGAS-JUN21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xe813b65da6c38a04591aed3f082d32db7d53c382",
      name: "Yield Dollar [WETH Dec 2021]",
      symbol: "YD-ETH-DEC21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x4b606e9eb2228c70f44453afe5a73e1fea258ce1",
      name: "pxUSD Synthetic USD Expiring 31 Mar 2022",
      symbol: "pxUSD_MAR2022",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x5247c0db4044fb6f97f32c7e1b48758019a5a912",
      name: "pxGOLD Synthetic Gold Expiring 31 Mar 2022",
      symbol: "pxGOLD_MAR2022",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x56fb1acaff95c0b6ebcd17c8361a63d98b1a5a11",
      name: "uForex CNYUSD Synthetic Token April 2021",
      symbol: "uCNYUSD-APR",
      decimals: 6
    },
    {
      chainId: 1,
      address: "0xd49fa405dce086c65d66ca1ca41f8e98583812b4",
      name: "uForex EURUSD Synthetic Token April 2021",
      symbol: "uEURUSD-APR",
      decimals: 6
    },
    {
      chainId: 1,
      address: "0x29dddacba3b231ee8d673dd0f0fa759ea145561b",
      name: "DEFI_PULSE_TOTAL_TVL Synthetic Token Expiring 15 April 2021",
      symbol: "TVL_ALL_APRIL15",
      decimals: 6
    },
    {
      chainId: 1,
      address: "0xcbe430927370e95b4b10cfc702c6017ec7abefc3",
      name: "Yield Dollar [WETH Jun 2021]",
      symbol: "YD-ETH-JUN21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x4b7fb448df91c8ed973494f8c8c4f12daf3a8521",
      name: "Yield Dollar [renBTC Jun 2021]",
      symbol: "YD-BTC-JUN21",
      decimals: 8
    },
    {
      chainId: 1,
      address: "0x3108c33b6fb38efedaefd8b5f7ca01d5f5c7372d",
      name: "Yield Dollar UMA 21",
      symbol: "yUMA21",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x0cae9e4d663793c2a2A0b211c1Cf4bBca2B9cAa7",
      name: "Mirrored Amazon",
      symbol: "MAMZN",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x31c63146a635EB7465e5853020b39713AC356991",
      name: "M US Oil",
      symbol: "MUSO",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x59A921Db27Dd6d4d974745B7FfC5c33932653442",
      name: "M Google",
      symbol: "MGOOGL",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xf72FCd9DCF0190923Fadd44811E240Ef4533fc86",
      name: "Mirrored ProShares",
      symbol: "MVIXY",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x56aA298a19C93c6801FDde870fA63EF75Cc0aF72",
      name: "Mirrored Alibaba",
      symbol: "MBABA",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x0e99cC0535BB6251F6679Fa6E65d6d3b430e840B",
      name: "Mirrored Facebook",
      symbol: "MFB",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x13B02c8dE71680e71F0820c996E4bE43c2F57d15",
      name: "Mirrored Invesco QQ",
      symbol: "MQQQ",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x41BbEDd7286dAab5910a1f15d12CBda839852BD7",
      name: "Mirrored Microsoft",
      symbol: "MMSFT",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x9d1555d8cB3C846Bb4f7D5B1B1080872c3166676",
      name: "Mirrored iShares Si",
      symbol: "MSLV",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x21cA39943E91d704678F5D00b6616650F066fD63",
      name: "Mirrored Tesla",
      symbol: "MTSLA",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0xe82bbB62fA81d0701643d1675FB50ec52fD3Df92",
      name: "DYDX Token",
      symbol: "DYDX",
      decimals: 18
    },
    {
      chainId: 10,
      address: "0xE405de8F52ba7559f9df3C368500B6E6ae6Cee49",
      name: "sETH",
      symbol: "Synth Ether",
      decimals: 18
    },
    {
      chainId: 10,
      address: "0x298B9B95708152ff6968aafd889c6586e9169f1D",
      name: "sBTC",
      symbol: "Synth Bitcoin",
      decimals: 18
    },
    {
      chainId: 10,
      address: "0xc5Db22719A06418028A40A9B5E9A7c02959D0d08",
      name: "sLINK",
      symbol: "Synth Link",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x6B60eE11b73230045cF9095E6e43AE9Ea638e172",
      name: "Shatner",
      symbol: "SHAT",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x7277a44D1325D81Ac58893002a1B40a41bea43fe",
      name: "FAANG Index",
      symbol: "FAANG",
      decimals: 18
    },
    {
      chainId: 1,
      address: "0x76175599887730786bdA1545D0D7AcE8737fEBB1",
      name: "ENS DAO",
      symbol: "ENS",
      decimals: 18
    }
  ]
};
var V1Route = class extends RouteV1$1 {
  constructor() {
    super(...arguments);
    this.protocol = "V1" /* V1 */;
  }
};
var V2Route = class extends RouteV2$1 {
  constructor() {
    super(...arguments);
    this.protocol = "V2" /* V2 */;
  }
};
var MixedRoute2 = class extends MixedRouteSDK {
  constructor() {
    super(...arguments);
    this.protocol = "MIXED" /* MIXED */;
  }
};
var SwapToRatioStatus = /* @__PURE__ */ ((SwapToRatioStatus2) => {
  SwapToRatioStatus2[SwapToRatioStatus2["SUCCESS"] = 1] = "SUCCESS";
  SwapToRatioStatus2[SwapToRatioStatus2["NO_ROUTE_FOUND"] = 2] = "NO_ROUTE_FOUND";
  SwapToRatioStatus2[SwapToRatioStatus2["NO_SWAP_NEEDED"] = 3] = "NO_SWAP_NEEDED";
  return SwapToRatioStatus2;
})(SwapToRatioStatus || {});
var SwapType = /* @__PURE__ */ ((SwapType2) => {
  SwapType2[SwapType2["UNIVERSAL_ROUTER"] = 0] = "UNIVERSAL_ROUTER";
  SwapType2[SwapType2["SWAP_ROUTER_02"] = 1] = "SWAP_ROUTER_02";
  return SwapType2;
})(SwapType || {});
var IRouter = class {
};
var ISwapToRatio = class {
};

// src/routers/alpha-router/config.ts
var DEFAULT_ROUTING_CONFIG_BY_CHAIN = (chainId) => {
  switch (chainId) {
    default:
      return {
        v1PoolSelection: {
          topN: 3,
          topNDirectSwaps: 1,
          topNTokenInOut: 5,
          topNSecondHop: 2,
          topNWithEachBaseToken: 2,
          topNWithBaseToken: 6
        },
        v2PoolSelection: {
          topN: 3,
          topNDirectSwaps: 1,
          topNTokenInOut: 5,
          topNSecondHop: 2,
          topNWithEachBaseToken: 2,
          topNWithBaseToken: 6
        },
        maxSwapsPerPath: 3,
        minSplits: 1,
        maxSplits: 7,
        distributionPercent: 5,
        forceCrossProtocol: false
      };
  }
};
var usdGasTokensByChain = {
  [ChainId.PULSECHAIN]: [eDAI_PULSECHAIN, eUSDC_PULSECHAIN, eUSDT_PULSECHAIN],
  [ChainId.PULSECHAIN_TESTNET]: [tDAI_TESTNET]
};
var IV1GasModelFactory = class {
};
var IV2GasModelFactory = class {
};
var IOnChainGasModelFactory = class {
};

// src/routers/alpha-router/functions/best-swap-route.ts
async function getBestSwapRoute(amount, percents, routesWithValidQuotes, routeType, chainId, routingConfig) {
  const now = Date.now();
  const { forceMixedRoutes } = routingConfig;
  if (forceMixedRoutes) {
    log.info(
      {
        forceMixedRoutes
      },
      "Forcing mixed routes by filtering out other route types"
    );
    routesWithValidQuotes = _16.filter(routesWithValidQuotes, (quotes) => {
      return quotes.protocol === "MIXED" /* MIXED */;
    });
    if (!routesWithValidQuotes) {
      return null;
    }
  }
  const percentToQuotes = {};
  for (const routeWithValidQuote of routesWithValidQuotes) {
    if (!percentToQuotes[routeWithValidQuote.percent]) {
      percentToQuotes[routeWithValidQuote.percent] = [];
    }
    percentToQuotes[routeWithValidQuote.percent].push(routeWithValidQuote);
  }
  metric.putMetric(
    "BuildRouteWithValidQuoteObjects",
    Date.now() - now,
    "Milliseconds" /* Milliseconds */
  );
  const swapRoute = await getBestSwapRouteBy(
    routeType,
    percentToQuotes,
    percents,
    chainId,
    (rq) => rq.quoteAdjustedForGas,
    routingConfig
  );
  if (!swapRoute) {
    return null;
  }
  const { routes: routeAmounts } = swapRoute;
  const totalAmount = _16.reduce(
    routeAmounts,
    (total, routeAmount) => total.add(routeAmount.amount),
    CurrencyAmount.fromRawAmount(routeAmounts[0].amount.currency, 0)
  );
  const missingAmount = amount.subtract(totalAmount);
  if (missingAmount.greaterThan(0)) {
    log.info(
      {
        missingAmount: missingAmount.quotient.toString()
      },
      `Optimal route's amounts did not equal exactIn/exactOut total. Adding missing amount to last route in array.`
    );
    routeAmounts[routeAmounts.length - 1].amount = routeAmounts[routeAmounts.length - 1].amount.add(missingAmount);
  }
  log.info(
    {
      routes: routeAmountsToString(routeAmounts),
      numSplits: routeAmounts.length,
      amount: amount.toExact(),
      quote: swapRoute.quote.toExact(),
      quoteGasAdjusted: swapRoute.quoteGasAdjusted.toFixed(
        Math.min(swapRoute.quoteGasAdjusted.currency.decimals, 2)
      ),
      estimatedGasUSD: swapRoute.estimatedGasUsedUSD.toFixed(
        Math.min(swapRoute.estimatedGasUsedUSD.currency.decimals, 2)
      ),
      estimatedGasToken: swapRoute.estimatedGasUsedQuoteToken.toFixed(
        Math.min(swapRoute.estimatedGasUsedQuoteToken.currency.decimals, 2)
      )
    },
    `Found best swap route. ${routeAmounts.length} split.`
  );
  return swapRoute;
}
async function getBestSwapRouteBy(routeType, percentToQuotes, percents, chainId, by, routingConfig) {
  const percentToSortedQuotes = _16.mapValues(
    percentToQuotes,
    (routeQuotes) => {
      return routeQuotes.sort((routeQuoteA, routeQuoteB) => {
        if (routeType == TradeType.EXACT_INPUT) {
          return by(routeQuoteA).greaterThan(by(routeQuoteB)) ? -1 : 1;
        } else {
          return by(routeQuoteA).lessThan(by(routeQuoteB)) ? -1 : 1;
        }
      });
    }
  );
  const quoteCompFn = routeType == TradeType.EXACT_INPUT ? (a, b) => a.greaterThan(b) : (a, b) => a.lessThan(b);
  const sumFn = (currencyAmounts) => {
    let sum = currencyAmounts[0];
    for (let i = 1; i < currencyAmounts.length; i++) {
      sum = sum.add(currencyAmounts[i]);
    }
    return sum;
  };
  let bestQuote;
  let bestSwap;
  const bestSwapsPerSplit = new FixedReverseHeap(
    Array,
    (a, b) => {
      return quoteCompFn(a.quote, b.quote) ? -1 : 1;
    },
    3
  );
  const { minSplits, maxSplits, forceCrossProtocol } = routingConfig;
  if (!percentToSortedQuotes[100] || minSplits > 1 || forceCrossProtocol) {
    log.info(
      {
        percentToSortedQuotes: _16.mapValues(
          percentToSortedQuotes,
          (p) => p.length
        )
      },
      "Did not find a valid route without any splits. Continuing search anyway."
    );
  } else {
    bestQuote = by(percentToSortedQuotes[100][0]);
    bestSwap = [percentToSortedQuotes[100][0]];
    for (const routeWithQuote of percentToSortedQuotes[100].slice(0, 5)) {
      bestSwapsPerSplit.push({
        quote: by(routeWithQuote),
        routes: [routeWithQuote]
      });
    }
  }
  const queue = new Queue();
  for (let i = percents.length; i >= 0; i--) {
    const percent = percents[i];
    if (!percentToSortedQuotes[percent]) {
      continue;
    }
    queue.enqueue({
      curRoutes: [percentToSortedQuotes[percent][0]],
      percentIndex: i,
      remainingPercent: 100 - percent,
      special: false
    });
    if (!percentToSortedQuotes[percent] || !percentToSortedQuotes[percent][1]) {
      continue;
    }
    queue.enqueue({
      curRoutes: [percentToSortedQuotes[percent][1]],
      percentIndex: i,
      remainingPercent: 100 - percent,
      special: true
    });
  }
  let splits = 1;
  let startedSplit = Date.now();
  while (queue.size > 0) {
    metric.putMetric(
      `Split${splits}Done`,
      Date.now() - startedSplit,
      "Milliseconds" /* Milliseconds */
    );
    startedSplit = Date.now();
    log.info(
      {
        top5: _16.map(
          Array.from(bestSwapsPerSplit.consume()),
          (q) => `${q.quote.toExact()} (${_16(q.routes).map((r) => r.toString()).join(", ")})`
        ),
        onQueue: queue.size
      },
      `Top 3 with ${splits} splits`
    );
    bestSwapsPerSplit.clear();
    let layer = queue.size;
    splits++;
    if (splits >= 3 && bestSwap && bestSwap.length < splits - 1) {
      break;
    }
    if (splits > maxSplits) {
      log.info("Max splits reached. Stopping search.");
      metric.putMetric(`MaxSplitsHitReached`, 1, "Count" /* Count */);
      break;
    }
    while (layer > 0) {
      layer--;
      const { remainingPercent, curRoutes, percentIndex, special } = queue.dequeue();
      for (let i = percentIndex; i >= 0; i--) {
        const percentA = percents[i];
        if (percentA > remainingPercent) {
          continue;
        }
        if (!percentToSortedQuotes[percentA]) {
          continue;
        }
        const candidateRoutesA = percentToSortedQuotes[percentA];
        const routeWithQuoteA = findFirstRouteNotUsingUsedPools(
          curRoutes,
          candidateRoutesA,
          forceCrossProtocol
        );
        if (!routeWithQuoteA) {
          continue;
        }
        const remainingPercentNew = remainingPercent - percentA;
        const curRoutesNew = [...curRoutes, routeWithQuoteA];
        if (remainingPercentNew == 0 && splits >= minSplits) {
          const quotesNew = _16.map(curRoutesNew, (r) => by(r));
          const quoteNew = sumFn(quotesNew);
          bestSwapsPerSplit.push({
            quote: quoteNew,
            routes: curRoutesNew
          });
          if (!bestQuote || quoteCompFn(quoteNew, bestQuote)) {
            bestQuote = quoteNew;
            bestSwap = curRoutesNew;
            if (special) {
              metric.putMetric(
                `BestSwapNotPickingBestForPercent`,
                1,
                "Count" /* Count */
              );
            }
          }
        } else {
          queue.enqueue({
            curRoutes: curRoutesNew,
            remainingPercent: remainingPercentNew,
            percentIndex: i,
            special
          });
        }
      }
    }
  }
  if (!bestSwap) {
    log.info(`Could not find a valid swap`);
    return void 0;
  }
  const postSplitNow = Date.now();
  let quoteGasAdjusted = sumFn(
    _16.map(
      bestSwap,
      (routeWithValidQuote) => routeWithValidQuote.quoteAdjustedForGas
    )
  );
  const estimatedGasUsed = _16(bestSwap).map((routeWithValidQuote) => routeWithValidQuote.gasEstimate).reduce(
    (sum, routeWithValidQuote) => sum + routeWithValidQuote,
    BigInt(0)
  );
  if (!usdGasTokensByChain[chainId] || !usdGasTokensByChain[chainId][0]) {
    throw new Error(
      `Could not find a USD token for computing gas costs on ${chainId}`
    );
  }
  const usdToken = usdGasTokensByChain[chainId][0];
  const usdTokenDecimals = usdToken.decimals;
  const estimatedGasUsedUSDs = _16(bestSwap).map((routeWithValidQuote) => {
    const decimalsDiff = usdTokenDecimals - routeWithValidQuote.gasCostInUSD.currency.decimals;
    if (decimalsDiff == 0) {
      return CurrencyAmount.fromRawAmount(
        usdToken,
        routeWithValidQuote.gasCostInUSD.quotient
      );
    }
    return CurrencyAmount.fromRawAmount(
      usdToken,
      routeWithValidQuote.gasCostInUSD.quotient * 10n ** BigInt(decimalsDiff)
    );
  }).value();
  let estimatedGasUsedUSD = sumFn(estimatedGasUsedUSDs);
  log.info(
    {
      estimatedGasUsedUSD: estimatedGasUsedUSD.toExact(),
      normalizedUsdToken: usdToken,
      routeUSDGasEstimates: _16.map(
        bestSwap,
        (b) => `${b.percent}% ${routeToString(b.route)} ${b.gasCostInUSD.toExact()}`
      )
    },
    "USD gas estimates of best route"
  );
  const estimatedGasUsedQuoteToken = sumFn(
    _16.map(bestSwap, (routeWithValidQuote) => routeWithValidQuote.gasCostInToken)
  );
  const quote = sumFn(
    _16.map(bestSwap, (routeWithValidQuote) => routeWithValidQuote.quote)
  );
  const routeWithQuotes = bestSwap.sort(
    (routeAmountA, routeAmountB) => routeAmountB.amount.greaterThan(routeAmountA.amount) ? 1 : -1
  );
  metric.putMetric(
    "PostSplitDone",
    Date.now() - postSplitNow,
    "Milliseconds" /* Milliseconds */
  );
  return {
    quote,
    quoteGasAdjusted,
    estimatedGasUsed,
    estimatedGasUsedUSD,
    estimatedGasUsedQuoteToken,
    routes: routeWithQuotes
  };
}
var findFirstRouteNotUsingUsedPools = (usedRoutes, candidateRouteQuotes, forceCrossProtocol) => {
  const poolAddressSet = /* @__PURE__ */ new Set();
  const usedPoolAddresses = _16(usedRoutes).flatMap((r) => r.poolAddresses).value();
  for (const poolAddress of usedPoolAddresses) {
    poolAddressSet.add(poolAddress);
  }
  const protocolsSet = /* @__PURE__ */ new Set();
  const usedProtocols = _16(usedRoutes).flatMap((r) => r.protocol).uniq().value();
  for (const protocol of usedProtocols) {
    protocolsSet.add(protocol);
  }
  for (const routeQuote of candidateRouteQuotes) {
    const { poolAddresses, protocol } = routeQuote;
    if (poolAddresses.some((poolAddress) => poolAddressSet.has(poolAddress))) {
      continue;
    }
    const needToForce = forceCrossProtocol && protocolsSet.size == 1;
    if (needToForce && protocolsSet.has(protocol)) {
      continue;
    }
    return routeQuote;
  }
  return null;
};
function calculateRatioAmountIn(optimalRatio, inputTokenPrice, inputBalance, outputBalance) {
  const amountToSwapRaw = new Fraction(inputBalance.quotient).subtract(optimalRatio.multiply(outputBalance.quotient)).divide(optimalRatio.multiply(inputTokenPrice).add(1));
  if (amountToSwapRaw.lessThan(0)) {
    throw new Error("routeToRatio: insufficient input token amount");
  }
  return CurrencyAmount.fromRawAmount(
    inputBalance.currency,
    amountToSwapRaw.quotient
  );
}
async function getV1NativePool(token, poolProvider) {
  const chainId = token.chainId;
  const weth = WRAPPED_NATIVE_CURRENCY[chainId];
  const poolAccessor = await poolProvider.getPools([[weth, token]]);
  const pool = poolAccessor.getPool(weth, token);
  if (!pool || pool.reserve0.equalTo(0) || pool.reserve1.equalTo(0)) {
    log.error(
      {
        weth,
        token,
        reserve0: pool?.reserve0.toExact(),
        reserve1: pool?.reserve1.toExact()
      },
      `Could not find a valid WETH V1 pool with ${token.symbol} for computing gas costs.`
    );
    return null;
  }
  return pool;
}
async function getV2NativePool(token, poolProvider) {
  const chainId = token.chainId;
  const weth = WRAPPED_NATIVE_CURRENCY[chainId];
  const poolAccessor = await poolProvider.getPools([[weth, token]]);
  const pool = poolAccessor.getPool(weth, token);
  if (!pool || pool.reserve0.equalTo(0) || pool.reserve1.equalTo(0)) {
    log.error(
      {
        weth,
        token,
        reserve0: pool?.reserve0.toExact(),
        reserve1: pool?.reserve1.toExact()
      },
      `Could not find a valid WETH V2 pool with ${token.symbol} for computing gas costs.`
    );
    return null;
  }
  return pool;
}
async function getHighestLiquidityV1andV2USDPool(chainId, poolProviderV1, poolProviderV2) {
  const usdTokens = usdGasTokensByChain[chainId];
  if (!usdTokens) {
    throw new Error(
      `Could not find a USD token for computing gas costs on ${chainId}`
    );
  }
  const usdPools = _16.map(usdTokens, (usdToken) => [
    usdToken,
    WRAPPED_NATIVE_CURRENCY[chainId]
  ]);
  const poolAccessorV1 = await poolProviderV1.getPools(usdPools);
  const poolsRawV1 = poolAccessorV1.getAllPools();
  const poolsV1 = _16.filter(
    poolsRawV1,
    (pool) => pool.reserve0.greaterThan(0) && pool.reserve1.greaterThan(0)
  );
  if (poolsV1.length == 0) {
    log.error(
      { poolsV1 },
      `Could not find a USD/WETH pool for computing gas costs.`
    );
    throw new Error(`Can't find USD/WETH pool for computing gas costs.`);
  }
  const maxPoolV1 = _16.maxBy(poolsV1, (pool) => {
    if (pool.token0.equals(WRAPPED_NATIVE_CURRENCY[chainId])) {
      return parseFloat(pool.reserve0.toSignificant(2));
    } else {
      return parseFloat(pool.reserve1.toSignificant(2));
    }
  });
  const poolAccessorV2 = await poolProviderV2.getPools(usdPools);
  const poolsRawV2 = poolAccessorV2.getAllPools();
  const poolsV2 = _16.filter(
    poolsRawV2,
    (pool) => pool.reserve0.greaterThan(0) && pool.reserve1.greaterThan(0)
  );
  if (poolsV2.length == 0) {
    log.error(
      { poolsV2 },
      `Could not find a USD/WETH pool for computing gas costs.`
    );
    throw new Error(`Can't find USD/WETH pool for computing gas costs.`);
  }
  const maxPoolV2 = _16.maxBy(poolsV2, (pool) => {
    if (pool.token0.equals(WRAPPED_NATIVE_CURRENCY[chainId])) {
      return parseFloat(pool.reserve0.toSignificant(2));
    } else {
      return parseFloat(pool.reserve1.toSignificant(2));
    }
  });
  const maxPool = _16.maxBy([maxPoolV1, maxPoolV2], (pool) => {
    if (pool.token0.equals(WRAPPED_NATIVE_CURRENCY[chainId])) {
      return parseFloat(pool.reserve0.toSignificant(2));
    } else {
      return parseFloat(pool.reserve1.toSignificant(2));
    }
  });
  return maxPool;
}
function getGasCostInUSD(usdPool, costNativeCurrency) {
  const nativeCurrency = costNativeCurrency.currency;
  const nativeTokenPrice = usdPool.token0.address == nativeCurrency.address ? usdPool.token0Price : usdPool.token1Price;
  const gasCostUSD = nativeTokenPrice.quote(costNativeCurrency);
  return gasCostUSD;
}
function getGasCostInNativeCurrency(nativeCurrency, gasCostInWei) {
  const costNativeCurrency = CurrencyAmount$1.fromRawAmount(
    nativeCurrency,
    gasCostInWei.toString()
  );
  return costNativeCurrency;
}
async function getGasCostInQuoteToken(quoteToken, nativePool, costNativeCurrency) {
  const nativeTokenPrice = nativePool.token0.address == quoteToken.address ? nativePool.token1Price : nativePool.token0Price;
  const gasCostQuoteToken = nativeTokenPrice.quote(costNativeCurrency);
  return gasCostQuoteToken;
}
async function calculateGasUsed(chainId, route, simulatedGasUsed, v1PoolProvider, v2PoolProvider) {
  const quoteToken = route.quote.currency.wrapped;
  const gasPriceWei = route.gasPriceWei;
  const gasCostInWei = gasPriceWei * simulatedGasUsed;
  const nativeCurrency = WRAPPED_NATIVE_CURRENCY[chainId];
  const costNativeCurrency = getGasCostInNativeCurrency(
    nativeCurrency,
    gasCostInWei
  );
  const usdPool = await getHighestLiquidityV1andV2USDPool(
    chainId,
    v1PoolProvider,
    v2PoolProvider
  );
  const gasCostUSD = await getGasCostInUSD(usdPool, costNativeCurrency);
  let gasCostQuoteToken = costNativeCurrency;
  if (!quoteToken.equals(nativeCurrency)) {
    const nativePools = await Promise.all([
      getV1NativePool(quoteToken, v1PoolProvider),
      getV2NativePool(quoteToken, v2PoolProvider)
    ]);
    const nativePool = nativePools.find((pool) => pool !== null);
    if (!nativePool) {
      log.info(
        "Could not find any V1 or V2 pools to convert the cost into the quote token"
      );
      gasCostQuoteToken = CurrencyAmount$1.fromRawAmount(quoteToken, 0);
    } else {
      gasCostQuoteToken = await getGasCostInQuoteToken(
        quoteToken,
        nativePool,
        costNativeCurrency
      );
    }
  }
  let quoteGasAdjusted;
  if (route.trade.tradeType == TradeType.EXACT_OUTPUT) {
    quoteGasAdjusted = route.quote.add(gasCostQuoteToken);
  } else {
    quoteGasAdjusted = route.quote.subtract(gasCostQuoteToken);
  }
  return {
    estimatedGasUsedUSD: gasCostUSD,
    estimatedGasUsedQuoteToken: gasCostQuoteToken,
    quoteGasAdjusted
  };
}
function initSwapRouteFromExisting(swapRoute, v1PoolProvider, v2PoolProvider, quoteGasAdjusted, estimatedGasUsed, estimatedGasUsedQuoteToken, estimatedGasUsedUSD) {
  const currencyIn = swapRoute.trade.inputAmount.currency;
  const currencyOut = swapRoute.trade.outputAmount.currency;
  const tradeType = swapRoute.trade.tradeType.valueOf() ? TradeType.EXACT_OUTPUT : TradeType.EXACT_INPUT;
  const routesWithValidQuote = swapRoute.route.map((route) => {
    switch (route.protocol) {
      case "V1" /* V1 */:
        return new V1RouteWithValidQuote2({
          amount: CurrencyAmount$1.fromFractionalAmount(
            route.amount.currency,
            route.amount.numerator,
            route.amount.denominator
          ),
          rawQuote: route.rawQuote,
          percent: route.percent,
          route: route.route,
          gasModel: route.gasModel,
          quoteToken: new Token(
            currencyIn.chainId,
            route.quoteToken.address,
            route.quoteToken.decimals,
            route.quoteToken.symbol,
            route.quoteToken.name
          ),
          tradeType,
          v1PoolProvider
        });
      case "V2" /* V2 */:
        return new V2RouteWithValidQuote2({
          amount: CurrencyAmount$1.fromFractionalAmount(
            route.amount.currency,
            route.amount.numerator,
            route.amount.denominator
          ),
          rawQuote: route.rawQuote,
          percent: route.percent,
          route: route.route,
          gasModel: route.gasModel,
          quoteToken: new Token(
            currencyIn.chainId,
            route.quoteToken.address,
            route.quoteToken.decimals,
            route.quoteToken.symbol,
            route.quoteToken.name
          ),
          tradeType,
          v2PoolProvider
        });
      case "MIXED" /* MIXED */:
        return new MixedRouteWithValidQuote2({
          amount: CurrencyAmount$1.fromFractionalAmount(
            route.amount.currency,
            route.amount.numerator,
            route.amount.denominator
          ),
          rawQuote: route.rawQuote,
          percent: route.percent,
          route: route.route,
          mixedRouteGasModel: route.gasModel,
          v2PoolProvider,
          quoteToken: new Token(
            currencyIn.chainId,
            route.quoteToken.address,
            route.quoteToken.decimals,
            route.quoteToken.symbol,
            route.quoteToken.name
          ),
          tradeType,
          v1PoolProvider
        });
    }
  });
  const trade = buildTrade(
    currencyIn,
    currencyOut,
    tradeType,
    routesWithValidQuote
  );
  return {
    quote: swapRoute.quote,
    quoteGasAdjusted,
    estimatedGasUsed,
    estimatedGasUsedQuoteToken,
    estimatedGasUsedUSD,
    gasPriceWei: swapRoute.gasPriceWei,
    trade,
    route: routesWithValidQuote,
    blockNumber: swapRoute.blockNumber,
    methodParameters: swapRoute.methodParameters ? {
      calldata: swapRoute.methodParameters.calldata,
      value: swapRoute.methodParameters.value,
      to: swapRoute.methodParameters.to
    } : void 0,
    simulationStatus: swapRoute.simulationStatus
  };
}
var BASE_SWAP_COST = 135000n;
var COST_PER_EXTRA_HOP = 50000n;
var V2HeuristicGasModelFactory = class extends IV2GasModelFactory {
  constructor() {
    super();
  }
  async buildGasModel({
    chainId,
    gasPriceWei,
    poolProvider,
    token
  }) {
    if (token.equals(WRAPPED_NATIVE_CURRENCY[chainId])) {
      const usdPool2 = await this.getHighestLiquidityUSDPool(
        chainId,
        poolProvider
      );
      return {
        estimateGasCost: (routeWithValidQuote) => {
          const { gasCostInEth, gasUse } = this.estimateGas(
            routeWithValidQuote,
            gasPriceWei,
            chainId
          );
          const ethToken0 = usdPool2.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address;
          const ethTokenPrice = ethToken0 ? usdPool2.token0Price : usdPool2.token1Price;
          const gasCostInTermsOfUSD = ethTokenPrice.quote(
            gasCostInEth
          );
          return {
            gasEstimate: gasUse,
            gasCostInToken: gasCostInEth,
            gasCostInUSD: gasCostInTermsOfUSD
          };
        }
      };
    }
    const ethPool = await this.getEthPool(
      chainId,
      token,
      poolProvider
    );
    if (!ethPool) {
      log.info(
        "Unable to find ETH pool with the quote token to produce gas adjusted costs. Route will not account for gas."
      );
    }
    const usdPool = await this.getHighestLiquidityUSDPool(
      chainId,
      poolProvider
    );
    return {
      estimateGasCost: (routeWithValidQuote) => {
        const usdToken = usdPool.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address ? usdPool.token1 : usdPool.token0;
        const { gasCostInEth, gasUse } = this.estimateGas(
          routeWithValidQuote,
          gasPriceWei,
          chainId
        );
        if (!ethPool) {
          return {
            gasEstimate: gasUse,
            gasCostInToken: CurrencyAmount.fromRawAmount(token, 0),
            gasCostInUSD: CurrencyAmount.fromRawAmount(usdToken, 0)
          };
        }
        const ethToken0 = ethPool.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address;
        const ethTokenPrice = ethToken0 ? ethPool.token0Price : ethPool.token1Price;
        let gasCostInTermsOfQuoteToken;
        try {
          gasCostInTermsOfQuoteToken = ethTokenPrice.quote(
            gasCostInEth
          );
        } catch (err) {
          log.error(
            {
              ethTokenPriceBase: ethTokenPrice.baseCurrency,
              ethTokenPriceQuote: ethTokenPrice.quoteCurrency,
              gasCostInEth: gasCostInEth.currency
            },
            "Debug eth price token issue"
          );
          throw err;
        }
        const ethToken0USDPool = usdPool.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address;
        const ethTokenPriceUSDPool = ethToken0USDPool ? usdPool.token0Price : usdPool.token1Price;
        let gasCostInTermsOfUSD;
        try {
          gasCostInTermsOfUSD = ethTokenPriceUSDPool.quote(
            gasCostInEth
          );
        } catch (err) {
          log.error(
            {
              usdT1: usdPool.token0.symbol,
              usdT2: usdPool.token1.symbol,
              gasCostInEthToken: gasCostInEth.currency.symbol
            },
            "Failed to compute USD gas price"
          );
          throw err;
        }
        return {
          gasEstimate: gasUse,
          gasCostInToken: gasCostInTermsOfQuoteToken,
          gasCostInUSD: gasCostInTermsOfUSD
        };
      }
    };
  }
  estimateGas(routeWithValidQuote, gasPriceWei, chainId) {
    const hops = routeWithValidQuote.route.pairs.length;
    const gasUse = BASE_SWAP_COST + COST_PER_EXTRA_HOP * (BigInt(hops) - 1n);
    const totalGasCostWei = gasPriceWei * gasUse;
    const weth = WRAPPED_NATIVE_CURRENCY[chainId];
    const gasCostInEth = CurrencyAmount.fromRawAmount(
      weth,
      totalGasCostWei.toString()
    );
    return { gasCostInEth, gasUse };
  }
  async getEthPool(chainId, token, poolProvider) {
    const weth = WRAPPED_NATIVE_CURRENCY[chainId];
    const poolAccessor = await poolProvider.getPools([[weth, token]]);
    const pool = poolAccessor.getPool(weth, token);
    if (!pool || pool.reserve0.equalTo(0) || pool.reserve1.equalTo(0)) {
      log.error(
        {
          weth,
          token,
          reserve0: pool?.reserve0.toExact(),
          reserve1: pool?.reserve1.toExact()
        },
        `Could not find a valid WETH pool with ${token.symbol} for computing gas costs.`
      );
      return null;
    }
    return pool;
  }
  async getHighestLiquidityUSDPool(chainId, poolProvider) {
    const usdTokens = usdGasTokensByChain[chainId];
    if (!usdTokens) {
      throw new Error(
        `Could not find a USD token for computing gas costs on ${chainId}`
      );
    }
    const usdPools = _16.map(usdTokens, (usdToken) => [
      usdToken,
      WRAPPED_NATIVE_CURRENCY[chainId]
    ]);
    const poolAccessor = await poolProvider.getPools(usdPools);
    const poolsRaw = poolAccessor.getAllPools();
    const pools = _16.filter(
      poolsRaw,
      (pool) => pool.reserve0.greaterThan(0) && pool.reserve1.greaterThan(0)
    );
    if (pools.length == 0) {
      log.error(
        { pools },
        `Could not find a USD/WETH pool for computing gas costs.`
      );
      throw new Error(`Can't find USD/WETH pool for computing gas costs.`);
    }
    const maxPool = _16.maxBy(pools, (pool) => {
      if (pool.token0.equals(WRAPPED_NATIVE_CURRENCY[chainId])) {
        return parseFloat(pool.reserve0.toSignificant(2));
      } else {
        return parseFloat(pool.reserve1.toSignificant(2));
      }
    });
    return maxPool;
  }
};

// src/routers/alpha-router/gas-models/mixedRoute/mixed-route-heuristic-gas-model.ts
var MixedRouteHeuristicGasModelFactory = class extends IOnChainGasModelFactory {
  constructor() {
    super();
  }
  async buildGasModel({
    chainId,
    gasPriceWei,
    v1poolProvider: V1poolProvider,
    quoteToken,
    v2poolProvider: V2poolProvider
  }) {
    const usdPool = await getHighestLiquidityV1andV2USDPool(
      chainId,
      V1poolProvider,
      V2poolProvider
    );
    const nativeCurrency = WNATIVE[chainId];
    if (quoteToken.equals(nativeCurrency)) {
      const estimateGasCost2 = (routeWithValidQuote) => {
        const { totalGasCostNativeCurrency, baseGasUse } = this.estimateGas(
          routeWithValidQuote,
          gasPriceWei,
          chainId
        );
        const token0 = usdPool.token0.address == nativeCurrency.address;
        const nativeTokenPrice = token0 ? usdPool.token0Price : usdPool.token1Price;
        const gasCostInTermsOfUSD = nativeTokenPrice.quote(
          totalGasCostNativeCurrency
        );
        return {
          gasEstimate: baseGasUse,
          gasCostInToken: totalGasCostNativeCurrency,
          gasCostInUSD: gasCostInTermsOfUSD
        };
      };
      return {
        estimateGasCost: estimateGasCost2
      };
    }
    const nativeV1Pool = await getV1NativePool(quoteToken, V1poolProvider);
    let nativeV2Pool;
    if (V2poolProvider) {
      nativeV2Pool = await getV2NativePool(quoteToken, V2poolProvider);
    }
    const usdToken = usdPool.token0.address == nativeCurrency.address ? usdPool.token1 : usdPool.token0;
    const estimateGasCost = (routeWithValidQuote) => {
      const { totalGasCostNativeCurrency, baseGasUse } = this.estimateGas(
        routeWithValidQuote,
        gasPriceWei,
        chainId
      );
      if (!nativeV1Pool && !nativeV2Pool) {
        log.info(
          `Unable to find ${nativeCurrency.symbol} pool with the quote token, ${quoteToken.symbol} to produce gas adjusted costs. Route will not account for gas.`
        );
        return {
          gasEstimate: baseGasUse,
          gasCostInToken: CurrencyAmount.fromRawAmount(quoteToken, 0),
          gasCostInUSD: CurrencyAmount.fromRawAmount(usdToken, 0)
        };
      }
      const nativePool = (!nativeV1Pool || nativeV1Pool.reserve0.equalTo(0) || nativeV1Pool.reserve1.equalTo(0)) && nativeV2Pool ? nativeV2Pool : nativeV1Pool;
      const token0 = nativePool.token0.address == nativeCurrency.address;
      const nativeTokenPrice = token0 ? nativePool.token0Price : nativePool.token1Price;
      let gasCostInTermsOfQuoteToken;
      try {
        gasCostInTermsOfQuoteToken = nativeTokenPrice.quote(
          totalGasCostNativeCurrency
        );
      } catch (err) {
        log.info(
          {
            nativeTokenPriceBase: nativeTokenPrice.baseCurrency,
            nativeTokenPriceQuote: nativeTokenPrice.quoteCurrency,
            gasCostInEth: totalGasCostNativeCurrency.currency
          },
          "Debug eth price token issue"
        );
        throw err;
      }
      const token0USDPool = usdPool.token0.address == nativeCurrency.address;
      const nativeTokenPriceUSDPool = token0USDPool ? usdPool.token0Price : usdPool.token1Price;
      let gasCostInTermsOfUSD;
      try {
        gasCostInTermsOfUSD = nativeTokenPriceUSDPool.quote(
          totalGasCostNativeCurrency
        );
      } catch (err) {
        log.info(
          {
            usdT1: usdPool.token0.symbol,
            usdT2: usdPool.token1.symbol,
            gasCostInNativeToken: totalGasCostNativeCurrency.currency.symbol
          },
          "Failed to compute USD gas price"
        );
        throw err;
      }
      return {
        gasEstimate: baseGasUse,
        gasCostInToken: gasCostInTermsOfQuoteToken,
        gasCostInUSD: gasCostInTermsOfUSD
      };
    };
    return {
      estimateGasCost: estimateGasCost.bind(this)
    };
  }
  estimateGas(routeWithValidQuote, gasPriceWei, chainId) {
    let baseGasUse = 0n;
    const route = routeWithValidQuote.route;
    const res = partitionMixedRouteByProtocol(route);
    res.map((section) => {
      if (section.every((pool) => pool instanceof PairV1)) {
        baseGasUse = baseGasUse + BASE_SWAP_COST;
        baseGasUse = baseGasUse + COST_PER_EXTRA_HOP * BigInt(section.length - 1);
      } else if (section.every((pool) => pool instanceof PairV2)) {
        baseGasUse = baseGasUse + BASE_SWAP_COST;
        baseGasUse = baseGasUse + /// same behavior in v2 heuristic gas model factory
        COST_PER_EXTRA_HOP * BigInt(section.length - 1);
      }
    });
    const baseGasCostWei = gasPriceWei * baseGasUse;
    const wrappedCurrency = WNATIVE[chainId];
    const totalGasCostNativeCurrency = CurrencyAmount.fromRawAmount(
      wrappedCurrency,
      baseGasCostWei.toString()
    );
    return {
      totalGasCostNativeCurrency,
      baseGasUse
    };
  }
};
var BASE_SWAP_COST2 = 135000n;
var COST_PER_EXTRA_HOP2 = 50000n;
var V1HeuristicGasModelFactory = class extends IV1GasModelFactory {
  constructor() {
    super();
  }
  async buildGasModel({
    chainId,
    gasPriceWei,
    poolProvider,
    token
  }) {
    if (token.equals(WRAPPED_NATIVE_CURRENCY[chainId])) {
      const usdPool2 = await this.getHighestLiquidityUSDPool(
        chainId,
        poolProvider
      );
      return {
        estimateGasCost: (routeWithValidQuote) => {
          const { gasCostInEth, gasUse } = this.estimateGas(
            routeWithValidQuote,
            gasPriceWei,
            chainId
          );
          const ethToken0 = usdPool2.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address;
          const ethTokenPrice = ethToken0 ? usdPool2.token0Price : usdPool2.token1Price;
          const gasCostInTermsOfUSD = ethTokenPrice.quote(
            gasCostInEth
          );
          return {
            gasEstimate: gasUse,
            gasCostInToken: gasCostInEth,
            gasCostInUSD: gasCostInTermsOfUSD
          };
        }
      };
    }
    const ethPool = await this.getEthPool(
      chainId,
      token,
      poolProvider
    );
    if (!ethPool) {
      log.info(
        "Unable to find ETH pool with the quote token to produce gas adjusted costs. Route will not account for gas."
      );
    }
    const usdPool = await this.getHighestLiquidityUSDPool(
      chainId,
      poolProvider
    );
    return {
      estimateGasCost: (routeWithValidQuote) => {
        const usdToken = usdPool.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address ? usdPool.token1 : usdPool.token0;
        const { gasCostInEth, gasUse } = this.estimateGas(
          routeWithValidQuote,
          gasPriceWei,
          chainId
        );
        if (!ethPool) {
          return {
            gasEstimate: gasUse,
            gasCostInToken: CurrencyAmount.fromRawAmount(token, 0),
            gasCostInUSD: CurrencyAmount.fromRawAmount(usdToken, 0)
          };
        }
        const ethToken0 = ethPool.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address;
        const ethTokenPrice = ethToken0 ? ethPool.token0Price : ethPool.token1Price;
        let gasCostInTermsOfQuoteToken;
        try {
          gasCostInTermsOfQuoteToken = ethTokenPrice.quote(
            gasCostInEth
          );
        } catch (err) {
          log.error(
            {
              ethTokenPriceBase: ethTokenPrice.baseCurrency,
              ethTokenPriceQuote: ethTokenPrice.quoteCurrency,
              gasCostInEth: gasCostInEth.currency
            },
            "Debug eth price token issue"
          );
          throw err;
        }
        const ethToken0USDPool = usdPool.token0.address == WRAPPED_NATIVE_CURRENCY[chainId].address;
        const ethTokenPriceUSDPool = ethToken0USDPool ? usdPool.token0Price : usdPool.token1Price;
        let gasCostInTermsOfUSD;
        try {
          gasCostInTermsOfUSD = ethTokenPriceUSDPool.quote(
            gasCostInEth
          );
        } catch (err) {
          log.error(
            {
              usdT1: usdPool.token0.symbol,
              usdT2: usdPool.token1.symbol,
              gasCostInEthToken: gasCostInEth.currency.symbol
            },
            "Failed to compute USD gas price"
          );
          throw err;
        }
        return {
          gasEstimate: gasUse,
          gasCostInToken: gasCostInTermsOfQuoteToken,
          gasCostInUSD: gasCostInTermsOfUSD
        };
      }
    };
  }
  estimateGas(routeWithValidQuote, gasPriceWei, chainId) {
    const hops = routeWithValidQuote.route.pairs.length;
    const gasUse = BASE_SWAP_COST2 + COST_PER_EXTRA_HOP2 * BigInt(hops - 1);
    const totalGasCostWei = gasPriceWei * gasUse;
    const weth = WRAPPED_NATIVE_CURRENCY[chainId];
    const gasCostInEth = CurrencyAmount.fromRawAmount(
      weth,
      totalGasCostWei.toString()
    );
    return { gasCostInEth, gasUse };
  }
  async getEthPool(chainId, token, poolProvider) {
    const weth = WRAPPED_NATIVE_CURRENCY[chainId];
    const poolAccessor = await poolProvider.getPools([[weth, token]]);
    const pool = poolAccessor.getPool(weth, token);
    if (!pool || pool.reserve0.equalTo(0) || pool.reserve1.equalTo(0)) {
      log.error(
        {
          weth,
          token,
          reserve0: pool?.reserve0.toExact(),
          reserve1: pool?.reserve1.toExact()
        },
        `Could not find a valid WETH pool with ${token.symbol} for computing gas costs.`
      );
      return null;
    }
    return pool;
  }
  async getHighestLiquidityUSDPool(chainId, poolProvider) {
    const usdTokens = usdGasTokensByChain[chainId];
    if (!usdTokens) {
      throw new Error(
        `Could not find a USD token for computing gas costs on ${chainId}`
      );
    }
    const usdPools = _16.map(usdTokens, (usdToken) => [
      usdToken,
      WRAPPED_NATIVE_CURRENCY[chainId]
    ]);
    const poolAccessor = await poolProvider.getPools(usdPools);
    const poolsRaw = poolAccessor.getAllPools();
    const pools = _16.filter(
      poolsRaw,
      (pool) => pool.reserve0.greaterThan(0) && pool.reserve1.greaterThan(0)
    );
    if (pools.length == 0) {
      log.error(
        { pools },
        `Could not find a USD/WETH pool for computing gas costs.`
      );
      throw new Error(`Can't find USD/WETH pool for computing gas costs.`);
    }
    const maxPool = _16.maxBy(pools, (pool) => {
      if (pool.token0.equals(WRAPPED_NATIVE_CURRENCY[chainId])) {
        return parseFloat(pool.reserve0.toSignificant(2));
      } else {
        return parseFloat(pool.reserve1.toSignificant(2));
      }
    });
    return maxPool;
  }
};
var BaseQuoter = class {
  constructor(tokenProvider, chainId, blockedTokenListProvider, tokenValidatorProvider) {
    this.tokenProvider = tokenProvider;
    this.chainId = chainId;
    this.blockedTokenListProvider = blockedTokenListProvider;
    this.tokenValidatorProvider = tokenValidatorProvider;
  }
  /**
   * Public method which would first get the routes and then get the quotes.
   *
   * @param tokenIn The token that the user wants to provide
   * @param tokenOut The token that the user wants to receive
   * @param amounts the list of amounts to query for EACH route.
   * @param percents the percentage of each amount.
   * @param quoteToken
   * @param tradeType
   * @param routingConfig
   * @param gasModel the gasModel to be used for estimating gas cost
   * @param gasPriceWei instead of passing gasModel, gasPriceWei is used to generate a gasModel
   */
  getRoutesThenQuotes(tokenIn, tokenOut, amounts, percents, quoteToken, tradeType, routingConfig, gasModel, gasPriceWei) {
    return this.getRoutes(tokenIn, tokenOut, tradeType, routingConfig).then(
      (routesResult) => this.getQuotes(
        routesResult.routes,
        amounts,
        percents,
        quoteToken,
        tradeType,
        routingConfig,
        routesResult.candidatePools,
        gasModel,
        gasPriceWei
      )
    );
  }
  async applyTokenValidatorToPools(pools, isInvalidFn) {
    if (!this.tokenValidatorProvider) {
      return pools;
    }
    log.info(`Running token validator on ${pools.length} pools`);
    const tokens = _16.flatMap(pools, (pool) => [pool.token0, pool.token1]);
    const tokenValidationResults = await this.tokenValidatorProvider.validateTokens(tokens);
    const poolsFiltered = _16.filter(pools, (pool) => {
      const token0Validation = tokenValidationResults.getValidationByToken(
        pool.token0
      );
      const token1Validation = tokenValidationResults.getValidationByToken(
        pool.token1
      );
      const token0Invalid = isInvalidFn(pool.token0, token0Validation);
      const token1Invalid = isInvalidFn(pool.token1, token1Validation);
      if (token0Invalid || token1Invalid) {
        log.info(
          `Dropping pool ${poolToString(pool)} because token is invalid. ${pool.token0.symbol}: ${token0Validation}, ${pool.token1.symbol}: ${token1Validation}`
        );
      }
      return !token0Invalid && !token1Invalid;
    });
    return poolsFiltered;
  }
};
var V1RouteWithValidQuote2 = class {
  constructor({
    amount,
    rawQuote,
    percent,
    route,
    gasModel,
    quoteToken,
    tradeType,
    v1PoolProvider
  }) {
    this.protocol = "V1" /* V1 */;
    this.amount = amount;
    this.rawQuote = rawQuote;
    this.quote = CurrencyAmount.fromRawAmount(quoteToken, rawQuote.toString());
    this.percent = percent;
    this.route = route;
    this.gasModel = gasModel;
    this.quoteToken = quoteToken;
    this.tradeType = tradeType;
    const { gasEstimate, gasCostInToken, gasCostInUSD } = this.gasModel.estimateGasCost(this);
    this.gasCostInToken = gasCostInToken;
    this.gasCostInUSD = gasCostInUSD;
    this.gasEstimate = gasEstimate;
    if (this.tradeType == TradeType.EXACT_INPUT) {
      const quoteGasAdjusted = this.quote.subtract(gasCostInToken);
      this.quoteAdjustedForGas = quoteGasAdjusted;
    } else {
      const quoteGasAdjusted = this.quote.add(gasCostInToken);
      this.quoteAdjustedForGas = quoteGasAdjusted;
    }
    this.poolAddresses = _16.map(
      route.pairs,
      (p) => v1PoolProvider.getPoolAddress(p.token0, p.token1).poolAddress
    );
    this.tokenPath = this.route.path;
  }
  toString() {
    return `${this.percent.toFixed(
      2
    )}% QuoteGasAdj[${this.quoteAdjustedForGas.toExact()}] Quote[${this.quote.toExact()}] Gas[${this.gasEstimate.toString()}] = ${routeToString(
      this.route
    )}`;
  }
};
var V2RouteWithValidQuote2 = class {
  constructor({
    amount,
    rawQuote,
    percent,
    route,
    gasModel,
    quoteToken,
    tradeType,
    v2PoolProvider
  }) {
    this.protocol = "V2" /* V2 */;
    this.amount = amount;
    this.rawQuote = rawQuote;
    this.quote = CurrencyAmount.fromRawAmount(quoteToken, rawQuote.toString());
    this.percent = percent;
    this.route = route;
    this.gasModel = gasModel;
    this.quoteToken = quoteToken;
    this.tradeType = tradeType;
    const { gasEstimate, gasCostInToken, gasCostInUSD } = this.gasModel.estimateGasCost(this);
    this.gasCostInToken = gasCostInToken;
    this.gasCostInUSD = gasCostInUSD;
    this.gasEstimate = gasEstimate;
    if (this.tradeType == TradeType.EXACT_INPUT) {
      const quoteGasAdjusted = this.quote.subtract(gasCostInToken);
      this.quoteAdjustedForGas = quoteGasAdjusted;
    } else {
      const quoteGasAdjusted = this.quote.add(gasCostInToken);
      this.quoteAdjustedForGas = quoteGasAdjusted;
    }
    this.poolAddresses = _16.map(
      route.pairs,
      (p) => v2PoolProvider.getPoolAddress(p.token0, p.token1).poolAddress
    );
    this.tokenPath = this.route.path;
  }
  toString() {
    return `${this.percent.toFixed(
      2
    )}% QuoteGasAdj[${this.quoteAdjustedForGas.toExact()}] Quote[${this.quote.toExact()}] Gas[${this.gasEstimate.toString()}] = ${routeToString(
      this.route
    )}`;
  }
};
var MixedRouteWithValidQuote2 = class {
  constructor({
    amount,
    rawQuote,
    percent,
    route,
    mixedRouteGasModel,
    quoteToken,
    tradeType,
    v1PoolProvider,
    v2PoolProvider
  }) {
    this.protocol = "MIXED" /* MIXED */;
    this.amount = amount;
    this.rawQuote = rawQuote;
    this.quote = CurrencyAmount.fromRawAmount(quoteToken, rawQuote.toString());
    this.percent = percent;
    this.route = route;
    this.gasModel = mixedRouteGasModel;
    this.quoteToken = quoteToken;
    this.tradeType = tradeType;
    const { gasEstimate, gasCostInToken, gasCostInUSD } = this.gasModel.estimateGasCost(this);
    this.gasCostInToken = gasCostInToken;
    this.gasCostInUSD = gasCostInUSD;
    this.gasEstimate = gasEstimate;
    if (this.tradeType == TradeType.EXACT_INPUT) {
      const quoteGasAdjusted = this.quote.subtract(gasCostInToken);
      this.quoteAdjustedForGas = quoteGasAdjusted;
    } else {
      const quoteGasAdjusted = this.quote.add(gasCostInToken);
      this.quoteAdjustedForGas = quoteGasAdjusted;
    }
    this.poolAddresses = _16.map(route.pools, (p) => {
      return p instanceof PairV1 ? v1PoolProvider.getPoolAddress(p.token0, p.token1).poolAddress : v2PoolProvider.getPoolAddress(p.token0, p.token1).poolAddress;
    });
    this.tokenPath = this.route.path;
  }
  toString() {
    return `${this.percent.toFixed(
      2
    )}% QuoteGasAdj[${this.quoteAdjustedForGas.toExact()}] Quote[${this.quote.toExact()}] Gas[${this.gasEstimate.toString()}] = ${routeToString(
      this.route
    )}`;
  }
};
function computeAllV1Routes(tokenIn, tokenOut, pools, maxHops) {
  const rawRoutes = computeAllRoutes(
    tokenIn,
    tokenOut,
    (route, tokenIn2, tokenOut2) => {
      return new V1Route(route, tokenIn2, tokenOut2);
    },
    pools,
    maxHops
  );
  return rawRoutes;
}
function computeAllV2Routes(tokenIn, tokenOut, pools, maxHops) {
  const rawRoutes = computeAllRoutes(
    tokenIn,
    tokenOut,
    (route, tokenIn2, tokenOut2) => {
      return new V2Route(route, tokenIn2, tokenOut2);
    },
    pools,
    maxHops
  );
  return rawRoutes;
}
function computeAllMixedRoutes(tokenIn, tokenOut, parts, maxHops) {
  const routesRaw = computeAllRoutes(
    tokenIn,
    tokenOut,
    (route, tokenIn2, tokenOut2) => {
      return new MixedRoute2(route, tokenIn2, tokenOut2);
    },
    parts,
    maxHops
  );
  return routesRaw.filter((route) => {
    return !route.pools.every((pool) => pool instanceof PairV1) && !route.pools.every((pool) => pool instanceof PairV2);
  });
}
function computeAllRoutes(tokenIn, tokenOut, buildRoute, pools, maxHops) {
  const poolsUsed = Array(pools.length).fill(false);
  const routes = [];
  const computeRoutes = (tokenIn2, tokenOut2, currentRoute, poolsUsed2, _previousTokenOut) => {
    if (currentRoute.length > maxHops) {
      return;
    }
    if (currentRoute.length > 0 && currentRoute[currentRoute.length - 1].involvesToken(tokenOut2)) {
      routes.push(buildRoute([...currentRoute], tokenIn2, tokenOut2));
      return;
    }
    for (let i = 0; i < pools.length; i++) {
      if (poolsUsed2[i]) {
        continue;
      }
      const curPool = pools[i];
      const previousTokenOut = _previousTokenOut ? _previousTokenOut : tokenIn2;
      if (!curPool.involvesToken(previousTokenOut)) {
        continue;
      }
      const currentTokenOut = curPool.token0.equals(previousTokenOut) ? curPool.token1 : curPool.token0;
      currentRoute.push(curPool);
      poolsUsed2[i] = true;
      computeRoutes(
        tokenIn2,
        tokenOut2,
        currentRoute,
        poolsUsed2,
        currentTokenOut
      );
      poolsUsed2[i] = false;
      currentRoute.pop();
    }
  };
  computeRoutes(tokenIn, tokenOut, [], poolsUsed);
  log.info(
    {
      routes: routes.map(routeToString),
      pools: pools.map(poolToString)
    },
    `Computed ${routes.length} possible routes for type ${routes[0]?.protocol}.`
  );
  return routes;
}
var baseTokensByChain = {
  [ChainId.PULSECHAIN]: [
    eUSDC_PULSECHAIN,
    eUSDT_PULSECHAIN,
    eWBTC_PULSECHAIN,
    eDAI_PULSECHAIN,
    PLSX_PULSECHAIN,
    eHEX_PULSECHAIN,
    HEX_PULSECHAIN,
    INC_PULSECHAIN,
    WETH_PULSECHAIN,
    WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN]
  ],
  [ChainId.PULSECHAIN_TESTNET]: [
    WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN_TESTNET],
    PLSX_TESTNET,
    INC_TESTNET,
    HEX_TESTNET,
    tDAI_TESTNET
  ]
};
async function getV1CandidatePools({
  tokenIn,
  tokenOut,
  routeType,
  routingConfig,
  subgraphProvider,
  tokenProvider,
  poolProvider,
  blockedTokenListProvider,
  chainId
}) {
  const {
    blockNumber,
    v1PoolSelection: {
      topN,
      topNDirectSwaps,
      topNTokenInOut,
      topNSecondHop,
      topNWithEachBaseToken,
      topNWithBaseToken
    }
  } = routingConfig;
  const tokenInAddress = tokenIn.address.toLowerCase();
  const tokenOutAddress = tokenOut.address.toLowerCase();
  const beforeSubgraphPools = Date.now();
  const allPoolsRaw = await subgraphProvider.getPools(tokenIn, tokenOut, {
    blockNumber
  });
  const allPools = _16.map(allPoolsRaw, (pool) => {
    return {
      ...pool,
      token0: {
        ...pool.token0,
        id: pool.token0.id.toLowerCase()
      },
      token1: {
        ...pool.token1,
        id: pool.token1.id.toLowerCase()
      }
    };
  });
  metric.putMetric(
    "V1SubgraphPoolsLoad",
    Date.now() - beforeSubgraphPools,
    "Milliseconds" /* Milliseconds */
  );
  let filteredPools = allPools;
  if (blockedTokenListProvider) {
    filteredPools = [];
    for (const pool of allPools) {
      const token0InBlocklist = await blockedTokenListProvider.getTokenByAddress(pool.token0.id);
      const token1InBlocklist = await blockedTokenListProvider.getTokenByAddress(pool.token1.id);
      if (token0InBlocklist || token1InBlocklist) {
        continue;
      }
      filteredPools.push(pool);
    }
  }
  const subgraphPoolsSorted = _16(filteredPools).sortBy((tokenListPool) => -tokenListPool.reserve).value();
  log.info(
    `After filtering blocked tokens went from ${allPools.length} to ${subgraphPoolsSorted.length}.`
  );
  const poolAddressesSoFar = /* @__PURE__ */ new Set();
  const addToAddressSet = (pools) => {
    _16(pools).map((pool) => pool.id).forEach((poolAddress) => poolAddressesSoFar.add(poolAddress));
  };
  const baseTokens = baseTokensByChain[chainId] ?? [];
  const topByBaseWithTokenIn = _16(baseTokens).flatMap((token) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      const tokenAddress = token.address.toLowerCase();
      return subgraphPool.token0.id == tokenAddress && subgraphPool.token1.id == tokenInAddress || subgraphPool.token1.id == tokenAddress && subgraphPool.token0.id == tokenInAddress;
    }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithEachBaseToken).value();
  }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithBaseToken).value();
  const topByBaseWithTokenOut = _16(baseTokens).flatMap((token) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      const tokenAddress = token.address.toLowerCase();
      return subgraphPool.token0.id == tokenAddress && subgraphPool.token1.id == tokenOutAddress || subgraphPool.token1.id == tokenAddress && subgraphPool.token0.id == tokenOutAddress;
    }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithEachBaseToken).value();
  }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithBaseToken).value();
  let topByDirectSwapPool = [];
  if (topNDirectSwaps != 0) {
    const { token0, token1, poolAddress } = poolProvider.getPoolAddress(
      tokenIn,
      tokenOut
    );
    topByDirectSwapPool = [
      {
        id: poolAddress,
        token0: {
          id: token0.address
        },
        token1: {
          id: token1.address
        },
        supply: 1e4,
        // Not used. Set to arbitrary number.
        reserve: 1e4,
        // Not used. Set to arbitrary number.
        reserveUSD: 1e4
        // Not used. Set to arbitrary number.
      }
    ];
  }
  addToAddressSet(topByDirectSwapPool);
  const wethAddress = WRAPPED_NATIVE_CURRENCY[chainId].address;
  let topByEthQuoteTokenPool = [];
  if (tokenOut.symbol != "WPLS" && tokenOut.symbol != "PLS" && tokenOut.symbol != "tPLS") {
    topByEthQuoteTokenPool = _16(subgraphPoolsSorted).filter((subgraphPool) => {
      if (routeType == TradeType.EXACT_INPUT) {
        return subgraphPool.token0.id == wethAddress && subgraphPool.token1.id == tokenOutAddress || subgraphPool.token1.id == wethAddress && subgraphPool.token0.id == tokenOutAddress;
      } else {
        return subgraphPool.token0.id == wethAddress && subgraphPool.token1.id == tokenInAddress || subgraphPool.token1.id == wethAddress && subgraphPool.token0.id == tokenInAddress;
      }
    }).slice(0, 1).value();
  }
  addToAddressSet(topByEthQuoteTokenPool);
  const topByTVL = _16(subgraphPoolsSorted).filter((subgraphPool) => {
    return !poolAddressesSoFar.has(subgraphPool.id);
  }).slice(0, topN).value();
  addToAddressSet(topByTVL);
  const topByTVLUsingTokenIn = _16(subgraphPoolsSorted).filter((subgraphPool) => {
    return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == tokenInAddress || subgraphPool.token1.id == tokenInAddress);
  }).slice(0, topNTokenInOut).value();
  addToAddressSet(topByTVLUsingTokenIn);
  const topByTVLUsingTokenOut = _16(subgraphPoolsSorted).filter((subgraphPool) => {
    return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == tokenOutAddress || subgraphPool.token1.id == tokenOutAddress);
  }).slice(0, topNTokenInOut).value();
  addToAddressSet(topByTVLUsingTokenOut);
  const topByTVLUsingTokenInSecondHops = _16(topByTVLUsingTokenIn).map((subgraphPool) => {
    return tokenInAddress == subgraphPool.token0.id ? subgraphPool.token1.id : subgraphPool.token0.id;
  }).flatMap((secondHopId) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == secondHopId || subgraphPool.token1.id == secondHopId);
    }).slice(0, topNSecondHop).value();
  }).uniqBy((pool) => pool.id).value();
  addToAddressSet(topByTVLUsingTokenInSecondHops);
  const topByTVLUsingTokenOutSecondHops = _16(topByTVLUsingTokenOut).map((subgraphPool) => {
    return tokenOutAddress == subgraphPool.token0.id ? subgraphPool.token1.id : subgraphPool.token0.id;
  }).flatMap((secondHopId) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == secondHopId || subgraphPool.token1.id == secondHopId);
    }).slice(0, topNSecondHop).value();
  }).uniqBy((pool) => pool.id).value();
  addToAddressSet(topByTVLUsingTokenOutSecondHops);
  const subgraphPools = _16([
    ...topByBaseWithTokenIn,
    ...topByBaseWithTokenOut,
    ...topByDirectSwapPool,
    ...topByEthQuoteTokenPool,
    ...topByTVL,
    ...topByTVLUsingTokenIn,
    ...topByTVLUsingTokenOut,
    ...topByTVLUsingTokenInSecondHops,
    ...topByTVLUsingTokenOutSecondHops
  ]).compact().uniqBy((pool) => pool.id).value();
  const tokenAddresses = _16(subgraphPools).flatMap((subgraphPool) => [subgraphPool.token0.id, subgraphPool.token1.id]).compact().uniq().value();
  log.info(
    `Getting the ${tokenAddresses.length} tokens within the ${subgraphPools.length} V1 pools we are considering`
  );
  const tokenAccessor = await tokenProvider.getTokens(tokenAddresses, {
    blockNumber
  });
  const printV1SubgraphPool = (s) => `${tokenAccessor.getTokenByAddress(s.token0.id)?.symbol ?? s.token0.id}/${tokenAccessor.getTokenByAddress(s.token1.id)?.symbol ?? s.token1.id}`;
  log.info(
    {
      topByBaseWithTokenIn: topByBaseWithTokenIn.map(printV1SubgraphPool),
      topByBaseWithTokenOut: topByBaseWithTokenOut.map(printV1SubgraphPool),
      topByTVL: topByTVL.map(printV1SubgraphPool),
      topByTVLUsingTokenIn: topByTVLUsingTokenIn.map(printV1SubgraphPool),
      topByTVLUsingTokenOut: topByTVLUsingTokenOut.map(printV1SubgraphPool),
      topByTVLUsingTokenInSecondHops: topByTVLUsingTokenInSecondHops.map(printV1SubgraphPool),
      topByTVLUsingTokenOutSecondHops: topByTVLUsingTokenOutSecondHops.map(printV1SubgraphPool),
      top2DirectSwap: topByDirectSwapPool.map(printV1SubgraphPool),
      top2EthQuotePool: topByEthQuoteTokenPool.map(printV1SubgraphPool)
    },
    `V1 Candidate pools`
  );
  const tokenPairsRaw = _16.map(
    subgraphPools,
    (subgraphPool) => {
      const tokenA = tokenAccessor.getTokenByAddress(subgraphPool.token0.id);
      const tokenB = tokenAccessor.getTokenByAddress(subgraphPool.token1.id);
      if (!tokenA || !tokenB) {
        log.info(
          `Dropping candidate pool for ${subgraphPool.token0.id}/${subgraphPool.token1.id}`
        );
        return void 0;
      }
      return [tokenA, tokenB];
    }
  );
  const tokenPairs = _16.compact(tokenPairsRaw);
  const beforePoolsLoad = Date.now();
  const poolAccessor = await poolProvider.getPools(tokenPairs, { blockNumber });
  metric.putMetric(
    "V1PoolsLoad",
    Date.now() - beforePoolsLoad,
    "Milliseconds" /* Milliseconds */
  );
  const poolsBySelection = {
    protocol: "V1" /* V1 */,
    selections: {
      topByBaseWithTokenIn,
      topByBaseWithTokenOut,
      topByDirectSwapPool,
      topByEthQuoteTokenPool,
      topByTVL,
      topByTVLUsingTokenIn,
      topByTVLUsingTokenOut,
      topByTVLUsingTokenInSecondHops,
      topByTVLUsingTokenOutSecondHops
    }
  };
  return { poolAccessor, candidatePools: poolsBySelection, subgraphPools };
}
async function getV2CandidatePools({
  tokenIn,
  tokenOut,
  routeType,
  routingConfig,
  subgraphProvider,
  tokenProvider,
  poolProvider,
  blockedTokenListProvider,
  chainId
}) {
  const {
    blockNumber,
    v2PoolSelection: {
      topN,
      topNDirectSwaps,
      topNTokenInOut,
      topNSecondHop,
      topNWithEachBaseToken,
      topNWithBaseToken
    }
  } = routingConfig;
  const tokenInAddress = tokenIn.address.toLowerCase();
  const tokenOutAddress = tokenOut.address.toLowerCase();
  const beforeSubgraphPools = Date.now();
  const allPoolsRaw = await subgraphProvider.getPools(tokenIn, tokenOut, {
    blockNumber
  });
  const allPools = _16.map(allPoolsRaw, (pool) => {
    return {
      ...pool,
      token0: {
        ...pool.token0,
        id: pool.token0.id.toLowerCase()
      },
      token1: {
        ...pool.token1,
        id: pool.token1.id.toLowerCase()
      }
    };
  });
  metric.putMetric(
    "V2SubgraphPoolsLoad",
    Date.now() - beforeSubgraphPools,
    "Milliseconds" /* Milliseconds */
  );
  let filteredPools = allPools;
  if (blockedTokenListProvider) {
    filteredPools = [];
    for (const pool of allPools) {
      const token0InBlocklist = await blockedTokenListProvider.getTokenByAddress(pool.token0.id);
      const token1InBlocklist = await blockedTokenListProvider.getTokenByAddress(pool.token1.id);
      if (token0InBlocklist || token1InBlocklist) {
        continue;
      }
      filteredPools.push(pool);
    }
  }
  const subgraphPoolsSorted = _16(filteredPools).sortBy((tokenListPool) => -tokenListPool.reserve).value();
  log.info(
    `After filtering blocked tokens went from ${allPools.length} to ${subgraphPoolsSorted.length}.`
  );
  const poolAddressesSoFar = /* @__PURE__ */ new Set();
  const addToAddressSet = (pools) => {
    _16(pools).map((pool) => pool.id).forEach((poolAddress) => poolAddressesSoFar.add(poolAddress));
  };
  const baseTokens = baseTokensByChain[chainId] ?? [];
  const topByBaseWithTokenIn = _16(baseTokens).flatMap((token) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      const tokenAddress = token.address.toLowerCase();
      return subgraphPool.token0.id == tokenAddress && subgraphPool.token1.id == tokenInAddress || subgraphPool.token1.id == tokenAddress && subgraphPool.token0.id == tokenInAddress;
    }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithEachBaseToken).value();
  }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithBaseToken).value();
  const topByBaseWithTokenOut = _16(baseTokens).flatMap((token) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      const tokenAddress = token.address.toLowerCase();
      return subgraphPool.token0.id == tokenAddress && subgraphPool.token1.id == tokenOutAddress || subgraphPool.token1.id == tokenAddress && subgraphPool.token0.id == tokenOutAddress;
    }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithEachBaseToken).value();
  }).sortBy((tokenListPool) => -tokenListPool.reserve).slice(0, topNWithBaseToken).value();
  let topByDirectSwapPool = [];
  if (topNDirectSwaps != 0) {
    const { token0, token1, poolAddress } = poolProvider.getPoolAddress(
      tokenIn,
      tokenOut
    );
    topByDirectSwapPool = [
      {
        id: poolAddress,
        token0: {
          id: token0.address
        },
        token1: {
          id: token1.address
        },
        supply: 1e4,
        // Not used. Set to arbitrary number.
        reserve: 1e4,
        // Not used. Set to arbitrary number.
        reserveUSD: 1e4
        // Not used. Set to arbitrary number.
      }
    ];
  }
  addToAddressSet(topByDirectSwapPool);
  const wethAddress = WRAPPED_NATIVE_CURRENCY[chainId].address;
  let topByEthQuoteTokenPool = [];
  if (tokenOut.symbol != "WPLS" && tokenOut.symbol != "PLS" && tokenOut.symbol != "tPLS") {
    topByEthQuoteTokenPool = _16(subgraphPoolsSorted).filter((subgraphPool) => {
      if (routeType == TradeType.EXACT_INPUT) {
        return subgraphPool.token0.id == wethAddress && subgraphPool.token1.id == tokenOutAddress || subgraphPool.token1.id == wethAddress && subgraphPool.token0.id == tokenOutAddress;
      } else {
        return subgraphPool.token0.id == wethAddress && subgraphPool.token1.id == tokenInAddress || subgraphPool.token1.id == wethAddress && subgraphPool.token0.id == tokenInAddress;
      }
    }).slice(0, 1).value();
  }
  addToAddressSet(topByEthQuoteTokenPool);
  const topByTVL = _16(subgraphPoolsSorted).filter((subgraphPool) => {
    return !poolAddressesSoFar.has(subgraphPool.id);
  }).slice(0, topN).value();
  addToAddressSet(topByTVL);
  const topByTVLUsingTokenIn = _16(subgraphPoolsSorted).filter((subgraphPool) => {
    return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == tokenInAddress || subgraphPool.token1.id == tokenInAddress);
  }).slice(0, topNTokenInOut).value();
  addToAddressSet(topByTVLUsingTokenIn);
  const topByTVLUsingTokenOut = _16(subgraphPoolsSorted).filter((subgraphPool) => {
    return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == tokenOutAddress || subgraphPool.token1.id == tokenOutAddress);
  }).slice(0, topNTokenInOut).value();
  addToAddressSet(topByTVLUsingTokenOut);
  const topByTVLUsingTokenInSecondHops = _16(topByTVLUsingTokenIn).map((subgraphPool) => {
    return tokenInAddress == subgraphPool.token0.id ? subgraphPool.token1.id : subgraphPool.token0.id;
  }).flatMap((secondHopId) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == secondHopId || subgraphPool.token1.id == secondHopId);
    }).slice(0, topNSecondHop).value();
  }).uniqBy((pool) => pool.id).value();
  addToAddressSet(topByTVLUsingTokenInSecondHops);
  const topByTVLUsingTokenOutSecondHops = _16(topByTVLUsingTokenOut).map((subgraphPool) => {
    return tokenOutAddress == subgraphPool.token0.id ? subgraphPool.token1.id : subgraphPool.token0.id;
  }).flatMap((secondHopId) => {
    return _16(subgraphPoolsSorted).filter((subgraphPool) => {
      return !poolAddressesSoFar.has(subgraphPool.id) && (subgraphPool.token0.id == secondHopId || subgraphPool.token1.id == secondHopId);
    }).slice(0, topNSecondHop).value();
  }).uniqBy((pool) => pool.id).value();
  addToAddressSet(topByTVLUsingTokenOutSecondHops);
  const subgraphPools = _16([
    ...topByBaseWithTokenIn,
    ...topByBaseWithTokenOut,
    ...topByDirectSwapPool,
    ...topByEthQuoteTokenPool,
    ...topByTVL,
    ...topByTVLUsingTokenIn,
    ...topByTVLUsingTokenOut,
    ...topByTVLUsingTokenInSecondHops,
    ...topByTVLUsingTokenOutSecondHops
  ]).compact().uniqBy((pool) => pool.id).value();
  const tokenAddresses = _16(subgraphPools).flatMap((subgraphPool) => [subgraphPool.token0.id, subgraphPool.token1.id]).compact().uniq().value();
  log.info(
    `Getting the ${tokenAddresses.length} tokens within the ${subgraphPools.length} V2 pools we are considering`
  );
  const tokenAccessor = await tokenProvider.getTokens(tokenAddresses, {
    blockNumber
  });
  const printV2SubgraphPool = (s) => `${tokenAccessor.getTokenByAddress(s.token0.id)?.symbol ?? s.token0.id}/${tokenAccessor.getTokenByAddress(s.token1.id)?.symbol ?? s.token1.id}`;
  log.info(
    {
      topByBaseWithTokenIn: topByBaseWithTokenIn.map(printV2SubgraphPool),
      topByBaseWithTokenOut: topByBaseWithTokenOut.map(printV2SubgraphPool),
      topByTVL: topByTVL.map(printV2SubgraphPool),
      topByTVLUsingTokenIn: topByTVLUsingTokenIn.map(printV2SubgraphPool),
      topByTVLUsingTokenOut: topByTVLUsingTokenOut.map(printV2SubgraphPool),
      topByTVLUsingTokenInSecondHops: topByTVLUsingTokenInSecondHops.map(printV2SubgraphPool),
      topByTVLUsingTokenOutSecondHops: topByTVLUsingTokenOutSecondHops.map(printV2SubgraphPool),
      top2DirectSwap: topByDirectSwapPool.map(printV2SubgraphPool),
      top2EthQuotePool: topByEthQuoteTokenPool.map(printV2SubgraphPool)
    },
    `V2 Candidate pools`
  );
  const tokenPairsRaw = _16.map(
    subgraphPools,
    (subgraphPool) => {
      const tokenA = tokenAccessor.getTokenByAddress(subgraphPool.token0.id);
      const tokenB = tokenAccessor.getTokenByAddress(subgraphPool.token1.id);
      if (!tokenA || !tokenB) {
        log.info(
          `Dropping candidate pool for ${subgraphPool.token0.id}/${subgraphPool.token1.id}`
        );
        return void 0;
      }
      return [tokenA, tokenB];
    }
  );
  const tokenPairs = _16.compact(tokenPairsRaw);
  const beforePoolsLoad = Date.now();
  const poolAccessor = await poolProvider.getPools(tokenPairs, { blockNumber });
  metric.putMetric(
    "V2PoolsLoad",
    Date.now() - beforePoolsLoad,
    "Milliseconds" /* Milliseconds */
  );
  const poolsBySelection = {
    protocol: "V2" /* V2 */,
    selections: {
      topByBaseWithTokenIn,
      topByBaseWithTokenOut,
      topByDirectSwapPool,
      topByEthQuoteTokenPool,
      topByTVL,
      topByTVLUsingTokenIn,
      topByTVLUsingTokenOut,
      topByTVLUsingTokenInSecondHops,
      topByTVLUsingTokenOutSecondHops
    }
  };
  return { poolAccessor, candidatePools: poolsBySelection, subgraphPools };
}
async function getMixedRouteCandidatePools({
  tokenIn,
  tokenOut,
  routeType,
  routingConfig,
  v1subgraphProvider,
  v2subgraphProvider,
  tokenProvider,
  v1poolProvider,
  v2poolProvider,
  blockedTokenListProvider,
  chainId
}) {
  const { blockNumber } = routingConfig;
  const { subgraphPools: V1subgraphPools, candidatePools: V1candidatePools } = await getV1CandidatePools({
    tokenIn,
    tokenOut,
    tokenProvider,
    blockedTokenListProvider,
    poolProvider: v1poolProvider,
    routeType,
    subgraphProvider: v1subgraphProvider,
    routingConfig,
    chainId
  });
  const { subgraphPools: V2subgraphPools, candidatePools: V2candidatePools } = await getV2CandidatePools({
    tokenIn,
    tokenOut,
    tokenProvider,
    blockedTokenListProvider,
    poolProvider: v2poolProvider,
    routeType,
    subgraphProvider: v2subgraphProvider,
    routingConfig,
    chainId
  });
  const V2topByTVLPoolIds = new Set(
    [
      ...V2candidatePools.selections.topByTVLUsingTokenIn,
      ...V2candidatePools.selections.topByBaseWithTokenIn,
      /// tokenOut:
      ...V2candidatePools.selections.topByTVLUsingTokenOut,
      ...V2candidatePools.selections.topByBaseWithTokenOut,
      /// Direct swap:
      ...V2candidatePools.selections.topByDirectSwapPool
    ].map((poolId) => poolId.id)
  );
  const V2topByTVLSortedPools = _16(V2subgraphPools).filter((pool) => V2topByTVLPoolIds.has(pool.id)).sortBy((pool) => -pool.reserveUSD).value();
  const V1sortedPools = _16(V1subgraphPools).sortBy((pool) => -pool.reserveUSD).value();
  const buildV2Pools = [];
  V2topByTVLSortedPools.forEach((V2subgraphPool) => {
    const V1subgraphPool = V1sortedPools.find(
      (pool) => pool.token0.id == V2subgraphPool.token0.id && pool.token1.id == V2subgraphPool.token1.id || pool.token0.id == V2subgraphPool.token1.id && pool.token1.id == V2subgraphPool.token0.id
    );
    if (V1subgraphPool) {
      if (V2subgraphPool.reserveUSD > V1subgraphPool.reserveUSD) {
        log.info(
          {
            token0: V2subgraphPool.token0.id,
            token1: V2subgraphPool.token1.id,
            v2reserveUSD: V2subgraphPool.reserveUSD,
            v1reserveUSD: V1subgraphPool.reserveUSD
          },
          `MixedRoute heuristic, found a V2 pool with higher liquidity than its V1 counterpart`
        );
        buildV2Pools.push(V2subgraphPool);
      }
    } else {
      log.info(
        {
          token0: V2subgraphPool.token0.id,
          token1: V2subgraphPool.token1.id,
          v2reserveUSD: V2subgraphPool.reserveUSD
        },
        `MixedRoute heuristic, found a V2 pool with no V1 counterpart`
      );
      buildV2Pools.push(V2subgraphPool);
    }
  });
  log.info(
    buildV2Pools.length,
    `Number of V2 candidate pools that fit first heuristic`
  );
  const subgraphPools = [...buildV2Pools, ...V1sortedPools];
  const tokenAddresses = _16(subgraphPools).flatMap((subgraphPool) => [subgraphPool.token0.id, subgraphPool.token1.id]).compact().uniq().value();
  log.info(
    `Getting the ${tokenAddresses.length} tokens within the ${subgraphPools.length} pools we are considering`
  );
  const tokenAccessor = await tokenProvider.getTokens(tokenAddresses, {
    blockNumber
  });
  const V1tokenPairsRaw = _16.map(
    V1sortedPools,
    (subgraphPool) => {
      const tokenA = tokenAccessor.getTokenByAddress(subgraphPool.token0.id);
      const tokenB = tokenAccessor.getTokenByAddress(subgraphPool.token1.id);
      if (!tokenA || !tokenB) {
        log.info(
          `Dropping candidate pool for ${subgraphPool.token0.id}/${subgraphPool.token1.id}`
        );
        return void 0;
      }
      return [tokenA, tokenB];
    }
  );
  const V1tokenPairs = _16.compact(V1tokenPairsRaw);
  const V2tokenPairsRaw = _16.map(
    buildV2Pools,
    (subgraphPool) => {
      const tokenA = tokenAccessor.getTokenByAddress(subgraphPool.token0.id);
      const tokenB = tokenAccessor.getTokenByAddress(subgraphPool.token1.id);
      if (!tokenA || !tokenB) {
        log.info(
          `Dropping candidate pool for ${subgraphPool.token0.id}/${subgraphPool.token1.id}`
        );
        return void 0;
      }
      return [tokenA, tokenB];
    }
  );
  const V2tokenPairs = _16.compact(V2tokenPairsRaw);
  const [V1poolAccessor, V2poolAccessor] = await Promise.all([
    v1poolProvider.getPools(V1tokenPairs, {
      blockNumber
    }),
    v2poolProvider.getPools(V2tokenPairs, {
      blockNumber
    })
  ]);
  const buildPoolsBySelection = (key) => {
    return [
      ...buildV2Pools.filter(
        (pool) => V2candidatePools.selections[key].map((p) => p.id).includes(pool.id)
      ),
      ...V1candidatePools.selections[key]
    ];
  };
  const poolsBySelection = {
    protocol: "MIXED" /* MIXED */,
    selections: {
      topByBaseWithTokenIn: buildPoolsBySelection("topByBaseWithTokenIn"),
      topByBaseWithTokenOut: buildPoolsBySelection("topByBaseWithTokenOut"),
      topByDirectSwapPool: buildPoolsBySelection("topByDirectSwapPool"),
      topByEthQuoteTokenPool: buildPoolsBySelection("topByEthQuoteTokenPool"),
      topByTVL: buildPoolsBySelection("topByTVL"),
      topByTVLUsingTokenIn: buildPoolsBySelection("topByTVLUsingTokenIn"),
      topByTVLUsingTokenOut: buildPoolsBySelection("topByTVLUsingTokenOut"),
      topByTVLUsingTokenInSecondHops: buildPoolsBySelection(
        "topByTVLUsingTokenInSecondHops"
      ),
      topByTVLUsingTokenOutSecondHops: buildPoolsBySelection(
        "topByTVLUsingTokenOutSecondHops"
      )
    }
  };
  return {
    V1poolAccessor,
    V2poolAccessor,
    candidatePools: poolsBySelection,
    subgraphPools
  };
}

// src/routers/alpha-router/quoters/v1-quoter.ts
var V1Quoter = class extends BaseQuoter {
  constructor(v1SubgraphProvider, v1PoolProvider, v1QuoteProvider, v1GasModelFactory, tokenProvider, chainId, blockedTokenListProvider, tokenValidatorProvider) {
    super(tokenProvider, chainId, blockedTokenListProvider, tokenValidatorProvider);
    this.v1SubgraphProvider = v1SubgraphProvider;
    this.v1PoolProvider = v1PoolProvider;
    this.v1QuoteProvider = v1QuoteProvider;
    this.v1GasModelFactory = v1GasModelFactory;
  }
  async getRoutes(tokenIn, tokenOut, tradeType, routingConfig) {
    const { poolAccessor, candidatePools } = await getV1CandidatePools({
      tokenIn,
      tokenOut,
      tokenProvider: this.tokenProvider,
      blockedTokenListProvider: this.blockedTokenListProvider,
      poolProvider: this.v1PoolProvider,
      routeType: tradeType,
      subgraphProvider: this.v1SubgraphProvider,
      routingConfig,
      chainId: this.chainId
    });
    const poolsRaw = poolAccessor.getAllPools();
    const pools = await this.applyTokenValidatorToPools(
      poolsRaw,
      (token, tokenValidation) => {
        if (!tokenValidation) {
          return false;
        }
        if (tokenValidation == 2 /* STF */ && (token.equals(tokenIn) || token.equals(tokenOut))) {
          return false;
        }
        return tokenValidation == 2 /* STF */;
      }
    );
    const { maxSwapsPerPath } = routingConfig;
    const routes = computeAllV1Routes(
      tokenIn,
      tokenOut,
      pools,
      maxSwapsPerPath
    );
    return {
      routes,
      candidatePools
    };
  }
  async getQuotes(routes, amounts, percents, quoteToken, tradeType, _routingConfig, candidatePools, _gasModel, gasPriceWei) {
    log.info("Starting to get V1 quotes");
    if (gasPriceWei === void 0) {
      throw new Error("GasPriceWei for V1Routes is required to getQuotes");
    }
    if (routes.length == 0) {
      return { routesWithValidQuotes: [], candidatePools };
    }
    const quoteFn = tradeType == TradeType.EXACT_INPUT ? this.v1QuoteProvider.getQuotesManyExactIn.bind(this.v1QuoteProvider) : this.v1QuoteProvider.getQuotesManyExactOut.bind(this.v1QuoteProvider);
    const beforeQuotes = Date.now();
    log.info(
      `Getting quotes for V1 for ${routes.length} routes with ${amounts.length} amounts per route.`
    );
    const { routesWithQuotes } = await quoteFn(amounts, routes);
    const v1GasModel = await this.v1GasModelFactory.buildGasModel({
      chainId: this.chainId,
      gasPriceWei,
      poolProvider: this.v1PoolProvider,
      token: quoteToken
    });
    metric.putMetric(
      "V1QuotesLoad",
      Date.now() - beforeQuotes,
      "Milliseconds" /* Milliseconds */
    );
    metric.putMetric(
      "V1QuotesFetched",
      _16(routesWithQuotes).map(([, quotes]) => quotes.length).sum(),
      "Count" /* Count */
    );
    const routesWithValidQuotes = [];
    for (const routeWithQuote of routesWithQuotes) {
      const [route, quotes] = routeWithQuote;
      for (let i = 0; i < quotes.length; i++) {
        const percent = percents[i];
        const amountQuote = quotes[i];
        const { quote, amount } = amountQuote;
        if (!quote) {
          log.debug(
            {
              route: routeToString(route),
              amountQuote
            },
            "Dropping a null V1 quote for route."
          );
          continue;
        }
        const routeWithValidQuote = new V1RouteWithValidQuote2({
          route,
          rawQuote: quote,
          amount,
          percent,
          gasModel: v1GasModel,
          quoteToken,
          tradeType,
          v1PoolProvider: this.v1PoolProvider
        });
        routesWithValidQuotes.push(routeWithValidQuote);
      }
    }
    return {
      routesWithValidQuotes,
      candidatePools
    };
  }
};
var V2Quoter = class extends BaseQuoter {
  constructor(v2SubgraphProvider, v2PoolProvider, v2QuoteProvider, v2GasModelFactory, tokenProvider, chainId, blockedTokenListProvider, tokenValidatorProvider) {
    super(tokenProvider, chainId, blockedTokenListProvider, tokenValidatorProvider);
    this.v2SubgraphProvider = v2SubgraphProvider;
    this.v2PoolProvider = v2PoolProvider;
    this.v2QuoteProvider = v2QuoteProvider;
    this.v2GasModelFactory = v2GasModelFactory;
  }
  async getRoutes(tokenIn, tokenOut, tradeType, routingConfig) {
    const { poolAccessor, candidatePools } = await getV2CandidatePools({
      tokenIn,
      tokenOut,
      tokenProvider: this.tokenProvider,
      blockedTokenListProvider: this.blockedTokenListProvider,
      poolProvider: this.v2PoolProvider,
      routeType: tradeType,
      subgraphProvider: this.v2SubgraphProvider,
      routingConfig,
      chainId: this.chainId
    });
    const poolsRaw = poolAccessor.getAllPools();
    const pools = await this.applyTokenValidatorToPools(
      poolsRaw,
      (token, tokenValidation) => {
        if (!tokenValidation) {
          return false;
        }
        if (tokenValidation == 2 /* STF */ && (token.equals(tokenIn) || token.equals(tokenOut))) {
          return false;
        }
        return tokenValidation == 2 /* STF */;
      }
    );
    const { maxSwapsPerPath } = routingConfig;
    const routes = computeAllV2Routes(
      tokenIn,
      tokenOut,
      pools,
      maxSwapsPerPath
    );
    return {
      routes,
      candidatePools
    };
  }
  async getQuotes(routes, amounts, percents, quoteToken, tradeType, _routingConfig, candidatePools, _gasModel, gasPriceWei) {
    log.info("Starting to get V2 quotes");
    if (gasPriceWei === void 0) {
      throw new Error("GasPriceWei for V2Routes is required to getQuotes");
    }
    if (routes.length == 0) {
      return { routesWithValidQuotes: [], candidatePools };
    }
    const quoteFn = tradeType == TradeType.EXACT_INPUT ? this.v2QuoteProvider.getQuotesManyExactIn.bind(this.v2QuoteProvider) : this.v2QuoteProvider.getQuotesManyExactOut.bind(this.v2QuoteProvider);
    const beforeQuotes = Date.now();
    log.info(
      `Getting quotes for V2 for ${routes.length} routes with ${amounts.length} amounts per route.`
    );
    const { routesWithQuotes } = await quoteFn(amounts, routes);
    const v2GasModel = await this.v2GasModelFactory.buildGasModel({
      chainId: this.chainId,
      gasPriceWei,
      poolProvider: this.v2PoolProvider,
      token: quoteToken
    });
    metric.putMetric(
      "V2QuotesLoad",
      Date.now() - beforeQuotes,
      "Milliseconds" /* Milliseconds */
    );
    metric.putMetric(
      "V2QuotesFetched",
      _16(routesWithQuotes).map(([, quotes]) => quotes.length).sum(),
      "Count" /* Count */
    );
    const routesWithValidQuotes = [];
    for (const routeWithQuote of routesWithQuotes) {
      const [route, quotes] = routeWithQuote;
      for (let i = 0; i < quotes.length; i++) {
        const percent = percents[i];
        const amountQuote = quotes[i];
        const { quote, amount } = amountQuote;
        if (!quote) {
          log.debug(
            {
              route: routeToString(route),
              amountQuote
            },
            "Dropping a null V2 quote for route."
          );
          continue;
        }
        const routeWithValidQuote = new V2RouteWithValidQuote2({
          route,
          rawQuote: quote,
          amount,
          percent,
          gasModel: v2GasModel,
          quoteToken,
          tradeType,
          v2PoolProvider: this.v2PoolProvider
        });
        routesWithValidQuotes.push(routeWithValidQuote);
      }
    }
    return {
      routesWithValidQuotes,
      candidatePools
    };
  }
};
var MixedQuoter = class extends BaseQuoter {
  constructor(v1SubgraphProvider, v1PoolProvider, v2SubgraphProvider, v2PoolProvider, onChainQuoteProvider, tokenProvider, chainId, blockedTokenListProvider, tokenValidatorProvider) {
    super(tokenProvider, chainId, blockedTokenListProvider, tokenValidatorProvider);
    this.v1SubgraphProvider = v1SubgraphProvider;
    this.v1PoolProvider = v1PoolProvider;
    this.v2SubgraphProvider = v2SubgraphProvider;
    this.v2PoolProvider = v2PoolProvider;
    this.onChainQuoteProvider = onChainQuoteProvider;
  }
  async getRoutes(tokenIn, tokenOut, tradeType, routingConfig) {
    if (tradeType != TradeType.EXACT_INPUT) {
      throw new Error("Mixed route quotes are not supported for EXACT_OUTPUT");
    }
    const {
      V1poolAccessor,
      V2poolAccessor,
      candidatePools: mixedRouteCandidatePools
    } = await getMixedRouteCandidatePools({
      tokenIn,
      tokenOut,
      tokenProvider: this.tokenProvider,
      blockedTokenListProvider: this.blockedTokenListProvider,
      v1poolProvider: this.v1PoolProvider,
      v2poolProvider: this.v2PoolProvider,
      routeType: tradeType,
      v1subgraphProvider: this.v1SubgraphProvider,
      v2subgraphProvider: this.v2SubgraphProvider,
      routingConfig,
      chainId: this.chainId
    });
    const V1poolsRaw = V1poolAccessor.getAllPools();
    const V2poolsRaw = V2poolAccessor.getAllPools();
    const poolsRaw = [...V1poolsRaw, ...V2poolsRaw];
    const candidatePools = mixedRouteCandidatePools;
    const pools = await this.applyTokenValidatorToPools(
      poolsRaw,
      (token, tokenValidation) => {
        if (!tokenValidation) {
          return false;
        }
        if (tokenValidation == 2 /* STF */ && (token.equals(tokenIn) || token.equals(tokenOut))) {
          return false;
        }
        return tokenValidation == 1 /* FOT */ || tokenValidation == 2 /* STF */;
      }
    );
    const { maxSwapsPerPath } = routingConfig;
    const routes = computeAllMixedRoutes(
      tokenIn,
      tokenOut,
      pools,
      maxSwapsPerPath
    );
    return {
      routes,
      candidatePools
    };
  }
  async getQuotes(routes, amounts, percents, quoteToken, tradeType, routingConfig, candidatePools, gasModel) {
    log.info("Starting to get mixed quotes");
    if (gasModel === void 0) {
      throw new Error("GasModel for MixedRouteWithValidQuote is required to getQuotes");
    }
    if (routes.length == 0) {
      return { routesWithValidQuotes: [], candidatePools };
    }
    const quoteFn = this.onChainQuoteProvider.getQuotesManyExactIn.bind(
      this.onChainQuoteProvider
    );
    const beforeQuotes = Date.now();
    log.info(
      `Getting quotes for mixed for ${routes.length} routes with ${amounts.length} amounts per route.`
    );
    const { routesWithQuotes } = await quoteFn(amounts, routes, {
      blockNumber: routingConfig.blockNumber
    });
    metric.putMetric(
      "MixedQuotesLoad",
      Date.now() - beforeQuotes,
      "Milliseconds" /* Milliseconds */
    );
    metric.putMetric(
      "MixedQuotesFetched",
      _16(routesWithQuotes).map(([, quotes]) => quotes.length).sum(),
      "Count" /* Count */
    );
    const routesWithValidQuotes = [];
    for (const routeWithQuote of routesWithQuotes) {
      const [route, quotes] = routeWithQuote;
      for (let i = 0; i < quotes.length; i++) {
        const percent = percents[i];
        const amountQuote = quotes[i];
        const {
          quote,
          amount
        } = amountQuote;
        if (!quote) {
          log.debug(
            {
              route: routeToString(route),
              amountQuote
            },
            "Dropping a null mixed quote for route."
          );
          continue;
        }
        const routeWithValidQuote = new MixedRouteWithValidQuote2({
          route,
          rawQuote: quote,
          amount,
          percent,
          mixedRouteGasModel: gasModel,
          quoteToken,
          tradeType,
          v1PoolProvider: this.v1PoolProvider,
          v2PoolProvider: this.v2PoolProvider
        });
        routesWithValidQuotes.push(routeWithValidQuote);
      }
    }
    return {
      routesWithValidQuotes,
      candidatePools
    };
  }
};

// src/routers/alpha-router/alpha-router.ts
var MapWithLowerCaseKey = class extends Map {
  set(key, value) {
    return super.set(key.toLowerCase(), value);
  }
};
var AlphaRouter = class {
  constructor({
    chainId,
    provider,
    multicall2Provider,
    v1PoolProvider,
    v1QuoteProvider,
    v1SubgraphProvider,
    v2PoolProvider,
    v2QuoteProvider,
    v2SubgraphProvider,
    onChainQuoteProvider,
    tokenProvider,
    blockedTokenListProvider,
    gasPriceProvider,
    v1GasModelFactory,
    v2GasModelFactory,
    mixedRouteGasModelFactory,
    swapRouterProvider,
    tokenValidatorProvider,
    simulator,
    routeCachingProvider
  }) {
    this.chainId = chainId;
    this.provider = provider;
    this.multicall2Provider = multicall2Provider ?? new UniswapMulticallProvider(chainId, provider, 375e3);
    this.v1PoolProvider = v1PoolProvider ?? new CachingV1PoolProvider(
      this.chainId,
      new V1PoolProvider(ID_TO_CHAIN_ID(chainId), this.multicall2Provider),
      new NodeJSCache(new NodeCache({ stdTTL: 360, useClones: false }))
    );
    this.v1QuoteProvider = v1QuoteProvider ?? new V1QuoteProvider();
    this.simulator = simulator;
    this.routeCachingProvider = routeCachingProvider;
    if (onChainQuoteProvider) {
      this.onChainQuoteProvider = onChainQuoteProvider;
    } else {
      switch (chainId) {
        default:
          this.onChainQuoteProvider = new OnChainQuoteProvider(
            chainId,
            provider,
            this.multicall2Provider,
            {
              retries: 2,
              minTimeout: 100,
              maxTimeout: 1e3
            },
            {
              multicallChunk: 210,
              gasLimitPerCall: 705e3,
              quoteMinSuccessRate: 0.15
            },
            {
              gasLimitOverride: 2e6,
              multicallChunk: 70
            }
          );
          break;
      }
    }
    this.v2PoolProvider = v2PoolProvider ?? new CachingV2PoolProvider(
      chainId,
      new V2PoolProvider(chainId, this.multicall2Provider),
      new NodeJSCache(new NodeCache({ stdTTL: 60, useClones: false }))
    );
    this.v2QuoteProvider = v2QuoteProvider ?? new V2QuoteProvider();
    this.blockedTokenListProvider = blockedTokenListProvider ?? new CachingTokenListProvider(
      chainId,
      UNSUPPORTED_TOKENS,
      new NodeJSCache(new NodeCache({ stdTTL: 3600, useClones: false }))
    );
    this.tokenProvider = tokenProvider ?? new CachingTokenProviderWithFallback(
      chainId,
      new NodeJSCache(new NodeCache({ stdTTL: 3600, useClones: false })),
      // new CachingTokenListProvider(
      //   chainId,
      //   DEFAULT_TOKEN_LIST,
      //   new NodeJSCache(new NodeCache({ stdTTL: 3600, useClones: false }))
      // ),
      new TokenProvider(chainId, this.multicall2Provider)
    );
    if (v1SubgraphProvider) {
      this.v1SubgraphProvider = v1SubgraphProvider;
    } else {
      this.v1SubgraphProvider = new V1SubgraphProviderWithFallBacks([
        // TODO: implement api endpoint for retrieving subgraph data in json format
        // this will greatly improve performance and reliability of these queries
        // new CachingV1SubgraphProvider(
        //   chainId,
        //   new URISubgraphProvider(
        //     chainId,
        //     `https://cloudflare-ipfs.com/ipns/api.uniswap.org/v1/pools/v2/${chainName}.json`,
        //     undefined,
        //     0
        //   ),
        //   new NodeJSCache(new NodeCache({ stdTTL: 300, useClones: false }))
        // ),
        // new CachingV1SubgraphProvider(
        //   chainId,
        //   new V1SubgraphProvider(chainId),
        //   new NodeJSCache(new NodeCache({ stdTTL: 300, useClones: false }))
        // ),
        new StaticV1SubgraphProvider(chainId)
      ]);
    }
    if (v2SubgraphProvider) {
      this.v2SubgraphProvider = v2SubgraphProvider;
    } else {
      this.v2SubgraphProvider = new V2SubgraphProviderWithFallBacks([
        // TODO: implement api endpoint for retrieving subgraph data in json format
        // this will greatly improve performance and reliability of these queries
        // new CachingV2SubgraphProvider(
        //   chainId,
        //   new URISubgraphProvider(
        //     chainId,
        //     `https://cloudflare-ipfs.com/ipns/api.uniswap.org/v1/pools/v2/${chainName}.json`,
        //     undefined,
        //     0
        //   ),
        //   new NodeJSCache(new NodeCache({ stdTTL: 300, useClones: false }))
        // ),
        // new CachingV2SubgraphProvider(
        //   chainId,
        //   new V2SubgraphProvider(chainId),
        //   new NodeJSCache(new NodeCache({ stdTTL: 300, useClones: false }))
        // ),
        new StaticV2SubgraphProvider(chainId)
      ]);
    }
    let gasPriceProviderInstance = new OnChainGasPriceProvider(
      chainId,
      new EIP1559GasPriceProvider(this.provider),
      new LegacyGasPriceProvider(this.provider)
    );
    this.gasPriceProvider = gasPriceProvider ?? new CachingGasStationProvider(
      chainId,
      gasPriceProviderInstance,
      new NodeJSCache(
        new NodeCache({ stdTTL: 15, useClones: false })
      )
    );
    this.v1GasModelFactory = v1GasModelFactory ?? new V1HeuristicGasModelFactory();
    this.v2GasModelFactory = v2GasModelFactory ?? new V2HeuristicGasModelFactory();
    this.mixedRouteGasModelFactory = mixedRouteGasModelFactory ?? new MixedRouteHeuristicGasModelFactory();
    this.swapRouterProvider = swapRouterProvider ?? new SwapRouterProvider(this.multicall2Provider, this.chainId);
    if (tokenValidatorProvider) {
      this.tokenValidatorProvider = tokenValidatorProvider;
    } else if (this.chainId === ChainId.PULSECHAIN || this.chainId === ChainId.PULSECHAIN_TESTNET) {
      this.tokenValidatorProvider = new TokenValidatorProvider(
        this.chainId,
        this.multicall2Provider,
        new NodeJSCache(new NodeCache({ stdTTL: 3e4, useClones: false }))
      );
    }
    this.v1Quoter = new V1Quoter(
      this.v1SubgraphProvider,
      this.v1PoolProvider,
      this.v1QuoteProvider,
      this.v1GasModelFactory,
      this.tokenProvider,
      this.chainId,
      this.blockedTokenListProvider,
      this.tokenValidatorProvider
    );
    this.v2Quoter = new V2Quoter(
      this.v2SubgraphProvider,
      this.v2PoolProvider,
      this.v2QuoteProvider,
      this.v2GasModelFactory,
      this.tokenProvider,
      this.chainId,
      this.blockedTokenListProvider,
      this.tokenValidatorProvider
    );
    this.mixedQuoter = new MixedQuoter(
      this.v1SubgraphProvider,
      this.v1PoolProvider,
      this.v2SubgraphProvider,
      this.v2PoolProvider,
      this.onChainQuoteProvider,
      this.tokenProvider,
      this.chainId,
      this.blockedTokenListProvider,
      this.tokenValidatorProvider
    );
  }
  async routeToRatio(token0Balance, token1Balance, position, swapAndAddConfig, swapAndAddOptions, routingConfig = DEFAULT_ROUTING_CONFIG_BY_CHAIN(
    this.chainId
  )) {
    if (token1Balance.currency.wrapped.sortsBefore(token0Balance.currency.wrapped)) {
      [token0Balance, token1Balance] = [token1Balance, token0Balance];
    }
    let preSwapOptimalRatio = this.calculateOptimalRatio(
      position,
      position.pool.sqrtRatioX96,
      true
    );
    let zeroForOne;
    if (position.pool.tickCurrent > position.tickUpper) {
      zeroForOne = true;
    } else if (position.pool.tickCurrent < position.tickLower) {
      zeroForOne = false;
    } else {
      zeroForOne = new Fraction(
        token0Balance.quotient,
        token1Balance.quotient
      ).greaterThan(preSwapOptimalRatio);
      if (!zeroForOne)
        preSwapOptimalRatio = preSwapOptimalRatio.invert();
    }
    const [inputBalance, outputBalance] = zeroForOne ? [token0Balance, token1Balance] : [token1Balance, token0Balance];
    let optimalRatio = preSwapOptimalRatio;
    let postSwapTargetPool = position.pool;
    let exchangeRate = zeroForOne ? position.pool.token0Price : position.pool.token1Price;
    let swap = null;
    let ratioAchieved = false;
    let n = 0;
    while (!ratioAchieved) {
      n++;
      if (n > swapAndAddConfig.maxIterations) {
        log.info("max iterations exceeded");
        return {
          status: 2 /* NO_ROUTE_FOUND */,
          error: "max iterations exceeded"
        };
      }
      const amountToSwap = calculateRatioAmountIn(
        optimalRatio,
        exchangeRate,
        inputBalance,
        outputBalance
      );
      if (amountToSwap.equalTo(0)) {
        log.info(`no swap needed: amountToSwap = 0`);
        return {
          status: 3 /* NO_SWAP_NEEDED */
        };
      }
      swap = await this.route(
        amountToSwap,
        outputBalance.currency,
        TradeType.EXACT_INPUT,
        void 0,
        {
          ...DEFAULT_ROUTING_CONFIG_BY_CHAIN(this.chainId),
          ...routingConfig,
          /// @dev We do not want to query for mixedRoutes for routeToRatio as they are not supported
          /// [Protocol.V1, Protocol.V2] will make sure we only query for V1 and V2
          protocols: ["V1" /* V1 */, "V2" /* V2 */]
        }
      );
      if (!swap) {
        log.info("no route found from this.route()");
        return {
          status: 2 /* NO_ROUTE_FOUND */,
          error: "no route found"
        };
      }
      const inputBalanceUpdated = inputBalance.subtract(
        swap.trade.inputAmount
      );
      const outputBalanceUpdated = outputBalance.add(swap.trade.outputAmount);
      const newRatio = inputBalanceUpdated.divide(outputBalanceUpdated);
      {
        optimalRatio = preSwapOptimalRatio;
      }
      ratioAchieved = newRatio.equalTo(optimalRatio) || this.absoluteValue(
        newRatio.asFraction.divide(optimalRatio).subtract(1)
      ).lessThan(swapAndAddConfig.ratioErrorTolerance);
      exchangeRate = swap.trade.outputAmount.divide(swap.trade.inputAmount);
      log.info(
        {
          exchangeRate: exchangeRate.asFraction.toFixed(18),
          optimalRatio: optimalRatio.asFraction.toFixed(18),
          newRatio: newRatio.asFraction.toFixed(18),
          inputBalanceUpdated: inputBalanceUpdated.asFraction.toFixed(18),
          outputBalanceUpdated: outputBalanceUpdated.asFraction.toFixed(18),
          ratioErrorTolerance: swapAndAddConfig.ratioErrorTolerance.toFixed(18),
          iterationN: n.toString()
        },
        "QuoteToRatio Iteration Parameters"
      );
      if (exchangeRate.equalTo(0)) {
        log.info("exchangeRate to 0");
        return {
          status: 2 /* NO_ROUTE_FOUND */,
          error: "insufficient liquidity to swap to optimal ratio"
        };
      }
    }
    if (!swap) {
      return {
        status: 2 /* NO_ROUTE_FOUND */,
        error: "no route found"
      };
    }
    let methodParameters;
    if (swapAndAddOptions) {
      methodParameters = await this.buildSwapAndAddMethodParameters(
        swap.trade,
        swapAndAddOptions,
        {
          initialBalanceTokenIn: inputBalance,
          initialBalanceTokenOut: outputBalance,
          preLiquidityPosition: position
        }
      );
    }
    return {
      status: 1 /* SUCCESS */,
      result: { ...swap, methodParameters, optimalRatio, postSwapTargetPool }
    };
  }
  /**
   * @inheritdoc IRouter
   */
  async route(amount, quoteCurrency, tradeType, swapConfig, partialRoutingConfig = {}) {
    const { currencyIn, currencyOut } = this.determineCurrencyInOutFromTradeType(tradeType, amount, quoteCurrency);
    const tokenIn = currencyIn.wrapped;
    const tokenOut = currencyOut.wrapped;
    metric.setProperty("chainId", this.chainId);
    metric.setProperty("pair", `${tokenIn.symbol}/${tokenOut.symbol}`);
    metric.setProperty("tokenIn", tokenIn.address);
    metric.setProperty("tokenOut", tokenOut.address);
    metric.setProperty("tradeType", tradeType === TradeType.EXACT_INPUT ? "ExactIn" : "ExactOut");
    metric.putMetric(
      `QuoteRequestedForChain${this.chainId}`,
      1,
      "Count" /* Count */
    );
    const blockNumber = partialRoutingConfig.blockNumber ?? this.getBlockNumberPromise();
    const routingConfig = _16.merge(
      {},
      DEFAULT_ROUTING_CONFIG_BY_CHAIN(this.chainId),
      partialRoutingConfig,
      { blockNumber }
    );
    const gasPriceWei = await this.getGasPriceWei();
    const quoteToken = quoteCurrency.wrapped;
    const mixedRouteGasModel = await this.getGasModels(
      gasPriceWei,
      amount.currency.wrapped,
      quoteToken
    );
    const protocols = Array.from(new Set(routingConfig.protocols).values());
    const cacheMode = await this.routeCachingProvider?.getCacheMode(
      this.chainId,
      amount,
      quoteToken,
      tradeType,
      protocols
    );
    let cachedRoutes;
    if (cacheMode !== "darkmode" /* Darkmode */) {
      cachedRoutes = await this.routeCachingProvider?.getCachedRoute(
        this.chainId,
        amount,
        quoteToken,
        tradeType,
        protocols,
        await blockNumber
      );
    }
    if (cacheMode && cacheMode !== "darkmode" /* Darkmode */ && !cachedRoutes) {
      metric.putMetric(
        `GetCachedRoute_miss_${cacheMode}`,
        1,
        "Count" /* Count */
      );
      log.info(
        {
          tokenIn: tokenIn.symbol,
          tokenInAddress: tokenIn.address,
          tokenOut: tokenOut.symbol,
          tokenOutAddress: tokenOut.address,
          cacheMode,
          amount: amount.toExact(),
          chainId: this.chainId,
          tradeType: this.tradeTypeStr(tradeType)
        },
        `GetCachedRoute miss ${cacheMode} for ${this.tokenPairSymbolTradeTypeChainId(tokenIn, tokenOut, tradeType)}`
      );
    } else if (cachedRoutes) {
      metric.putMetric(
        `GetCachedRoute_hit_${cacheMode}`,
        1,
        "Count" /* Count */
      );
      log.info(
        {
          tokenIn: tokenIn.symbol,
          tokenInAddress: tokenIn.address,
          tokenOut: tokenOut.symbol,
          tokenOutAddress: tokenOut.address,
          cacheMode,
          amount: amount.toExact(),
          chainId: this.chainId,
          tradeType: this.tradeTypeStr(tradeType)
        },
        `GetCachedRoute hit ${cacheMode} for ${this.tokenPairSymbolTradeTypeChainId(tokenIn, tokenOut, tradeType)}`
      );
    }
    let swapRouteFromCachePromise = Promise.resolve(null);
    if (cachedRoutes) {
      swapRouteFromCachePromise = this.getSwapRouteFromCache(
        cachedRoutes,
        await blockNumber,
        amount,
        quoteToken,
        tradeType,
        routingConfig,
        mixedRouteGasModel,
        gasPriceWei
      );
    }
    let swapRouteFromChainPromise = Promise.resolve(null);
    if (!cachedRoutes || cacheMode !== "livemode" /* Livemode */) {
      swapRouteFromChainPromise = this.getSwapRouteFromChain(
        amount,
        tokenIn,
        tokenOut,
        protocols,
        quoteToken,
        tradeType,
        routingConfig,
        mixedRouteGasModel,
        gasPriceWei
      );
    }
    const [swapRouteFromCache, swapRouteFromChain] = await Promise.all([
      swapRouteFromCachePromise,
      swapRouteFromChainPromise
    ]);
    let swapRouteRaw;
    if (cacheMode === "livemode" /* Livemode */ && swapRouteFromCache) {
      log.info(`CacheMode is ${cacheMode}, and we are using swapRoute from cache`);
      swapRouteRaw = swapRouteFromCache;
    } else {
      log.info(`CacheMode is ${cacheMode}, and we are using materialized swapRoute`);
      swapRouteRaw = swapRouteFromChain;
    }
    if (cacheMode === "tapcompare" /* Tapcompare */ && swapRouteFromCache && swapRouteFromChain) {
      const quoteDiff = swapRouteFromChain.quote.subtract(swapRouteFromCache.quote);
      const quoteGasAdjustedDiff = swapRouteFromChain.quoteGasAdjusted.subtract(swapRouteFromCache.quoteGasAdjusted);
      const gasUsedDiff = swapRouteFromChain.estimatedGasUsed - swapRouteFromCache.estimatedGasUsed;
      if (!quoteDiff.equalTo(0)) {
        log.warn(
          {
            quoteFromChain: swapRouteFromChain.quote.toExact(),
            quoteFromCache: swapRouteFromCache.quote.toExact(),
            quoteDiff: quoteDiff.toExact(),
            quoteGasAdjustedFromChain: swapRouteFromChain.quoteGasAdjusted.toExact(),
            quoteGasAdjustedFromCache: swapRouteFromCache.quoteGasAdjusted.toExact(),
            quoteGasAdjustedDiff: quoteGasAdjustedDiff.toExact(),
            gasUsedFromChain: swapRouteFromChain.estimatedGasUsed.toString(),
            gasUsedFromCache: swapRouteFromCache.estimatedGasUsed.toString(),
            gasUsedDiff: gasUsedDiff.toString(),
            routesFromChain: swapRouteFromChain.routes.toString(),
            routesFromCache: swapRouteFromCache.routes.toString(),
            amount: amount.toExact(),
            originalAmount: cachedRoutes?.originalAmount,
            pair: this.tokenPairSymbolTradeTypeChainId(tokenIn, tokenOut, tradeType)
          },
          `Comparing quotes between Chain and Cache for ${this.tokenPairSymbolTradeTypeChainId(
            tokenIn,
            tokenOut,
            tradeType
          )}`
        );
      }
    }
    if (!swapRouteRaw) {
      return null;
    }
    const {
      quote,
      quoteGasAdjusted,
      estimatedGasUsed,
      routes: routeAmounts,
      estimatedGasUsedQuoteToken,
      estimatedGasUsedUSD
    } = swapRouteRaw;
    if (this.routeCachingProvider && cacheMode !== "darkmode" /* Darkmode */ && swapRouteFromChain) {
      const routesToCache = CachedRoutes.fromRoutesWithValidQuotes(
        swapRouteFromChain.routes,
        this.chainId,
        tokenIn,
        tokenOut,
        protocols.sort(),
        // sort it for consistency in the order of the protocols.
        await blockNumber,
        tradeType,
        amount.toExact()
      );
      if (routesToCache) {
        const tokenPairSymbolTradeTypeChainId = this.tokenPairSymbolTradeTypeChainId(tokenIn, tokenOut, tradeType);
        this.routeCachingProvider.setCachedRoute(routesToCache, amount).then((success) => {
          const status = success ? "success" : "rejected";
          metric.putMetric(
            `SetCachedRoute_${tokenPairSymbolTradeTypeChainId}_${status}`,
            1,
            "Count" /* Count */
          );
        }).catch((reason) => {
          log.info(
            {
              reason,
              tokenPair: tokenPairSymbolTradeTypeChainId
            },
            `SetCachedRoute failure`
          );
          metric.putMetric(
            `SetCachedRoute_${tokenPairSymbolTradeTypeChainId}_failure`,
            1,
            "Count" /* Count */
          );
        });
      }
    }
    metric.putMetric(
      `QuoteFoundForChain${this.chainId}`,
      1,
      "Count" /* Count */
    );
    const trade = buildTrade(
      currencyIn,
      currencyOut,
      tradeType,
      routeAmounts
    );
    let methodParameters;
    if (swapConfig) {
      methodParameters = buildSwapMethodParameters(
        trade,
        swapConfig,
        this.chainId
      );
    }
    const swapRoute = {
      quote,
      quoteGasAdjusted,
      estimatedGasUsed,
      estimatedGasUsedQuoteToken,
      estimatedGasUsedUSD,
      gasPriceWei,
      route: routeAmounts,
      trade,
      methodParameters,
      blockNumber: BigInt(await blockNumber)
    };
    if (swapConfig && swapConfig.simulate && methodParameters && methodParameters.calldata) {
      if (!this.simulator) {
        throw new Error("Simulator not initialized!");
      }
      log.info({ swapConfig, methodParameters }, "Starting simulation");
      const fromAddress = swapConfig.simulate.fromAddress;
      const beforeSimulate = Date.now();
      const swapRouteWithSimulation = await this.simulator.simulate(
        fromAddress,
        swapConfig,
        swapRoute,
        amount,
        // Quote will be in WETH even if quoteCurrency is ETH
        // So we init a new CurrencyAmount object here
        CurrencyAmount.fromRawAmount(quoteCurrency, quote.quotient.toString())
      );
      metric.putMetric(
        "SimulateTransaction",
        Date.now() - beforeSimulate,
        "Milliseconds" /* Milliseconds */
      );
      return swapRouteWithSimulation;
    }
    return swapRoute;
  }
  async getSwapRouteFromCache(cachedRoutes, blockNumber, amount, quoteToken, tradeType, routingConfig, mixedRouteGasModel, gasPriceWei) {
    log.info(
      {
        protocols: cachedRoutes.protocolsCovered,
        tradeType: cachedRoutes.tradeType,
        cachedBlockNumber: cachedRoutes.blockNumber,
        quoteBlockNumber: blockNumber
      },
      "Routing across CachedRoute"
    );
    const quotePromises = [];
    const v1Routes = cachedRoutes.routes.filter((route) => route.protocol === "V1" /* V1 */);
    const v2Routes = cachedRoutes.routes.filter((route) => route.protocol === "V2" /* V2 */);
    const mixedRoutes = cachedRoutes.routes.filter((route) => route.protocol === "MIXED" /* MIXED */);
    const percentsSet = new Set(cachedRoutes.routes.map((route) => route.percent));
    percentsSet.add(100);
    percentsSet.add(50);
    const percents = Array.from(percentsSet.values());
    const amounts = percents.map((percent) => amount.multiply(new Fraction(percent, 100)));
    if (v1Routes.length > 0) {
      quotePromises.push(
        // When we fetch the quotes in V1, we are not calling the `onChainProvider` like on mixedRoutes
        // Instead we are using the reserves in the Pool object, so we need to re-load the current reserves.
        this.v1Quoter.getRoutesThenQuotes(
          v1Routes[0].tokenIn,
          v1Routes[0].tokenOut,
          amounts,
          percents,
          quoteToken,
          tradeType,
          routingConfig,
          void 0,
          gasPriceWei
        )
      );
    }
    if (v2Routes.length > 0) {
      quotePromises.push(
        // When we fetch the quotes in V2, we are not calling the `onChainProvider` like on mixedRoutes
        // Instead we are using the reserves in the Pool object, so we need to re-load the current reserves.
        this.v2Quoter.getRoutesThenQuotes(
          v2Routes[0].tokenIn,
          v2Routes[0].tokenOut,
          amounts,
          percents,
          quoteToken,
          tradeType,
          routingConfig,
          void 0,
          gasPriceWei
        )
      );
    }
    if (mixedRoutes.length > 0) {
      const mixedRoutesFromCache = mixedRoutes.map((cachedRoute) => cachedRoute.route);
      quotePromises.push(
        this.mixedQuoter.getQuotes(
          mixedRoutesFromCache,
          amounts,
          percents,
          quoteToken,
          tradeType,
          routingConfig,
          void 0,
          mixedRouteGasModel
        )
      );
    }
    const getQuotesResults = await Promise.all(quotePromises);
    const allRoutesWithValidQuotes = _16.flatMap(getQuotesResults, (quoteResult) => quoteResult.routesWithValidQuotes);
    return getBestSwapRoute(
      amount,
      percents,
      allRoutesWithValidQuotes,
      tradeType,
      this.chainId,
      routingConfig
    );
  }
  async getSwapRouteFromChain(amount, tokenIn, tokenOut, protocols, quoteToken, tradeType, routingConfig, mixedRouteGasModel, gasPriceWei) {
    const [percents, amounts] = this.getAmountDistribution(
      amount,
      routingConfig
    );
    const noProtocolsSpecified = protocols.length === 0;
    const v1ProtocolSpecified = protocols.includes("V1" /* V1 */);
    const v2ProtocolSpecified = protocols.includes("V2" /* V2 */);
    const v1SupportedInChain = V1_SUPPORTED.includes(this.chainId);
    const v2SupportedInChain = V2_SUPPORTED.includes(this.chainId);
    const shouldQueryMixedProtocol = protocols.includes("MIXED" /* MIXED */) || noProtocolsSpecified && v1SupportedInChain && v2SupportedInChain;
    const mixedProtocolAllowed = ([ChainId.PULSECHAIN].includes(this.chainId) || [ChainId.PULSECHAIN_TESTNET].includes(this.chainId)) && tradeType === TradeType.EXACT_INPUT;
    const quotePromises = [];
    if (v1SupportedInChain && (v1ProtocolSpecified || noProtocolsSpecified)) {
      log.info({ protocols, tradeType }, "Routing across V1");
      quotePromises.push(
        this.v1Quoter.getRoutesThenQuotes(
          tokenIn,
          tokenOut,
          amounts,
          percents,
          quoteToken,
          tradeType,
          routingConfig,
          void 0,
          gasPriceWei
        )
      );
    }
    if (v2SupportedInChain && (v2ProtocolSpecified || noProtocolsSpecified)) {
      log.info({ protocols, tradeType }, "Routing across V2");
      quotePromises.push(
        this.v2Quoter.getRoutesThenQuotes(
          tokenIn,
          tokenOut,
          amounts,
          percents,
          quoteToken,
          tradeType,
          routingConfig,
          void 0,
          gasPriceWei
        )
      );
    }
    if (shouldQueryMixedProtocol && mixedProtocolAllowed) {
      log.info({ protocols, tradeType }, "Routing across MixedRoutes");
      quotePromises.push(
        this.mixedQuoter.getRoutesThenQuotes(
          tokenIn,
          tokenOut,
          amounts,
          percents,
          quoteToken,
          tradeType,
          routingConfig,
          mixedRouteGasModel
        )
      );
    }
    const getQuotesResults = await Promise.all(quotePromises);
    const allRoutesWithValidQuotes = [];
    const allCandidatePools = [];
    getQuotesResults.forEach((getQuoteResult) => {
      allRoutesWithValidQuotes.push(...getQuoteResult.routesWithValidQuotes);
      if (getQuoteResult.candidatePools) {
        allCandidatePools.push(getQuoteResult.candidatePools);
      }
    });
    if (allRoutesWithValidQuotes.length === 0) {
      log.info({ allRoutesWithValidQuotes }, "Received no valid quotes");
      return null;
    }
    const bestSwapRoute = await getBestSwapRoute(
      amount,
      percents,
      allRoutesWithValidQuotes,
      tradeType,
      this.chainId,
      routingConfig
    );
    if (bestSwapRoute) {
      this.emitPoolSelectionMetrics(bestSwapRoute, allCandidatePools);
    }
    return bestSwapRoute;
  }
  tradeTypeStr(tradeType) {
    return tradeType === TradeType.EXACT_INPUT ? "ExactIn" : "ExactOut";
  }
  tokenPairSymbolTradeTypeChainId(tokenIn, tokenOut, tradeType) {
    return `${tokenIn.symbol}/${tokenOut.symbol}/${this.tradeTypeStr(tradeType)}/${this.chainId}`;
  }
  determineCurrencyInOutFromTradeType(tradeType, amount, quoteCurrency) {
    if (tradeType === TradeType.EXACT_INPUT) {
      return {
        currencyIn: amount.currency,
        currencyOut: quoteCurrency
      };
    } else {
      return {
        currencyIn: quoteCurrency,
        currencyOut: amount.currency
      };
    }
  }
  async getGasPriceWei() {
    const beforeGasTimestamp = Date.now();
    const { gasPriceWei } = await this.gasPriceProvider.getGasPrice();
    metric.putMetric(
      "GasPriceLoad",
      Date.now() - beforeGasTimestamp,
      "Milliseconds" /* Milliseconds */
    );
    return gasPriceWei;
  }
  async getGasModels(gasPriceWei, amountToken, quoteToken) {
    const beforeGasModel = Date.now();
    const mixedRouteGasModelPromise = this.mixedRouteGasModelFactory.buildGasModel({
      chainId: this.chainId,
      gasPriceWei,
      v1poolProvider: this.v1PoolProvider,
      amountToken,
      quoteToken,
      v2poolProvider: this.v2PoolProvider
    });
    const mixedRouteGasModel = await mixedRouteGasModelPromise;
    metric.putMetric(
      "GasModelCreation",
      Date.now() - beforeGasModel,
      "Milliseconds" /* Milliseconds */
    );
    return mixedRouteGasModel;
  }
  // Note multiplications here can result in a loss of precision in the amounts (e.g. taking 50% of 101)
  // This is reconcilled at the end of the algorithm by adding any lost precision to one of
  // the splits in the route.
  getAmountDistribution(amount, routingConfig) {
    const { distributionPercent } = routingConfig;
    const percents = [];
    const amounts = [];
    for (let i = 1; i <= 100 / distributionPercent; i++) {
      percents.push(i * distributionPercent);
      amounts.push(amount.multiply(new Fraction(i * distributionPercent, 100)));
    }
    return [percents, amounts];
  }
  async buildSwapAndAddMethodParameters(trade, swapAndAddOptions, swapAndAddParameters) {
    const {
      swapOptions: { recipient, slippageTolerance, deadline, inputTokenPermit },
      addLiquidityOptions: addLiquidityConfig
    } = swapAndAddOptions;
    const preLiquidityPosition = swapAndAddParameters.preLiquidityPosition;
    const finalBalanceTokenIn = swapAndAddParameters.initialBalanceTokenIn.subtract(trade.inputAmount);
    const finalBalanceTokenOut = swapAndAddParameters.initialBalanceTokenOut.add(trade.outputAmount);
    const approvalTypes = await this.swapRouterProvider.getApprovalType(
      finalBalanceTokenIn,
      finalBalanceTokenOut
    );
    const zeroForOne = finalBalanceTokenIn.currency.wrapped.sortsBefore(
      finalBalanceTokenOut.currency.wrapped
    );
    return {
      ...SwapRouter.swapAndAddCallParameters(
        trade,
        {
          recipient,
          slippageTolerance,
          deadlineOrPreviousBlockhash: deadline,
          inputTokenPermit
        },
        Position.fromAmounts({
          pool: preLiquidityPosition.pool,
          tickLower: preLiquidityPosition.tickLower,
          tickUpper: preLiquidityPosition.tickUpper,
          amount0: zeroForOne ? finalBalanceTokenIn.quotient.toString() : finalBalanceTokenOut.quotient.toString(),
          amount1: zeroForOne ? finalBalanceTokenOut.quotient.toString() : finalBalanceTokenIn.quotient.toString(),
          useFullPrecision: false
        }),
        addLiquidityConfig,
        approvalTypes.approvalTokenIn,
        approvalTypes.approvalTokenOut
      ),
      to: PULSEX_SWAP_ROUTER_ADDRESSES[this.chainId]
    };
  }
  emitPoolSelectionMetrics(swapRouteRaw, allPoolsBySelection) {
    const poolAddressesUsed = /* @__PURE__ */ new Set();
    const { routes: routeAmounts } = swapRouteRaw;
    _16(routeAmounts).flatMap((routeAmount) => {
      const { poolAddresses } = routeAmount;
      return poolAddresses;
    }).forEach((address) => {
      poolAddressesUsed.add(address.toLowerCase());
    });
    for (const poolsBySelection of allPoolsBySelection) {
      const { protocol } = poolsBySelection;
      _16.forIn(
        poolsBySelection.selections,
        (pools, topNSelection) => {
          const topNUsed = _16.findLastIndex(
            pools,
            (pool) => poolAddressesUsed.has(pool.id.toLowerCase())
          ) + 1;
          metric.putMetric(
            _16.capitalize(`${protocol}${topNSelection}`),
            topNUsed,
            "Count" /* Count */
          );
        }
      );
    }
    let hasV1Route = false;
    let hasV2Route = false;
    let hasMixedRoute = false;
    for (const routeAmount of routeAmounts) {
      if (routeAmount.protocol === "V1" /* V1 */) {
        hasV1Route = true;
      }
      if (routeAmount.protocol === "V2" /* V2 */) {
        hasV2Route = true;
      }
      if (routeAmount.protocol === "MIXED" /* MIXED */) {
        hasMixedRoute = true;
      }
    }
    if (hasMixedRoute && (hasV1Route || hasV2Route)) {
      if (hasV1Route && hasV2Route) {
        metric.putMetric(
          `MixedAndV3AndV2SplitRoute`,
          1,
          "Count" /* Count */
        );
        metric.putMetric(
          `MixedAndV3AndV2SplitRouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      } else if (hasV1Route) {
        metric.putMetric(`MixedAndV1SplitRoute`, 1, "Count" /* Count */);
        metric.putMetric(
          `MixedAndV1SplitRouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      } else if (hasV2Route) {
        metric.putMetric(`MixedAndV2SplitRoute`, 1, "Count" /* Count */);
        metric.putMetric(
          `MixedAndV2SplitRouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      }
    } else if (hasV1Route && hasV2Route) {
      metric.putMetric(`V1AndV2SplitRoute`, 1, "Count" /* Count */);
      metric.putMetric(
        `V1AndV2SplitRouteForChain${this.chainId}`,
        1,
        "Count" /* Count */
      );
    } else if (hasMixedRoute) {
      if (routeAmounts.length > 1) {
        metric.putMetric(`MixedSplitRoute`, 1, "Count" /* Count */);
        metric.putMetric(
          `MixedSplitRouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      } else {
        metric.putMetric(`MixedRoute`, 1, "Count" /* Count */);
        metric.putMetric(
          `MixedRouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      }
    } else if (hasV1Route) {
      if (routeAmounts.length > 1) {
        metric.putMetric(`V1SplitRoute`, 1, "Count" /* Count */);
        metric.putMetric(
          `V1SplitRouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      } else {
        metric.putMetric(`V1Route`, 1, "Count" /* Count */);
        metric.putMetric(
          `V1RouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      }
    } else if (hasV2Route) {
      if (routeAmounts.length > 1) {
        metric.putMetric(`V2SplitRoute`, 1, "Count" /* Count */);
        metric.putMetric(
          `V2SplitRouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      } else {
        metric.putMetric(`V2Route`, 1, "Count" /* Count */);
        metric.putMetric(
          `V2RouteForChain${this.chainId}`,
          1,
          "Count" /* Count */
        );
      }
    }
  }
  calculateOptimalRatio(position, sqrtRatioX96, zeroForOne) {
    const upperSqrtRatioX96 = TickMath.getSqrtRatioAtTick(position.tickUpper);
    const lowerSqrtRatioX96 = TickMath.getSqrtRatioAtTick(position.tickLower);
    if (sqrtRatioX96 > upperSqrtRatioX96 || sqrtRatioX96 < lowerSqrtRatioX96) {
      return new Fraction(0, 1);
    }
    const precision = BigInt("1" + "0".repeat(18));
    let optimalRatio = new Fraction(
      SqrtPriceMath.getAmount0Delta(
        sqrtRatioX96,
        upperSqrtRatioX96,
        precision,
        true
      ),
      SqrtPriceMath.getAmount1Delta(
        sqrtRatioX96,
        lowerSqrtRatioX96,
        precision,
        true
      )
    );
    if (!zeroForOne)
      optimalRatio = optimalRatio.invert();
    return optimalRatio;
  }
  async userHasSufficientBalance(fromAddress, tradeType, amount, quote) {
    try {
      const neededBalance = tradeType === TradeType.EXACT_INPUT ? amount : quote;
      let balance;
      if (neededBalance.currency.isNative) {
        balance = await this.provider.getBalance({ address: fromAddress });
      } else {
        const tokenContract = getContract({
          address: neededBalance.currency.address,
          abi: erc20Abi,
          publicClient: this.provider
        });
        balance = await tokenContract.read.balanceOf([fromAddress]);
      }
      return balance >= neededBalance.quotient;
    } catch (e) {
      log.error(e, "Error while checking user balance");
      return false;
    }
  }
  absoluteValue(fraction) {
    const numeratorAbs = fraction.numerator < 0n ? -fraction.numerator : fraction.numerator;
    const denominatorAbs = fraction.denominator < 0n ? -fraction.denominator : fraction.denominator;
    return new Fraction(numeratorAbs, denominatorAbs);
  }
  getBlockNumberPromise() {
    return retry7(
      async (_b, attempt) => {
        if (attempt > 1) {
          log.info(`Get block number attempt ${attempt}`);
        }
        return this.provider.getBlockNumber();
      },
      {
        retries: 2,
        minTimeout: 100,
        maxTimeout: 1e3
      }
    );
  }
};
var SimulationStatus = /* @__PURE__ */ ((SimulationStatus2) => {
  SimulationStatus2[SimulationStatus2["NotSupported"] = 0] = "NotSupported";
  SimulationStatus2[SimulationStatus2["Failed"] = 1] = "Failed";
  SimulationStatus2[SimulationStatus2["Succeeded"] = 2] = "Succeeded";
  SimulationStatus2[SimulationStatus2["InsufficientBalance"] = 3] = "InsufficientBalance";
  SimulationStatus2[SimulationStatus2["NotApproved"] = 4] = "NotApproved";
  return SimulationStatus2;
})(SimulationStatus || {});
var Simulator2 = class {
  /**
   * Returns a new SwapRoute with simulated gas estimates
   * @returns SwapRoute
   */
  constructor(provider, chainId) {
    this.chainId = chainId;
    this.provider = provider;
  }
  async simulate(fromAddress, swapOptions, swapRoute, amount, quote, providerConfig) {
    if (await this.userHasSufficientBalance(
      fromAddress,
      swapRoute.trade.tradeType,
      amount,
      quote
    )) {
      log.info(
        "User has sufficient balance to simulate. Simulating transaction."
      );
      try {
        return this.simulateTransaction(
          fromAddress,
          swapOptions,
          swapRoute,
          providerConfig
        );
      } catch (e) {
        log.error({ e }, "Error simulating transaction");
        return {
          ...swapRoute,
          simulationStatus: 1 /* Failed */
        };
      }
    } else {
      log.error("User does not have sufficient balance to simulate.");
      return {
        ...swapRoute,
        simulationStatus: 3 /* InsufficientBalance */
      };
    }
  }
  async userHasSufficientBalance(fromAddress, tradeType, amount, quote) {
    try {
      const neededBalance = tradeType == TradeType.EXACT_INPUT ? amount : quote;
      let balance;
      if (neededBalance.currency.isNative) {
        balance = await this.provider.getBalance({ address: fromAddress });
      } else {
        const tokenContract = getContract({
          address: neededBalance.currency.address,
          abi: erc20Abi,
          publicClient: this.provider
        });
        balance = await tokenContract.read.balanceOf([fromAddress]);
      }
      const hasBalance = balance >= neededBalance.quotient;
      log.info(
        {
          fromAddress,
          balance: balance.toString(),
          neededBalance: neededBalance.quotient.toString(),
          neededAddress: neededBalance.wrapped.currency.address,
          hasBalance
        },
        "Result of balance check for simulation"
      );
      return hasBalance;
    } catch (e) {
      log.error(e, "Error while checking user balance");
      return false;
    }
  }
  async checkTokenApproved(fromAddress, inputAmount, swapOptions, provider) {
    const tokenContract = getContract({
      address: inputAmount.currency.wrapped.address,
      abi: erc20Abi,
      publicClient: provider
    });
    if (swapOptions.type == 0 /* UNIVERSAL_ROUTER */) ; else if (swapOptions.type == 1 /* SWAP_ROUTER_02 */) {
      if (swapOptions.inputTokenPermit) {
        log.info(
          {
            inputAmount: inputAmount.quotient.toString()
          },
          "Simulating on SwapRouter02 info - Permit was provided for simulation. Not checking allowances."
        );
        return true;
      }
      const allowance = await tokenContract.read.allowance([
        fromAddress,
        PULSEX_SWAP_ROUTER_ADDRESSES[this.chainId]
      ]);
      const hasAllowance = allowance >= inputAmount.quotient;
      log.info(
        {
          hasAllowance,
          allowance: allowance.toString(),
          inputAmount: inputAmount.quotient.toString()
        },
        `Simulating on SwapRouter02 - Has allowance: ${hasAllowance}`
      );
      return hasAllowance;
    }
    throw new Error(`Unsupported swap type ${swapOptions}`);
  }
};

// src/providers/eth-estimate-gas-provider.ts
var EthEstimateGasSimulator = class extends Simulator2 {
  constructor(chainId, provider, v1PoolProvider, v2PoolProvider) {
    super(provider, chainId);
    this.v1PoolProvider = v1PoolProvider;
    this.v2PoolProvider = v2PoolProvider;
  }
  async ethEstimateGas(fromAddress, swapOptions, route) {
    const currencyIn = route.trade.inputAmount.currency;
    let estimatedGasUsed;
    if (swapOptions.type == 0 /* UNIVERSAL_ROUTER */) {
      log.info(
        { methodParameters: route.methodParameters },
        "Simulating using eth_estimateGas on Universal Router"
      );
      try {
        estimatedGasUsed = await this.provider.estimateGas({
          data: route.methodParameters.calldata,
          to: route.methodParameters.to,
          account: fromAddress,
          value: BigInt(
            currencyIn.isNative ? route.methodParameters.value : "0"
          )
        });
      } catch (e) {
        log.error({ e }, "Error estimating gas");
        return {
          ...route,
          simulationStatus: 1 /* Failed */
        };
      }
    } else if (swapOptions.type == 1 /* SWAP_ROUTER_02 */) {
      log.info(
        { methodParameters: route.methodParameters },
        "Simulating using eth_estimateGas on SwapRouter02"
      );
      try {
        estimatedGasUsed = await this.provider.estimateGas({
          data: route.methodParameters.calldata,
          to: route.methodParameters.to,
          account: fromAddress,
          value: BigInt(
            currencyIn.isNative ? route.methodParameters.value : "0"
          )
        });
      } catch (e) {
        log.error({ e }, "Error estimating gas");
        return {
          ...route,
          simulationStatus: 1 /* Failed */
        };
      }
    } else {
      throw new Error(`Unsupported swap type ${swapOptions}`);
    }
    estimatedGasUsed = this.inflateGasLimit(estimatedGasUsed);
    const {
      estimatedGasUsedUSD,
      estimatedGasUsedQuoteToken,
      quoteGasAdjusted
    } = await calculateGasUsed(
      route.quote.currency.chainId,
      route,
      estimatedGasUsed,
      this.v1PoolProvider,
      this.v2PoolProvider
    );
    return {
      ...initSwapRouteFromExisting(
        route,
        this.v1PoolProvider,
        this.v2PoolProvider,
        quoteGasAdjusted,
        estimatedGasUsed,
        estimatedGasUsedQuoteToken,
        estimatedGasUsedUSD
      ),
      simulationStatus: 2 /* Succeeded */
    };
  }
  inflateGasLimit(gasLimit) {
    return gasLimit + gasLimit / BigInt(5);
  }
  async simulateTransaction(fromAddress, swapOptions, swapRoute, _providerConfig) {
    const inputAmount = swapRoute.trade.inputAmount;
    if (inputAmount.currency.isNative || await this.checkTokenApproved(
      fromAddress,
      inputAmount,
      swapOptions,
      this.provider
    )) {
      return await this.ethEstimateGas(
        fromAddress,
        swapOptions,
        swapRoute
      );
    } else {
      log.info("Token not approved, skipping simulation");
      return {
        ...swapRoute,
        simulationStatus: 4 /* NotApproved */
      };
    }
  }
};
var ETHGasStationInfoProvider2 = class extends IGasPriceProvider {
  constructor(url) {
    super();
    this.url = url;
  }
  async getGasPrice() {
    log.info(`About to get gas prices from gas station ${this.url}`);
    const response = await retry7(
      async () => {
        return axios3.get(this.url);
      },
      { retries: 1 }
    );
    const { data: gasPriceResponse, status } = response;
    if (status != 200) {
      log.error({ response }, `Unabled to get gas price from ${this.url}.`);
      throw new Error(`Unable to get gas price from ${this.url}`);
    }
    log.info(
      { gasPriceResponse },
      'Gas price response from API. About to parse "fast" to big number'
    );
    const gasPriceWei = BigInt(gasPriceResponse.fast) / BigInt(10) * BigInt(10) ** BigInt(9);
    log.info(
      `Gas price in wei: ${gasPriceWei} as of block ${gasPriceResponse.blockNum}`
    );
    return { gasPriceWei };
  }
};

// src/providers/legacy-gas-price-provider.ts
var LegacyGasPriceProvider = class extends IGasPriceProvider {
  constructor(provider) {
    super();
    this.provider = provider;
  }
  async getGasPrice() {
    const gasPriceWei = await this.provider.getGasPrice();
    log.info(
      { gasPriceWei },
      `Got gas price ${gasPriceWei} using eth_gasPrice RPC`
    );
    return {
      gasPriceWei
    };
  }
};

// src/providers/multicall-provider.ts
var IMulticallProvider = class {
};

// src/abis/UniswapInterfaceMulticall.ts
var uniswapInterfaceMulticallAbi = [
  {
    "inputs": [],
    "name": "getCurrentBlockTimestamp",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "timestamp",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "addr",
        "type": "address"
      }
    ],
    "name": "getEthBalance",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "balance",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "target",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "gasLimit",
            "type": "uint256"
          },
          {
            "internalType": "bytes",
            "name": "callData",
            "type": "bytes"
          }
        ],
        "internalType": "struct UniswapInterfaceMulticall.Call[]",
        "name": "calls",
        "type": "tuple[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "blockNumber",
        "type": "uint256"
      },
      {
        "components": [
          {
            "internalType": "bool",
            "name": "success",
            "type": "bool"
          },
          {
            "internalType": "uint256",
            "name": "gasUsed",
            "type": "uint256"
          },
          {
            "internalType": "bytes",
            "name": "returnData",
            "type": "bytes"
          }
        ],
        "internalType": "struct UniswapInterfaceMulticall.Result[]",
        "name": "returnData",
        "type": "tuple[]"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

// src/providers/multicall-uniswap-provider.ts
var UniswapMulticallProvider = class extends IMulticallProvider {
  constructor(chainId, provider, gasLimitPerCall = 1e6) {
    super();
    this.chainId = chainId;
    this.provider = provider;
    this.gasLimitPerCall = gasLimitPerCall;
    this.provider = provider;
  }
  async callSameFunctionOnMultipleContracts(params) {
    const {
      abi,
      addresses,
      functionName,
      functionParams,
      bytes32SymbolCall,
      additionalConfig
    } = params;
    const callData = encodeFunctionData({
      abi,
      functionName,
      args: functionParams
    });
    const calls = _16.map(addresses, (address) => {
      return {
        target: address,
        callData,
        gasLimit: BigInt(this.gasLimitPerCall)
      };
    });
    const { results: result, blockNumber } = await multicallByGasLimit(calls, {
      gasLimit: additionalConfig?.gasLimit,
      gasBuffer: additionalConfig?.gasBuffer,
      dropUnexecutedCalls: additionalConfig?.dropUnexecutedCalls,
      chainId: this.chainId,
      client: this.provider
    });
    if (bytes32SymbolCall) {
      const erc20Abi2 = [
        {
          inputs: [],
          name: "symbol",
          outputs: [
            {
              internalType: "string",
              name: "",
              type: "string"
            }
          ],
          stateMutability: "view",
          type: "function"
        }
      ];
      const results2 = [];
      for (const { result: callResult, success } of result) {
        if (!success || callResult.length <= 2) {
          results2.push({
            success: false,
            returnData: callResult
          });
          continue;
        }
        const result2 = decodeFunctionResult({
          abi,
          functionName,
          data: callResult
        });
        const data = toBytes(result2[0]);
        if (data[31] !== 0) {
          results2.push({
            success: true,
            result: decodeFunctionResult({
              abi: erc20Abi2,
              functionName: "symbol",
              data: callResult
            })
          });
        } else {
          results2.push({
            success: true,
            result: [hexToString(result2[0], { size: 32 })]
          });
        }
      }
      return { blockNumber, results: results2 };
    }
    const results = [];
    for (const { result: callResult, success } of result) {
      if (!success || callResult.length <= 2) {
        results.push({
          success: false,
          returnData: callResult
        });
        continue;
      }
      results.push({
        success: true,
        result: decodeFunctionResult({
          abi,
          functionName,
          data: callResult
        })
      });
    }
    return { blockNumber, results };
  }
  async callSameFunctionOnContractWithMultipleParams(params) {
    const {
      address,
      abi,
      functionName,
      functionParams,
      additionalConfig,
      providerConfig
    } = params;
    const gasLimitPerCall = additionalConfig?.gasLimitPerCallOverride ?? this.gasLimitPerCall;
    providerConfig?.blockNumber ?? void 0;
    const calls = functionParams.map((functionParam) => {
      const callData = encodeFunctionData({
        abi,
        functionName,
        args: functionParam
      });
      return {
        target: address,
        callData,
        gasLimit: BigInt(gasLimitPerCall)
      };
    });
    const { results: result, blockNumber } = await multicallByGasLimit(calls, {
      gasLimit: additionalConfig?.gasLimit,
      gasBuffer: additionalConfig?.gasBuffer,
      dropUnexecutedCalls: additionalConfig?.dropUnexecutedCalls,
      chainId: this.chainId,
      client: this.provider
    });
    const results = [];
    const gasUsedForSuccess = [];
    for (const { result: callResult, success, gasUsed } of result) {
      if (!success || callResult.length <= 2) {
        results.push({
          success: false,
          returnData: callResult
        });
        continue;
      }
      gasUsedForSuccess.push(Number(gasUsed));
      results.push({
        success: true,
        result: decodeFunctionResult({
          abi,
          functionName,
          data: callResult
        })
      });
    }
    return {
      blockNumber,
      results,
      approxGasUsedPerSuccessCall: stats.percentile(gasUsedForSuccess, 99)
    };
  }
  async callMultipleFunctionsOnSameContract(params) {
    const {
      address,
      abi,
      functionNames,
      functionParams,
      additionalConfig,
      providerConfig
    } = params;
    const gasLimitPerCall = additionalConfig?.gasLimitPerCallOverride ?? this.gasLimitPerCall;
    providerConfig?.blockNumber ?? void 0;
    const calls = functionNames.map((functionName, i) => {
      const callData = encodeFunctionData({
        abi,
        functionName,
        args: functionParams ? functionParams[i] : []
      });
      return {
        target: address,
        callData,
        gasLimit: BigInt(gasLimitPerCall)
      };
    });
    const { results: result, blockNumber } = await multicallByGasLimit(calls, {
      gasLimit: additionalConfig?.gasLimit,
      gasBuffer: additionalConfig?.gasBuffer,
      dropUnexecutedCalls: additionalConfig?.dropUnexecutedCalls,
      chainId: this.chainId,
      client: this.provider
    });
    const results = [];
    const gasUsedForSuccess = [];
    for (const [i, { result: callResult, success, gasUsed }] of result.entries()) {
      if (!success || callResult.length <= 2) {
        results.push({
          success: false,
          returnData: callResult
        });
        continue;
      }
      gasUsedForSuccess.push(Number(gasUsed));
      results.push({
        success: true,
        result: decodeFunctionResult({
          abi,
          functionName: functionNames[i],
          data: callResult
        })
      });
    }
    return {
      blockNumber,
      results,
      approxGasUsedPerSuccessCall: stats.percentile(gasUsedForSuccess, 99)
    };
  }
};
UniswapMulticallProvider.abi = uniswapInterfaceMulticallAbi;
var DEFAULT_EIP_1559_SUPPORTED_CHAINS = [
  ChainId.PULSECHAIN,
  ChainId.PULSECHAIN_TESTNET
];
var OnChainGasPriceProvider = class extends IGasPriceProvider {
  constructor(chainId, eip1559GasPriceProvider, legacyGasPriceProvider, eipChains = DEFAULT_EIP_1559_SUPPORTED_CHAINS) {
    super();
    this.chainId = chainId;
    this.eip1559GasPriceProvider = eip1559GasPriceProvider;
    this.legacyGasPriceProvider = legacyGasPriceProvider;
    this.eipChains = eipChains;
  }
  async getGasPrice() {
    if (this.eipChains.includes(this.chainId)) {
      return this.eip1559GasPriceProvider.getGasPrice();
    }
    return this.legacyGasPriceProvider.getGasPrice();
  }
};

// src/abis/PulseXQuoter.ts
var pulsexQuoterAbi = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "_factoryV1",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "_factoryV2",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "_WETH9",
        "type": "address"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "constructor"
  },
  {
    "inputs": [],
    "name": "WETH9",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "factoryV1",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "factoryV2",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "bytes",
        "name": "path",
        "type": "bytes"
      },
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      }
    ],
    "name": "quoteExactInput",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "tokenIn",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "tokenOut",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "amountIn",
            "type": "uint256"
          }
        ],
        "internalType": "struct IPulseXQuoter.QuoteExactInputSingleParams",
        "name": "params",
        "type": "tuple"
      }
    ],
    "name": "quoteExactInputSingleV1",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "tokenIn",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "tokenOut",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "amountIn",
            "type": "uint256"
          }
        ],
        "internalType": "struct IPulseXQuoter.QuoteExactInputSingleParams",
        "name": "params",
        "type": "tuple"
      }
    ],
    "name": "quoteExactInputSingleV2",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  }
];

// src/abis/IQuoterV2.ts
var quoterV2Abi = [
  {
    "inputs": [
      {
        "internalType": "bytes",
        "name": "path",
        "type": "bytes"
      },
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      }
    ],
    "name": "quoteExactInput",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      },
      {
        "internalType": "uint160[]",
        "name": "sqrtPriceX96AfterList",
        "type": "uint160[]"
      },
      {
        "internalType": "uint32[]",
        "name": "initializedTicksCrossedList",
        "type": "uint32[]"
      },
      {
        "internalType": "uint256",
        "name": "gasEstimate",
        "type": "uint256"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "tokenIn",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "tokenOut",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "amountIn",
            "type": "uint256"
          },
          {
            "internalType": "uint24",
            "name": "fee",
            "type": "uint24"
          },
          {
            "internalType": "uint160",
            "name": "sqrtPriceLimitX96",
            "type": "uint160"
          }
        ],
        "internalType": "struct IQuoterV2.QuoteExactInputSingleParams",
        "name": "params",
        "type": "tuple"
      }
    ],
    "name": "quoteExactInputSingle",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      },
      {
        "internalType": "uint160",
        "name": "sqrtPriceX96After",
        "type": "uint160"
      },
      {
        "internalType": "uint32",
        "name": "initializedTicksCrossed",
        "type": "uint32"
      },
      {
        "internalType": "uint256",
        "name": "gasEstimate",
        "type": "uint256"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "bytes",
        "name": "path",
        "type": "bytes"
      },
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "name": "quoteExactOutput",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      },
      {
        "internalType": "uint160[]",
        "name": "sqrtPriceX96AfterList",
        "type": "uint160[]"
      },
      {
        "internalType": "uint32[]",
        "name": "initializedTicksCrossedList",
        "type": "uint32[]"
      },
      {
        "internalType": "uint256",
        "name": "gasEstimate",
        "type": "uint256"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "tokenIn",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "tokenOut",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "amount",
            "type": "uint256"
          },
          {
            "internalType": "uint24",
            "name": "fee",
            "type": "uint24"
          },
          {
            "internalType": "uint160",
            "name": "sqrtPriceLimitX96",
            "type": "uint160"
          }
        ],
        "internalType": "struct IQuoterV2.QuoteExactOutputSingleParams",
        "name": "params",
        "type": "tuple"
      }
    ],
    "name": "quoteExactOutputSingle",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      },
      {
        "internalType": "uint160",
        "name": "sqrtPriceX96After",
        "type": "uint160"
      },
      {
        "internalType": "uint32",
        "name": "initializedTicksCrossed",
        "type": "uint32"
      },
      {
        "internalType": "uint256",
        "name": "gasEstimate",
        "type": "uint256"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

// src/providers/on-chain-quote-provider.ts
var BlockConflictError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "BlockConflictError";
  }
};
var SuccessRateError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "SuccessRateError";
  }
};
var ProviderBlockHeaderError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "ProviderBlockHeaderError";
  }
};
var ProviderTimeoutError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "ProviderTimeoutError";
  }
};
var ProviderGasError = class extends Error {
  constructor() {
    super(...arguments);
    this.name = "ProviderGasError";
  }
};
var DEFAULT_BATCH_RETRIES = 2;
var OnChainQuoteProvider = class {
  /**
   * Creates an instance of OnChainQuoteProvider.
   *
   * @param chainId The chain to get quotes for.
   * @param provider The web 3 provider.
   * @param multicall2Provider The multicall provider to use to get the quotes on-chain.
   * Only supports the Uniswap Multicall contract as it needs the gas limitting functionality.
   * @param retryOptions The retry options for each call to the multicall.
   * @param batchParams The parameters for each batched call to the multicall.
   * @param gasErrorFailureOverride The gas and chunk parameters to use when retrying a batch that failed due to out of gas.
   * @param successRateFailureOverrides The parameters for retries when we fail to get quotes.
   * @param blockNumberConfig Parameters for adjusting which block we get quotes from, and how to handle block header not found errors.
   * @param [quoterAddressOverride] Overrides the address of the quoter contract to use.
   */
  constructor(chainId, provider, multicall2Provider, retryOptions = {
    retries: DEFAULT_BATCH_RETRIES,
    minTimeout: 25,
    maxTimeout: 250
  }, batchParams = {
    multicallChunk: 150,
    gasLimitPerCall: 1e6,
    quoteMinSuccessRate: 0.2
  }, gasErrorFailureOverride = {
    gasLimitOverride: 15e5,
    multicallChunk: 100
  }, successRateFailureOverrides = {
    gasLimitOverride: 13e5,
    multicallChunk: 110
  }, blockNumberConfig = {
    baseBlockOffset: 0,
    rollback: { enabled: false }
  }, quoterAddressOverride) {
    this.chainId = chainId;
    this.provider = provider;
    this.multicall2Provider = multicall2Provider;
    this.retryOptions = retryOptions;
    this.batchParams = batchParams;
    this.gasErrorFailureOverride = gasErrorFailureOverride;
    this.successRateFailureOverrides = successRateFailureOverrides;
    this.blockNumberConfig = blockNumberConfig;
    this.quoterAddressOverride = quoterAddressOverride;
  }
  getQuoterAddress(useMixedRouteQuoter) {
    if (this.quoterAddressOverride) {
      return this.quoterAddressOverride;
    }
    const quoterAddress = useMixedRouteQuoter ? PULSEX_QUOTER_ADDRESSES[this.chainId] : QUOTER_V2_ADDRESSES[this.chainId];
    if (!quoterAddress) {
      throw new Error(
        `No address for the quoter contract on chain id: ${this.chainId}`
      );
    }
    return quoterAddress;
  }
  async getQuotesManyExactIn(amountIns, routes, providerConfig) {
    return this.getQuotesManyData(
      amountIns,
      routes,
      "quoteExactInput",
      providerConfig
    );
  }
  async getQuotesManyData(amounts, routes, functionName, _providerConfig) {
    const useMixedRouteQuoter = routes.some((route) => route.protocol === "V1" /* V1 */) || routes.some((route) => route.protocol === "V2" /* V2 */) || routes.some((route) => route.protocol === "MIXED" /* MIXED */);
    this.validateRoutes(routes, functionName, useMixedRouteQuoter);
    let multicallChunk = this.batchParams.multicallChunk;
    let gasLimitOverride = this.batchParams.gasLimitPerCall;
    const { baseBlockOffset, rollback } = this.blockNumberConfig;
    const originalBlockNumber = await this.provider.getBlockNumber();
    const providerConfig = {
      ..._providerConfig,
      blockNumber: _providerConfig?.blockNumber ?? originalBlockNumber + BigInt(baseBlockOffset)
    };
    const inputs = _16(routes).flatMap((route) => {
      const encodedRoute = encodeMixedRouteToPath(
        route instanceof V1Route || route instanceof V2Route ? new MixedRouteSDK(route.pairs, route.input, route.output) : route
      );
      const routeInputs = amounts.map((amount) => [
        encodedRoute,
        `0x${amount.quotient.toString(16)}`
      ]);
      return routeInputs;
    }).value();
    const normalizedChunk = Math.ceil(
      inputs.length / Math.ceil(inputs.length / multicallChunk)
    );
    const inputsChunked = _16.chunk(inputs, normalizedChunk);
    let quoteStates = _16.map(inputsChunked, (inputChunk) => {
      return {
        status: "pending",
        inputs: inputChunk
      };
    });
    log.info(
      `About to get ${inputs.length} quotes in chunks of ${normalizedChunk} [${_16.map(
        inputsChunked,
        (i) => i.length
      ).join(",")}] ${gasLimitOverride ? `with a gas limit override of ${gasLimitOverride}` : ""} and block number: ${await providerConfig.blockNumber} [Original before offset: ${originalBlockNumber}].`
    );
    let haveRetriedForSuccessRate = false;
    let haveRetriedForBlockHeader = false;
    let blockHeaderRetryAttemptNumber = 0;
    let haveIncrementedBlockHeaderFailureCounter = false;
    let blockHeaderRolledBack = false;
    let haveRetriedForBlockConflictError = false;
    let haveRetriedForOutOfGas = false;
    let haveRetriedForTimeout = false;
    let haveRetriedForUnknownReason = false;
    let finalAttemptNumber = 1;
    const expectedCallsMade = quoteStates.length;
    let totalCallsMade = 0;
    const {
      results: quoteResults,
      blockNumber,
      approxGasUsedPerSuccessCall
    } = await retry7(
      async (_bail, attemptNumber) => {
        haveIncrementedBlockHeaderFailureCounter = false;
        finalAttemptNumber = attemptNumber;
        const [success, failed, pending] = this.partitionQuotes(quoteStates);
        log.info(
          `Starting attempt: ${attemptNumber}.
          Currently ${success.length} success, ${failed.length} failed, ${pending.length} pending.
          Gas limit override: ${gasLimitOverride} Block number override: ${providerConfig.blockNumber}.`
        );
        quoteStates = await Promise.all(
          _16.map(
            quoteStates,
            async (quoteState, idx) => {
              if (quoteState.status == "success") {
                return quoteState;
              }
              const { inputs: inputs2 } = quoteState;
              try {
                totalCallsMade = totalCallsMade + 1;
                const results = await this.multicall2Provider.callSameFunctionOnContractWithMultipleParams({
                  address: this.getQuoterAddress(useMixedRouteQuoter),
                  abi: useMixedRouteQuoter ? pulsexQuoterAbi : quoterV2Abi,
                  functionName,
                  functionParams: inputs2,
                  providerConfig,
                  additionalConfig: {
                    gasLimitPerCallOverride: gasLimitOverride
                  }
                });
                const successRateError = this.validateSuccessRate(
                  results.results,
                  haveRetriedForSuccessRate
                );
                if (successRateError) {
                  return {
                    status: "failed",
                    inputs: inputs2,
                    reason: successRateError,
                    results
                  };
                }
                return {
                  status: "success",
                  inputs: inputs2,
                  results
                };
              } catch (err) {
                console.log(`Error ${err}`);
                if (err.message.includes("header not found")) {
                  return {
                    status: "failed",
                    inputs: inputs2,
                    reason: new ProviderBlockHeaderError(
                      err.message.slice(0, 500)
                    )
                  };
                }
                if (err.message.includes("timeout")) {
                  return {
                    status: "failed",
                    inputs: inputs2,
                    reason: new ProviderTimeoutError(
                      `Req ${idx}/${quoteStates.length}. Request had ${inputs2.length} inputs. ${err.message.slice(0, 500)}`
                    )
                  };
                }
                if (err.message.includes("out of gas")) {
                  return {
                    status: "failed",
                    inputs: inputs2,
                    reason: new ProviderGasError(err.message.slice(0, 500))
                  };
                }
                return {
                  status: "failed",
                  inputs: inputs2,
                  reason: new Error(
                    `Unknown error from provider: ${err.message.slice(0, 500)}`
                  )
                };
              }
            }
          )
        );
        const [successfulQuoteStates, failedQuoteStates, pendingQuoteStates] = this.partitionQuotes(quoteStates);
        if (pendingQuoteStates.length > 0) {
          throw new Error("Pending quote after waiting for all promises.");
        }
        let retryAll = false;
        const blockNumberError = this.validateBlockNumbers(
          successfulQuoteStates,
          inputsChunked.length,
          gasLimitOverride
        );
        if (blockNumberError) {
          retryAll = true;
        }
        const reasonForFailureStr = _16.map(
          failedQuoteStates,
          (failedQuoteState) => failedQuoteState.reason.name
        ).join(", ");
        if (failedQuoteStates.length > 0) {
          log.info(
            `On attempt ${attemptNumber}: ${failedQuoteStates.length}/${quoteStates.length} quotes failed. Reasons: ${reasonForFailureStr}`
          );
          for (const failedQuoteState of failedQuoteStates) {
            const { reason: error } = failedQuoteState;
            log.info(
              { error },
              `[QuoteFetchError] Attempt ${attemptNumber}. ${error.message}`
            );
            if (error instanceof BlockConflictError) {
              if (!haveRetriedForBlockConflictError) {
                metric.putMetric(
                  "QuoteBlockConflictErrorRetry",
                  1,
                  "Count" /* Count */
                );
                haveRetriedForBlockConflictError = true;
              }
              retryAll = true;
            } else if (error instanceof ProviderBlockHeaderError) {
              if (!haveRetriedForBlockHeader) {
                metric.putMetric(
                  "QuoteBlockHeaderNotFoundRetry",
                  1,
                  "Count" /* Count */
                );
                haveRetriedForBlockHeader = true;
              }
              if (!haveIncrementedBlockHeaderFailureCounter) {
                blockHeaderRetryAttemptNumber = blockHeaderRetryAttemptNumber + 1;
                haveIncrementedBlockHeaderFailureCounter = true;
              }
              if (rollback.enabled) {
                const { rollbackBlockOffset, attemptsBeforeRollback } = rollback;
                if (blockHeaderRetryAttemptNumber >= attemptsBeforeRollback && !blockHeaderRolledBack) {
                  log.info(
                    `Attempt ${attemptNumber}. Have failed due to block header ${blockHeaderRetryAttemptNumber - 1} times. Rolling back block number by ${rollbackBlockOffset} for next retry`
                  );
                  providerConfig.blockNumber = providerConfig.blockNumber ? BigInt(await providerConfig.blockNumber) + BigInt(rollbackBlockOffset) : await this.provider.getBlockNumber() + BigInt(rollbackBlockOffset);
                  retryAll = true;
                  blockHeaderRolledBack = true;
                }
              }
            } else if (error instanceof ProviderTimeoutError) {
              if (!haveRetriedForTimeout) {
                metric.putMetric(
                  "QuoteTimeoutRetry",
                  1,
                  "Count" /* Count */
                );
                haveRetriedForTimeout = true;
              }
            } else if (error instanceof ProviderGasError) {
              if (!haveRetriedForOutOfGas) {
                metric.putMetric(
                  "QuoteOutOfGasExceptionRetry",
                  1,
                  "Count" /* Count */
                );
                haveRetriedForOutOfGas = true;
              }
              gasLimitOverride = this.gasErrorFailureOverride.gasLimitOverride;
              multicallChunk = this.gasErrorFailureOverride.multicallChunk;
              retryAll = true;
            } else if (error instanceof SuccessRateError) {
              if (!haveRetriedForSuccessRate) {
                metric.putMetric(
                  "QuoteSuccessRateRetry",
                  1,
                  "Count" /* Count */
                );
                haveRetriedForSuccessRate = true;
                gasLimitOverride = this.successRateFailureOverrides.gasLimitOverride;
                multicallChunk = this.successRateFailureOverrides.multicallChunk;
                retryAll = true;
              }
            } else {
              if (!haveRetriedForUnknownReason) {
                metric.putMetric(
                  "QuoteUnknownReasonRetry",
                  1,
                  "Count" /* Count */
                );
                haveRetriedForUnknownReason = true;
              }
            }
          }
        }
        if (retryAll) {
          log.info(
            `Attempt ${attemptNumber}. Resetting all requests to pending for next attempt.`
          );
          const normalizedChunk2 = Math.ceil(
            inputs.length / Math.ceil(inputs.length / multicallChunk)
          );
          const inputsChunked2 = _16.chunk(inputs, normalizedChunk2);
          quoteStates = _16.map(inputsChunked2, (inputChunk) => {
            return {
              status: "pending",
              inputs: inputChunk
            };
          });
        }
        const callResults = _16.map(
          successfulQuoteStates,
          (quoteState) => quoteState.results
        );
        return {
          results: _16.flatMap(callResults, (result) => result.results),
          blockNumber: BigInt(callResults[0].blockNumber),
          approxGasUsedPerSuccessCall: stats.percentile(
            _16.map(callResults, (result) => result.approxGasUsedPerSuccessCall),
            100
          )
        };
      },
      {
        retries: DEFAULT_BATCH_RETRIES,
        ...this.retryOptions
      }
    );
    const routesQuotes = this.processQuoteResults(
      quoteResults,
      routes,
      amounts
    );
    metric.putMetric(
      "QuoteApproxGasUsedPerSuccessfulCall",
      approxGasUsedPerSuccessCall,
      "Count" /* Count */
    );
    metric.putMetric(
      "QuoteNumRetryLoops",
      finalAttemptNumber - 1,
      "Count" /* Count */
    );
    metric.putMetric(
      "QuoteTotalCallsToProvider",
      totalCallsMade,
      "Count" /* Count */
    );
    metric.putMetric(
      "QuoteExpectedCallsToProvider",
      expectedCallsMade,
      "Count" /* Count */
    );
    metric.putMetric(
      "QuoteNumRetriedCalls",
      totalCallsMade - expectedCallsMade,
      "Count" /* Count */
    );
    const [successfulQuotes, failedQuotes] = _16(routesQuotes).flatMap((routeWithQuotes) => routeWithQuotes[1]).partition((quote) => quote.quote != null).value();
    log.info(
      `Got ${successfulQuotes.length} successful quotes, ${failedQuotes.length} failed quotes. Took ${finalAttemptNumber - 1} attempt loops. Total calls made to provider: ${totalCallsMade}. Have retried for timeout: ${haveRetriedForTimeout}`
    );
    return { routesWithQuotes: routesQuotes, blockNumber };
  }
  partitionQuotes(quoteStates) {
    const successfulQuoteStates = _16.filter(
      quoteStates,
      (quoteState) => quoteState.status == "success"
    );
    const failedQuoteStates = _16.filter(
      quoteStates,
      (quoteState) => quoteState.status == "failed"
    );
    const pendingQuoteStates = _16.filter(
      quoteStates,
      (quoteState) => quoteState.status == "pending"
    );
    return [successfulQuoteStates, failedQuoteStates, pendingQuoteStates];
  }
  processQuoteResults(quoteResults, routes, amounts) {
    const routesQuotes = [];
    const quotesResultsByRoute = _16.chunk(quoteResults, amounts.length);
    const debugFailedQuotes = [];
    for (let i = 0; i < quotesResultsByRoute.length; i++) {
      const route = routes[i];
      const quoteResults2 = quotesResultsByRoute[i];
      const quotes = _16.map(
        quoteResults2,
        (quoteResult, index) => {
          const amount = amounts[index];
          if (!quoteResult.success) {
            const percent = 100 / amounts.length * (index + 1);
            const amountStr = amount.toFixed(
              Math.min(amount.currency.decimals, 2)
            );
            const routeStr = routeToString(route);
            debugFailedQuotes.push({
              route: routeStr,
              percent,
              amount: amountStr
            });
            return {
              amount,
              quote: null
            };
          }
          return {
            amount,
            quote: quoteResult.result
          };
        }
      );
      routesQuotes.push([route, quotes]);
    }
    const debugChunk = 80;
    _16.forEach(_16.chunk(debugFailedQuotes, debugChunk), (quotes, idx) => {
      const failedQuotesByRoute = _16.groupBy(quotes, (q) => q.route);
      const failedFlat = _16.mapValues(
        failedQuotesByRoute,
        (f) => _16(f).map((f2) => `${f2.percent}%[${f2.amount}]`).join(",")
      );
      log.info(
        {
          failedQuotes: _16.map(
            failedFlat,
            (amounts2, routeStr) => `${routeStr} : ${amounts2}`
          )
        },
        `Failed on chain quotes for routes Part ${idx}/${Math.ceil(
          debugFailedQuotes.length / debugChunk
        )}`
      );
    });
    return routesQuotes;
  }
  validateBlockNumbers(successfulQuoteStates, totalCalls, gasLimitOverride) {
    if (successfulQuoteStates.length <= 1) {
      return null;
    }
    const results = _16.map(
      successfulQuoteStates,
      (quoteState) => quoteState.results
    );
    const blockNumbers = _16.map(results, (result) => result.blockNumber);
    const uniqBlocks = _16(blockNumbers).map((blockNumber) => Number(blockNumber)).uniq().value();
    if (uniqBlocks.length == 1) {
      return null;
    }
    return new BlockConflictError(
      `Quotes returned from different blocks. ${uniqBlocks}. ${totalCalls} calls were made with gas limit ${gasLimitOverride}`
    );
  }
  validateSuccessRate(allResults, haveRetriedForSuccessRate) {
    const numResults = allResults.length;
    const numSuccessResults = allResults.filter(
      (result) => result.success
    ).length;
    const successRate = 1 * numSuccessResults / numResults;
    const { quoteMinSuccessRate } = this.batchParams;
    if (successRate < quoteMinSuccessRate) {
      if (haveRetriedForSuccessRate) {
        log.info(
          `Quote success rate still below threshold despite retry. Continuing. ${quoteMinSuccessRate}: ${successRate}`
        );
        return;
      }
      return new SuccessRateError(
        `Quote success rate below threshold of ${quoteMinSuccessRate}: ${successRate}`
      );
    }
  }
  /**
   * Throw an error for incorrect routes / function combinations
   * @param routes Any combination of V1, V2, and Mixed routes.
   * @param functionName
   * @param useMixedRouteQuoter true if there are ANY V2Routes or MixedRoutes in the routes parameter
   */
  validateRoutes(routes, functionName, useMixedRouteQuoter) {
    if (functionName === "quoteExactOutput" && useMixedRouteQuoter) {
      throw new Error("Cannot call quoteExactOutput with V1/V2 or Mixed routes");
    }
  }
};

// src/providers/static-gas-price-provider.ts
var StaticGasPriceProvider = class {
  constructor(gasPriceWei) {
    this.gasPriceWei = gasPriceWei;
  }
  async getGasPrice() {
    return { gasPriceWei: this.gasPriceWei };
  }
};

// src/abis/PulseXSwapRouter.ts
var pulsexSwapRouterAbi = [
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "_factoryV1",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "factoryV2",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "_WETH9",
        "type": "address"
      }
    ],
    "stateMutability": "nonpayable",
    "type": "constructor"
  },
  {
    "inputs": [],
    "name": "WETH9",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "factoryV1",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "factoryV2",
    "outputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "bytes32",
        "name": "previousBlockhash",
        "type": "bytes32"
      },
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "bytes[]",
        "name": "data",
        "type": "bytes[]"
      }
    ],
    "name": "multicall",
    "outputs": [
      {
        "internalType": "bytes[]",
        "name": "results",
        "type": "bytes[]"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "pull",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [],
    "name": "refundETH",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermit",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "nonce",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "expiry",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermitAllowed",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "nonce",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "expiry",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermitAllowedIfNecessary",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      },
      {
        "internalType": "uint8",
        "name": "v",
        "type": "uint8"
      },
      {
        "internalType": "bytes32",
        "name": "r",
        "type": "bytes32"
      },
      {
        "internalType": "bytes32",
        "name": "s",
        "type": "bytes32"
      }
    ],
    "name": "selfPermitIfNecessary",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountOutMin",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapExactTokensForTokensV1",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountOutMin",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapExactTokensForTokensV2",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountInMax",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapTokensForExactTokensV1",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountOut",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "amountInMax",
        "type": "uint256"
      },
      {
        "internalType": "address[]",
        "name": "path",
        "type": "address[]"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      }
    ],
    "name": "swapTokensForExactTokensV2",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "amountIn",
        "type": "uint256"
      }
    ],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      }
    ],
    "name": "sweepToken",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      }
    ],
    "name": "sweepToken",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "sweepTokenWithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "sweepTokenWithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      }
    ],
    "name": "unwrapWETH9",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      }
    ],
    "name": "unwrapWETH9",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "recipient",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "unwrapWETH9WithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amountMinimum",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "feeBips",
        "type": "uint256"
      },
      {
        "internalType": "address",
        "name": "feeRecipient",
        "type": "address"
      }
    ],
    "name": "unwrapWETH9WithFee",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "value",
        "type": "uint256"
      }
    ],
    "name": "wrapETH",
    "outputs": [],
    "stateMutability": "payable",
    "type": "function"
  },
  {
    "stateMutability": "payable",
    "type": "receive"
  }
];

// src/providers/swap-router-provider.ts
var SwapRouterProvider = class {
  constructor(multicall2Provider, chainId) {
    this.multicall2Provider = multicall2Provider;
    this.chainId = chainId;
  }
  async getApprovalType(tokenInAmount, tokenOutAmount) {
    const functionParams = [
      [
        tokenInAmount.currency.wrapped.address,
        tokenInAmount.quotient.toString()
      ],
      [
        tokenOutAmount.currency.wrapped.address,
        tokenOutAmount.quotient.toString()
      ]
    ];
    const tx = await this.multicall2Provider.callSameFunctionOnContractWithMultipleParams({
      address: PULSEX_SWAP_ROUTER_ADDRESSES[this.chainId],
      abi: pulsexSwapRouterAbi,
      functionName: "getApprovalType",
      functionParams
    });
    if (!tx.results[0]?.success || !tx.results[1]?.success) {
      log.info(
        { results: tx.results },
        "Failed to get approval type from swap router for token in or token out"
      );
      throw new Error(
        "Failed to get approval type from swap router for token in or token out"
      );
    }
    const { result: approvalTokenIn } = tx.results[0];
    const { result: approvalTokenOut } = tx.results[1];
    return {
      approvalTokenIn: approvalTokenIn[0],
      approvalTokenOut: approvalTokenOut[0]
    };
  }
};

// src/abis/Permit2.ts
var permitAbi = [
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "deadline",
        "type": "uint256"
      }
    ],
    "name": "AllowanceExpired",
    "type": "error"
  },
  {
    "inputs": [],
    "name": "ExcessiveInvalidation",
    "type": "error"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "amount",
        "type": "uint256"
      }
    ],
    "name": "InsufficientAllowance",
    "type": "error"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "maxAmount",
        "type": "uint256"
      }
    ],
    "name": "InvalidAmount",
    "type": "error"
  },
  {
    "inputs": [],
    "name": "InvalidContractSignature",
    "type": "error"
  },
  {
    "inputs": [],
    "name": "InvalidNonce",
    "type": "error"
  },
  {
    "inputs": [],
    "name": "InvalidSignature",
    "type": "error"
  },
  {
    "inputs": [],
    "name": "InvalidSignatureLength",
    "type": "error"
  },
  {
    "inputs": [],
    "name": "InvalidSigner",
    "type": "error"
  },
  {
    "inputs": [],
    "name": "LengthMismatch",
    "type": "error"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "signatureDeadline",
        "type": "uint256"
      }
    ],
    "name": "SignatureExpired",
    "type": "error"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint160",
        "name": "amount",
        "type": "uint160"
      },
      {
        "indexed": false,
        "internalType": "uint48",
        "name": "expiration",
        "type": "uint48"
      }
    ],
    "name": "Approval",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "address",
        "name": "spender",
        "type": "address"
      }
    ],
    "name": "Lockdown",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint48",
        "name": "newNonce",
        "type": "uint48"
      },
      {
        "indexed": false,
        "internalType": "uint48",
        "name": "oldNonce",
        "type": "uint48"
      }
    ],
    "name": "NonceInvalidation",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "indexed": true,
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint160",
        "name": "amount",
        "type": "uint160"
      },
      {
        "indexed": false,
        "internalType": "uint48",
        "name": "expiration",
        "type": "uint48"
      },
      {
        "indexed": false,
        "internalType": "uint48",
        "name": "nonce",
        "type": "uint48"
      }
    ],
    "name": "Permit",
    "type": "event"
  },
  {
    "anonymous": false,
    "inputs": [
      {
        "indexed": true,
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "word",
        "type": "uint256"
      },
      {
        "indexed": false,
        "internalType": "uint256",
        "name": "mask",
        "type": "uint256"
      }
    ],
    "name": "UnorderedNonceInvalidation",
    "type": "event"
  },
  {
    "inputs": [],
    "name": "DOMAIN_SEPARATOR",
    "outputs": [
      {
        "internalType": "bytes32",
        "name": "",
        "type": "bytes32"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      }
    ],
    "name": "allowance",
    "outputs": [
      {
        "internalType": "uint160",
        "name": "amount",
        "type": "uint160"
      },
      {
        "internalType": "uint48",
        "name": "expiration",
        "type": "uint48"
      },
      {
        "internalType": "uint48",
        "name": "nonce",
        "type": "uint48"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint160",
        "name": "amount",
        "type": "uint160"
      },
      {
        "internalType": "uint48",
        "name": "expiration",
        "type": "uint48"
      }
    ],
    "name": "approve",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "spender",
        "type": "address"
      },
      {
        "internalType": "uint48",
        "name": "newNonce",
        "type": "uint48"
      }
    ],
    "name": "invalidateNonces",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "uint256",
        "name": "wordPos",
        "type": "uint256"
      },
      {
        "internalType": "uint256",
        "name": "mask",
        "type": "uint256"
      }
    ],
    "name": "invalidateUnorderedNonces",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "token",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "spender",
            "type": "address"
          }
        ],
        "internalType": "struct IAllowanceTransfer.TokenSpenderPair[]",
        "name": "approvals",
        "type": "tuple[]"
      }
    ],
    "name": "lockdown",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "",
        "type": "address"
      },
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "name": "nonceBitmap",
    "outputs": [
      {
        "internalType": "uint256",
        "name": "",
        "type": "uint256"
      }
    ],
    "stateMutability": "view",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "components": [
          {
            "components": [
              {
                "internalType": "address",
                "name": "token",
                "type": "address"
              },
              {
                "internalType": "uint160",
                "name": "amount",
                "type": "uint160"
              },
              {
                "internalType": "uint48",
                "name": "expiration",
                "type": "uint48"
              },
              {
                "internalType": "uint48",
                "name": "nonce",
                "type": "uint48"
              }
            ],
            "internalType": "struct IAllowanceTransfer.PermitDetails[]",
            "name": "details",
            "type": "tuple[]"
          },
          {
            "internalType": "address",
            "name": "spender",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "sigDeadline",
            "type": "uint256"
          }
        ],
        "internalType": "struct IAllowanceTransfer.PermitBatch",
        "name": "permitBatch",
        "type": "tuple"
      },
      {
        "internalType": "bytes",
        "name": "signature",
        "type": "bytes"
      }
    ],
    "name": "permit",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "components": [
          {
            "components": [
              {
                "internalType": "address",
                "name": "token",
                "type": "address"
              },
              {
                "internalType": "uint160",
                "name": "amount",
                "type": "uint160"
              },
              {
                "internalType": "uint48",
                "name": "expiration",
                "type": "uint48"
              },
              {
                "internalType": "uint48",
                "name": "nonce",
                "type": "uint48"
              }
            ],
            "internalType": "struct IAllowanceTransfer.PermitDetails",
            "name": "details",
            "type": "tuple"
          },
          {
            "internalType": "address",
            "name": "spender",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "sigDeadline",
            "type": "uint256"
          }
        ],
        "internalType": "struct IAllowanceTransfer.PermitSingle",
        "name": "permitSingle",
        "type": "tuple"
      },
      {
        "internalType": "bytes",
        "name": "signature",
        "type": "bytes"
      }
    ],
    "name": "permit",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "components": [
              {
                "internalType": "address",
                "name": "token",
                "type": "address"
              },
              {
                "internalType": "uint256",
                "name": "amount",
                "type": "uint256"
              }
            ],
            "internalType": "struct ISignatureTransfer.TokenPermissions",
            "name": "permitted",
            "type": "tuple"
          },
          {
            "internalType": "uint256",
            "name": "nonce",
            "type": "uint256"
          },
          {
            "internalType": "uint256",
            "name": "deadline",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.PermitTransferFrom",
        "name": "permit",
        "type": "tuple"
      },
      {
        "components": [
          {
            "internalType": "address",
            "name": "to",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "requestedAmount",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.SignatureTransferDetails",
        "name": "transferDetails",
        "type": "tuple"
      },
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "internalType": "bytes",
        "name": "signature",
        "type": "bytes"
      }
    ],
    "name": "permitTransferFrom",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "components": [
              {
                "internalType": "address",
                "name": "token",
                "type": "address"
              },
              {
                "internalType": "uint256",
                "name": "amount",
                "type": "uint256"
              }
            ],
            "internalType": "struct ISignatureTransfer.TokenPermissions[]",
            "name": "permitted",
            "type": "tuple[]"
          },
          {
            "internalType": "uint256",
            "name": "nonce",
            "type": "uint256"
          },
          {
            "internalType": "uint256",
            "name": "deadline",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.PermitBatchTransferFrom",
        "name": "permit",
        "type": "tuple"
      },
      {
        "components": [
          {
            "internalType": "address",
            "name": "to",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "requestedAmount",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.SignatureTransferDetails[]",
        "name": "transferDetails",
        "type": "tuple[]"
      },
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "internalType": "bytes",
        "name": "signature",
        "type": "bytes"
      }
    ],
    "name": "permitTransferFrom",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "components": [
              {
                "internalType": "address",
                "name": "token",
                "type": "address"
              },
              {
                "internalType": "uint256",
                "name": "amount",
                "type": "uint256"
              }
            ],
            "internalType": "struct ISignatureTransfer.TokenPermissions",
            "name": "permitted",
            "type": "tuple"
          },
          {
            "internalType": "uint256",
            "name": "nonce",
            "type": "uint256"
          },
          {
            "internalType": "uint256",
            "name": "deadline",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.PermitTransferFrom",
        "name": "permit",
        "type": "tuple"
      },
      {
        "components": [
          {
            "internalType": "address",
            "name": "to",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "requestedAmount",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.SignatureTransferDetails",
        "name": "transferDetails",
        "type": "tuple"
      },
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "internalType": "bytes32",
        "name": "witness",
        "type": "bytes32"
      },
      {
        "internalType": "string",
        "name": "witnessTypeString",
        "type": "string"
      },
      {
        "internalType": "bytes",
        "name": "signature",
        "type": "bytes"
      }
    ],
    "name": "permitWitnessTransferFrom",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "components": [
              {
                "internalType": "address",
                "name": "token",
                "type": "address"
              },
              {
                "internalType": "uint256",
                "name": "amount",
                "type": "uint256"
              }
            ],
            "internalType": "struct ISignatureTransfer.TokenPermissions[]",
            "name": "permitted",
            "type": "tuple[]"
          },
          {
            "internalType": "uint256",
            "name": "nonce",
            "type": "uint256"
          },
          {
            "internalType": "uint256",
            "name": "deadline",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.PermitBatchTransferFrom",
        "name": "permit",
        "type": "tuple"
      },
      {
        "components": [
          {
            "internalType": "address",
            "name": "to",
            "type": "address"
          },
          {
            "internalType": "uint256",
            "name": "requestedAmount",
            "type": "uint256"
          }
        ],
        "internalType": "struct ISignatureTransfer.SignatureTransferDetails[]",
        "name": "transferDetails",
        "type": "tuple[]"
      },
      {
        "internalType": "address",
        "name": "owner",
        "type": "address"
      },
      {
        "internalType": "bytes32",
        "name": "witness",
        "type": "bytes32"
      },
      {
        "internalType": "string",
        "name": "witnessTypeString",
        "type": "string"
      },
      {
        "internalType": "bytes",
        "name": "signature",
        "type": "bytes"
      }
    ],
    "name": "permitWitnessTransferFrom",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "components": [
          {
            "internalType": "address",
            "name": "from",
            "type": "address"
          },
          {
            "internalType": "address",
            "name": "to",
            "type": "address"
          },
          {
            "internalType": "uint160",
            "name": "amount",
            "type": "uint160"
          },
          {
            "internalType": "address",
            "name": "token",
            "type": "address"
          }
        ],
        "internalType": "struct IAllowanceTransfer.AllowanceTransferDetails[]",
        "name": "transferDetails",
        "type": "tuple[]"
      }
    ],
    "name": "transferFrom",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  },
  {
    "inputs": [
      {
        "internalType": "address",
        "name": "from",
        "type": "address"
      },
      {
        "internalType": "address",
        "name": "to",
        "type": "address"
      },
      {
        "internalType": "uint160",
        "name": "amount",
        "type": "uint160"
      },
      {
        "internalType": "address",
        "name": "token",
        "type": "address"
      }
    ],
    "name": "transferFrom",
    "outputs": [],
    "stateMutability": "nonpayable",
    "type": "function"
  }
];

// src/util/callData.ts
var APPROVE_TOKEN_FOR_TRANSFER = "0x095ea7b300000000000000000000000068b3465833fb72a70ecdf485e0e4c7bd8665fc45ffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";

// src/providers/tenderly-simulation-provider.ts
var TENDERLY_BATCH_SIMULATE_API = (tenderlyBaseUrl, tenderlyUser, tenderlyProject) => `${tenderlyBaseUrl}/api/v1/account/${tenderlyUser}/project/${tenderlyProject}/simulate-batch`;
var DEFAULT_ESTIMATE_MULTIPLIER = 1.25;
var FallbackTenderlySimulator = class extends Simulator2 {
  constructor(chainId, provider, tenderlySimulator, ethEstimateGasSimulator) {
    super(provider, chainId);
    this.tenderlySimulator = tenderlySimulator;
    this.ethEstimateGasSimulator = ethEstimateGasSimulator;
  }
  async simulateTransaction(fromAddress, swapOptions, swapRoute, providerConfig) {
    const inputAmount = swapRoute.trade.inputAmount;
    if (inputAmount.currency.isNative || await this.checkTokenApproved(
      fromAddress,
      inputAmount,
      swapOptions,
      this.provider
    )) {
      log.info(
        "Simulating with eth_estimateGas since token is native or approved."
      );
      try {
        const swapRouteWithGasEstimate = await this.ethEstimateGasSimulator.ethEstimateGas(
          fromAddress,
          swapOptions,
          swapRoute
        );
        return swapRouteWithGasEstimate;
      } catch (err) {
        log.info({ err }, "Error simulating using eth_estimateGas");
        return { ...swapRoute, simulationStatus: 1 /* Failed */ };
      }
    }
    try {
      return await this.tenderlySimulator.simulateTransaction(
        fromAddress,
        swapOptions,
        swapRoute,
        providerConfig
      );
    } catch (err) {
      log.info({ err }, "Failed to simulate via Tenderly");
      return { ...swapRoute, simulationStatus: 1 /* Failed */ };
    }
  }
};
var TenderlySimulator = class extends Simulator2 {
  constructor(chainId, tenderlyBaseUrl, tenderlyUser, tenderlyProject, tenderlyAccessKey, v1PoolProvider, v2PoolProvider, provider, overrideEstimateMultiplier) {
    super(provider, chainId);
    this.tenderlyBaseUrl = tenderlyBaseUrl;
    this.tenderlyUser = tenderlyUser;
    this.tenderlyProject = tenderlyProject;
    this.tenderlyAccessKey = tenderlyAccessKey;
    this.v1PoolProvider = v1PoolProvider;
    this.v2PoolProvider = v2PoolProvider;
    this.overrideEstimateMultiplier = overrideEstimateMultiplier ?? {};
  }
  async simulateTransaction(fromAddress, swapOptions, swapRoute, providerConfig) {
    const currencyIn = swapRoute.trade.inputAmount.currency;
    const tokenIn = currencyIn.wrapped;
    const chainId = this.chainId;
    if (!swapRoute.methodParameters) {
      const msg = "No calldata provided to simulate transaction";
      log.info(msg);
      throw new Error(msg);
    }
    const { calldata } = swapRoute.methodParameters;
    log.info(
      {
        calldata: swapRoute.methodParameters.calldata,
        fromAddress,
        chainId,
        tokenInAddress: tokenIn.address,
        router: swapOptions.type
      },
      "Simulating transaction on Tenderly"
    );
    await providerConfig?.blockNumber;
    let estimatedGasUsed;
    const estimateMultiplier = this.overrideEstimateMultiplier[chainId] ?? DEFAULT_ESTIMATE_MULTIPLIER;
    if (swapOptions.type == 0 /* UNIVERSAL_ROUTER */) {
      const approvePermit2Calldata = encodeFunctionData({
        abi: erc20Abi,
        functionName: "approve",
        args: [PERMIT2_ADDRESS, MaxUint256]
      });
      const approveUniversalRouterCallData = encodeFunctionData({
        abi: permitAbi,
        functionName: "approve",
        args: [
          tokenIn.address,
          UNIVERSAL_ROUTER_ADDRESS,
          MAX_UINT160,
          Math.floor((/* @__PURE__ */ new Date()).getTime() / 1e3) + 1e7
        ]
      });
      const approvePermit2 = {
        network_id: chainId,
        gas_estimate: true,
        input: approvePermit2Calldata,
        to: tokenIn.address,
        value: "0",
        from: fromAddress
      };
      const approveUniversalRouter = {
        network_id: chainId,
        gas_estimate: true,
        input: approveUniversalRouterCallData,
        to: PERMIT2_ADDRESS,
        value: "0",
        from: fromAddress
      };
      const swap = {
        network_id: chainId,
        input: calldata,
        gas_estimate: true,
        to: UNIVERSAL_ROUTER_ADDRESS,
        value: currencyIn.isNative ? swapRoute.methodParameters.value : "0",
        from: fromAddress,
        // TODO: This is a Temporary fix given by Tenderly team, remove once resolved on their end.
        block_number: void 0
      };
      const body = {
        simulations: [approvePermit2, approveUniversalRouter, swap],
        gas_estimate: true
      };
      const opts = {
        headers: {
          "X-Access-Key": this.tenderlyAccessKey
        }
      };
      const url = TENDERLY_BATCH_SIMULATE_API(
        this.tenderlyBaseUrl,
        this.tenderlyUser,
        this.tenderlyProject
      );
      const resp = (await axios3.post(url, body, opts)).data;
      if (!resp || resp.simulation_results.length < 3 || !resp.simulation_results[2].transaction || resp.simulation_results[2].transaction.error_message) {
        this.logTenderlyErrorResponse(resp);
        return { ...swapRoute, simulationStatus: 1 /* Failed */ };
      }
      estimatedGasUsed = BigInt(
        (resp.simulation_results[2].transaction.gas_used * estimateMultiplier).toFixed(0)
      );
      log.info(
        {
          body,
          approvePermit2GasUsed: resp.simulation_results[0].transaction.gas_used,
          approveUniversalRouterGasUsed: resp.simulation_results[1].transaction.gas_used,
          swapGasUsed: resp.simulation_results[2].transaction.gas_used,
          swapWithMultiplier: estimatedGasUsed.toString()
        },
        "Successfully Simulated Approvals + Swap via Tenderly for Universal Router. Gas used."
      );
      log.info(
        { swapTransaction: resp.simulation_results[2].transaction },
        "Successful Tenderly Swap Transaction for Universal Router"
      );
      log.info(
        { swapSimulation: resp.simulation_results[2].simulation },
        "Successful Tenderly Swap Simulation for Universal Router"
      );
    } else if (swapOptions.type == 1 /* SWAP_ROUTER_02 */) {
      const approve = {
        network_id: chainId,
        input: APPROVE_TOKEN_FOR_TRANSFER,
        gas_estimate: true,
        to: tokenIn.address,
        value: "0",
        from: fromAddress
      };
      const swap = {
        network_id: chainId,
        input: calldata,
        to: PULSEX_SWAP_ROUTER_ADDRESSES[chainId],
        gas_estimate: true,
        value: currencyIn.isNative ? swapRoute.methodParameters.value : "0",
        from: fromAddress,
        // TODO: This is a Temporary fix given by Tenderly team, remove once resolved on their end.
        block_number: void 0
      };
      const body = { simulations: [approve, swap] };
      const opts = {
        headers: {
          "X-Access-Key": this.tenderlyAccessKey
        }
      };
      const url = TENDERLY_BATCH_SIMULATE_API(
        this.tenderlyBaseUrl,
        this.tenderlyUser,
        this.tenderlyProject
      );
      const resp = (await axios3.post(url, body, opts)).data;
      if (!resp || resp.simulation_results.length < 2 || !resp.simulation_results[1].transaction || resp.simulation_results[1].transaction.error_message) {
        const msg = `Failed to Simulate Via Tenderly!: ${resp.simulation_results[1].transaction.error_message}`;
        log.info(
          { err: resp.simulation_results[1].transaction.error_message },
          msg
        );
        return { ...swapRoute, simulationStatus: 1 /* Failed */ };
      }
      estimatedGasUsed = BigInt(
        (resp.simulation_results[1].transaction.gas_used * estimateMultiplier).toFixed(0)
      );
      log.info(
        {
          body,
          approveGasUsed: resp.simulation_results[0].transaction.gas_used,
          swapGasUsed: resp.simulation_results[1].transaction.gas_used,
          swapWithMultiplier: estimatedGasUsed.toString()
        },
        "Successfully Simulated Approval + Swap via Tenderly for SwapRouter02. Gas used."
      );
      log.info(
        { swapTransaction: resp.simulation_results[1].transaction },
        "Successful Tenderly Swap Transaction for SwapRouter02"
      );
      log.info(
        { swapSimulation: resp.simulation_results[1].simulation },
        "Successful Tenderly Swap Simulation for SwapRouter02"
      );
    } else {
      throw new Error(`Unsupported swap type: ${swapOptions}`);
    }
    const {
      estimatedGasUsedUSD,
      estimatedGasUsedQuoteToken,
      quoteGasAdjusted
    } = await calculateGasUsed(
      chainId,
      swapRoute,
      estimatedGasUsed,
      this.v1PoolProvider,
      this.v2PoolProvider
    );
    return {
      ...initSwapRouteFromExisting(
        swapRoute,
        this.v1PoolProvider,
        this.v2PoolProvider,
        quoteGasAdjusted,
        estimatedGasUsed,
        estimatedGasUsedQuoteToken,
        estimatedGasUsedUSD
      ),
      simulationStatus: 2 /* Succeeded */
    };
  }
  logTenderlyErrorResponse(resp) {
    log.info(
      {
        resp
      },
      "Failed to Simulate on Tenderly"
    );
    log.info(
      {
        err: resp.simulation_results.length >= 1 ? resp.simulation_results[0].transaction : {}
      },
      "Failed to Simulate on Tenderly #1 Transaction"
    );
    log.info(
      {
        err: resp.simulation_results.length >= 1 ? resp.simulation_results[0].simulation : {}
      },
      "Failed to Simulate on Tenderly #1 Simulation"
    );
    log.info(
      {
        err: resp.simulation_results.length >= 2 ? resp.simulation_results[1].transaction : {}
      },
      "Failed to Simulate on Tenderly #2 Transaction"
    );
    log.info(
      {
        err: resp.simulation_results.length >= 2 ? resp.simulation_results[1].simulation : {}
      },
      "Failed to Simulate on Tenderly #2 Simulation"
    );
    log.info(
      {
        err: resp.simulation_results.length >= 3 ? resp.simulation_results[2].transaction : {}
      },
      "Failed to Simulate on Tenderly #3 Transaction"
    );
    log.info(
      {
        err: resp.simulation_results.length >= 3 ? resp.simulation_results[2].simulation : {}
      },
      "Failed to Simulate on Tenderly #3 Simulation"
    );
  }
};
var URISubgraphProvider = class {
  constructor(chainId, uri, timeout = 6e3, retries = 2) {
    this.chainId = chainId;
    this.uri = uri;
    this.timeout = timeout;
    this.retries = retries;
  }
  async getPools() {
    log.info(
      { uri: this.uri },
      `About to get subgraph pools from URI ${this.uri}`
    );
    let allPools = [];
    await retry7(
      async () => {
        const timeout = new Timeout();
        const timerPromise = timeout.set(this.timeout).then(() => {
          throw new Error(
            `Timed out getting pools from subgraph: ${this.timeout}`
          );
        });
        let response;
        try {
          response = await Promise.race([axios3.get(this.uri), timerPromise]);
        } catch (err) {
          throw err;
        } finally {
          timeout.clear();
        }
        const { data: poolsBuffer, status } = response;
        if (status != 200) {
          log.error({ response }, `Unabled to get pools from ${this.uri}.`);
          throw new Error(`Unable to get pools from ${this.uri}`);
        }
        const pools = poolsBuffer;
        log.info(
          { uri: this.uri, chain: this.chainId },
          `Got subgraph pools from uri. Num: ${pools.length}`
        );
        allPools = pools;
      },
      {
        retries: this.retries,
        onRetry: (err, retry9) => {
          log.info(
            { err },
            `Failed to get pools from uri ${this.uri}. Retry attempt: ${retry9}`
          );
        }
      }
    );
    return allPools;
  }
};

// src/providers/caching/route/model/cached-route.ts
var CachedRoute = class {
  /**
   * @param route
   * @param percent
   */
  constructor({ route, percent }) {
    this.route = route;
    this.percent = percent;
  }
  get protocol() {
    return this.route.protocol;
  }
  get tokenIn() {
    return this.route.input;
  }
  get tokenOut() {
    return this.route.output;
  }
};
var CachedRoutes = class {
  /**
   * @param routes
   * @param chainId
   * @param tokenIn
   * @param tokenOut
   * @param protocolsCovered
   * @param blockNumber
   * @param tradeType
   * @param originalAmount
   * @param blocksToLive
   */
  constructor({
    routes,
    chainId,
    tokenIn,
    tokenOut,
    protocolsCovered,
    blockNumber,
    tradeType,
    originalAmount,
    blocksToLive = 0
  }) {
    this.routes = routes;
    this.chainId = chainId;
    this.tokenIn = tokenIn;
    this.tokenOut = tokenOut;
    this.protocolsCovered = protocolsCovered;
    this.blockNumber = blockNumber;
    this.tradeType = tradeType;
    this.originalAmount = originalAmount;
    this.blocksToLive = blocksToLive;
  }
  /**
   * Factory method that creates a `CachedRoutes` object from an array of RouteWithValidQuote.
   *
   * @public
   * @static
   * @param routes
   * @param chainId
   * @param tokenIn
   * @param tokenOut
   * @param protocolsCovered
   * @param blockNumber
   * @param tradeType
   * @param originalAmount
   */
  static fromRoutesWithValidQuotes(routes, chainId, tokenIn, tokenOut, protocolsCovered, blockNumber, tradeType, originalAmount) {
    if (routes.length == 0)
      return void 0;
    const cachedRoutes = _16.map(
      routes,
      (route) => new CachedRoute({ route: route.route, percent: route.percent })
    );
    return new CachedRoutes({
      routes: cachedRoutes,
      chainId,
      tokenIn,
      tokenOut,
      protocolsCovered,
      blockNumber,
      tradeType,
      originalAmount
    });
  }
  /**
   * Function to determine if, given a block number, the CachedRoute is expired or not.
   *
   * @param currentBlockNumber
   * @param optimistic
   */
  notExpired(currentBlockNumber, optimistic = false) {
    const blocksToLive = optimistic ? this.blocksToLive : 0;
    const blocksDifference = currentBlockNumber - this.blockNumber;
    return blocksDifference <= BigInt(blocksToLive);
  }
};

// src/providers/caching/route/model/cache-mode.ts
var CacheMode = /* @__PURE__ */ ((CacheMode2) => {
  CacheMode2["Livemode"] = "livemode";
  CacheMode2["Darkmode"] = "darkmode";
  CacheMode2["Tapcompare"] = "tapcompare";
  return CacheMode2;
})(CacheMode || {});

// src/providers/caching/route/route-caching-provider.ts
var IRouteCachingProvider2 = class {
  constructor() {
    /**
     * Final implementation of the public `getCachedRoute` method, this is how code will interact with the implementation
     *
     * @public
     * @readonly
     * @param chainId
     * @param amount
     * @param quoteToken
     * @param tradeType
     * @param protocols
     * @param blockNumber
     */
    this.getCachedRoute = async (chainId, amount, quoteToken, tradeType, protocols, blockNumber, optimistic = false) => {
      if (await this.getCacheMode(chainId, amount, quoteToken, tradeType, protocols) == "darkmode" /* Darkmode */) {
        return void 0;
      }
      const cachedRoute = await this._getCachedRoute(
        chainId,
        amount,
        quoteToken,
        tradeType,
        protocols,
        blockNumber,
        optimistic
      );
      return this.filterExpiredCachedRoutes(cachedRoute, blockNumber, optimistic);
    };
    /**
     * Final implementation of the public `setCachedRoute` method.
     * This method will set the blockToLive in the CachedRoutes object before calling the internal method to insert in cache.
     *
     * @public
     * @readonly
     * @param cachedRoutes The route to cache.
     * @returns Promise<boolean> Indicates if the route was inserted into cache.
     */
    this.setCachedRoute = async (cachedRoutes, amount) => {
      if (await this.getCacheModeFromCachedRoutes(cachedRoutes, amount) == "darkmode" /* Darkmode */) {
        return false;
      }
      cachedRoutes.blocksToLive = await this._getBlocksToLive(cachedRoutes, amount);
      return this._setCachedRoute(cachedRoutes, amount);
    };
  }
  /**
   * Returns the CacheMode for the given cachedRoutes and amount
   *
   * @param cachedRoutes
   * @param amount
   */
  getCacheModeFromCachedRoutes(cachedRoutes, amount) {
    const quoteToken = cachedRoutes.tradeType == TradeType.EXACT_INPUT ? cachedRoutes.tokenOut : cachedRoutes.tokenIn;
    return this.getCacheMode(
      cachedRoutes.chainId,
      amount,
      quoteToken,
      cachedRoutes.tradeType,
      cachedRoutes.protocolsCovered
    );
  }
  filterExpiredCachedRoutes(cachedRoutes, blockNumber, optimistic) {
    return cachedRoutes?.notExpired(blockNumber, optimistic) ? cachedRoutes : void 0;
  }
};
var CachingV1PoolProvider = class {
  /**
   * Creates an instance of CachingV3PoolProvider.
   * @param chainId The chain id to use.
   * @param poolProvider The provider to use to get the pools when not in the cache.
   * @param cache Cache instance to hold cached pools.
   */
  constructor(chainId, poolProvider, cache) {
    this.chainId = chainId;
    this.poolProvider = poolProvider;
    this.cache = cache;
    this.POOL_KEY = (chainId, address) => `pool-${chainId}-${address}`;
  }
  async getPools(tokenPairs, providerConfig) {
    const poolAddressSet = /* @__PURE__ */ new Set();
    const poolsToGetTokenPairs = [];
    const poolsToGetAddresses = [];
    const poolAddressToPool = {};
    const blockNumber = await providerConfig?.blockNumber;
    for (const [tokenA, tokenB] of tokenPairs) {
      const { poolAddress, token0, token1 } = this.getPoolAddress(
        tokenA,
        tokenB
      );
      if (poolAddressSet.has(poolAddress)) {
        continue;
      }
      poolAddressSet.add(poolAddress);
      const cachedPool = await this.cache.get(
        this.POOL_KEY(this.chainId, poolAddress)
      );
      if (cachedPool) {
        if (!blockNumber || blockNumber && cachedPool.block == blockNumber) {
          poolAddressToPool[poolAddress] = cachedPool.pair;
          continue;
        }
      }
      poolsToGetTokenPairs.push([token0, token1]);
      poolsToGetAddresses.push(poolAddress);
    }
    log.info(
      {
        poolsFound: _16.map(
          Object.values(poolAddressToPool),
          (p) => p.token0.symbol + " " + p.token1.symbol
        ),
        poolsToGetTokenPairs: _16.map(
          poolsToGetTokenPairs,
          (t) => t[0].symbol + " " + t[1].symbol
        )
      },
      `Found ${Object.keys(poolAddressToPool).length} V1 pools already in local cache for block ${blockNumber}. About to get reserves for ${poolsToGetTokenPairs.length} pools.`
    );
    if (poolsToGetAddresses.length > 0) {
      const poolAccessor = await this.poolProvider.getPools(
        poolsToGetTokenPairs,
        providerConfig
      );
      for (const address of poolsToGetAddresses) {
        const pool = poolAccessor.getPoolByAddress(address);
        if (pool) {
          poolAddressToPool[address] = pool;
          await this.cache.set(this.POOL_KEY(this.chainId, address), {
            pair: pool,
            block: blockNumber
          });
        }
      }
    }
    return {
      getPool: (tokenA, tokenB) => {
        const { poolAddress } = this.getPoolAddress(tokenA, tokenB);
        return poolAddressToPool[poolAddress];
      },
      getPoolByAddress: (address) => poolAddressToPool[address],
      getAllPools: () => Object.values(poolAddressToPool)
    };
  }
  getPoolAddress(tokenA, tokenB) {
    return this.poolProvider.getPoolAddress(tokenA, tokenB);
  }
};

// src/providers/v1/caching-subgraph-provider.ts
var CachingV1SubgraphProvider2 = class {
  /**
   * Creates an instance of CachingV1SubgraphProvider.
   * @param chainId The chain id to use.
   * @param subgraphProvider The provider to use to get the subgraph pools when not in the cache.
   * @param cache Cache instance to hold cached pools.
   */
  constructor(chainId, subgraphProvider, cache) {
    this.chainId = chainId;
    this.subgraphProvider = subgraphProvider;
    this.cache = cache;
    this.SUBGRAPH_KEY = (chainId) => `subgraph-pools-v1-${chainId}`;
  }
  async getPools() {
    const cachedPools = await this.cache.get(this.SUBGRAPH_KEY(this.chainId));
    if (cachedPools) {
      return cachedPools;
    }
    const pools = await this.subgraphProvider.getPools();
    await this.cache.set(this.SUBGRAPH_KEY(this.chainId), pools);
    return pools;
  }
};
var V1QuoteProvider = class {
  /* eslint-disable @typescript-eslint/no-empty-function */
  constructor() {
  }
  /* eslint-enable @typescript-eslint/no-empty-function */
  async getQuotesManyExactIn(amountIns, routes) {
    return this.getQuotes(amountIns, routes, TradeType.EXACT_INPUT);
  }
  async getQuotesManyExactOut(amountOuts, routes) {
    return this.getQuotes(amountOuts, routes, TradeType.EXACT_OUTPUT);
  }
  async getQuotes(amounts, routes, tradeType) {
    const routesWithQuotes = [];
    const debugStrs = [];
    for (const route of routes) {
      const amountQuotes = [];
      let insufficientInputAmountErrorCount = 0;
      let insufficientReservesErrorCount = 0;
      for (const amount of amounts) {
        try {
          if (tradeType == TradeType.EXACT_INPUT) {
            let outputAmount = amount.wrapped;
            for (const pair of route.pairs) {
              const [outputAmountNew] = pair.getOutputAmount(outputAmount);
              outputAmount = outputAmountNew;
            }
            amountQuotes.push({
              amount,
              quote: outputAmount.quotient
            });
          } else {
            let inputAmount = amount.wrapped;
            for (let i = route.pairs.length - 1; i >= 0; i--) {
              const pair = route.pairs[i];
              [inputAmount] = pair.getInputAmount(inputAmount);
            }
            amountQuotes.push({
              amount,
              quote: inputAmount.quotient
            });
          }
        } catch (err) {
          if (err instanceof InsufficientInputAmountError) {
            insufficientInputAmountErrorCount = insufficientInputAmountErrorCount + 1;
            amountQuotes.push({ amount, quote: null });
          } else if (err instanceof InsufficientReservesError) {
            insufficientReservesErrorCount = insufficientReservesErrorCount + 1;
            amountQuotes.push({ amount, quote: null });
          } else {
            throw err;
          }
        }
      }
      if (insufficientInputAmountErrorCount > 0 || insufficientReservesErrorCount > 0) {
        debugStrs.push(
          `${[
            routeToString(route)
          ]} Input: ${insufficientInputAmountErrorCount} Reserves: ${insufficientReservesErrorCount} }`
        );
      }
      routesWithQuotes.push([route, amountQuotes]);
    }
    if (debugStrs.length > 0) {
      log.info({ debugStrs }, `Failed quotes for V1 routes`);
    }
    return {
      routesWithQuotes
    };
  }
};
var BASES_TO_CHECK_TRADES_AGAINST = {
  [ChainId.PULSECHAIN]: [
    WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN],
    eUSDC_PULSECHAIN,
    eUSDT_PULSECHAIN,
    eWBTC_PULSECHAIN,
    eDAI_PULSECHAIN,
    PLSX_PULSECHAIN,
    eHEX_PULSECHAIN,
    HEX_PULSECHAIN,
    INC_PULSECHAIN,
    WETH_PULSECHAIN
  ],
  [ChainId.PULSECHAIN_TESTNET]: [
    WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN_TESTNET],
    PLSX_TESTNET,
    INC_TESTNET,
    HEX_TESTNET,
    tDAI_TESTNET
  ]
};
var StaticV1SubgraphProvider = class {
  constructor(chainId) {
    this.chainId = chainId;
  }
  async getPools(tokenIn, tokenOut) {
    log.info("In static subgraph provider for V1");
    const bases = BASES_TO_CHECK_TRADES_AGAINST[this.chainId];
    const basePairs = _16.flatMap(
      bases,
      (base) => bases.map((otherBase) => [base, otherBase])
    );
    if (tokenIn && tokenOut) {
      basePairs.push(
        [tokenIn, tokenOut],
        ...bases.map((base) => [tokenIn, base]),
        ...bases.map((base) => [tokenOut, base])
      );
    }
    const pairs = _16(basePairs).filter(
      (tokens) => Boolean(tokens[0] && tokens[1])
    ).filter(
      ([tokenA, tokenB]) => tokenA.address !== tokenB.address && !tokenA.equals(tokenB)
    ).value();
    const poolAddressSet = /* @__PURE__ */ new Set();
    const subgraphPools = _16(pairs).map(([tokenA, tokenB]) => {
      const poolAddress = PairV1.getAddress(tokenA, tokenB);
      if (poolAddressSet.has(poolAddress)) {
        return void 0;
      }
      poolAddressSet.add(poolAddress);
      const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA];
      return {
        id: poolAddress,
        liquidity: "100",
        token0: {
          id: token0.address
        },
        token1: {
          id: token1.address
        },
        supply: 100,
        reserve: 100,
        reserveUSD: 100
      };
    }).compact().value();
    return subgraphPools;
  }
};
var SUBGRAPH_URL_BY_CHAIN = {
  [ChainId.PULSECHAIN]: "https://graph.pulsechain.com/subgraphs/name/pulsechain/pulsex",
  [ChainId.PULSECHAIN_TESTNET]: "https://graph.v4.testnet.pulsechain.com/subgraphs/name/pulsechain/pulsex"
};
var threshold = 0.025;
var PAGE_SIZE = 1e3;
var V1SubgraphProvider2 = class {
  constructor(chainId, retries = 2, timeout = 36e4, rollback = true, pageSize = PAGE_SIZE) {
    this.chainId = chainId;
    this.retries = retries;
    this.timeout = timeout;
    this.rollback = rollback;
    this.pageSize = pageSize;
    const subgraphUrl = SUBGRAPH_URL_BY_CHAIN[this.chainId];
    if (!subgraphUrl) {
      throw new Error(`No subgraph url for chain id: ${this.chainId}`);
    }
    this.client = new GraphQLClient(subgraphUrl, { fetch });
  }
  async getPools(_tokenIn, _tokenOut, providerConfig) {
    let blockNumber = providerConfig?.blockNumber ? await providerConfig.blockNumber : void 0;
    const query2 = gql`
      query getPools($pageSize: Int!, $id: String) {
        pairs(
          first: $pageSize
          ${blockNumber ? `block: { number: ${blockNumber} }` : ``}
          where: { id_gt: $id }
        ) {
          id
          token0 { id, symbol }
          token1 { id, symbol }
          totalSupply
          trackedReservePLS
          reserveUSD
        }
      }
    `;
    let pools = [];
    log.info(
      `Getting V1 pools from the subgraph with page size ${this.pageSize}${providerConfig?.blockNumber ? ` as of block ${providerConfig?.blockNumber}` : ""}.`
    );
    await retry7(
      async () => {
        const timeout = new Timeout();
        const getPools = async () => {
          let lastId = "";
          let pairs = [];
          let pairsPage = [];
          do {
            await retry7(
              async () => {
                const poolsResult = await this.client.request(query2, {
                  pageSize: this.pageSize,
                  id: lastId
                });
                pairsPage = poolsResult.pairs;
                pairs = pairs.concat(pairsPage);
                lastId = pairs[pairs.length - 1].id;
              },
              {
                retries: this.retries,
                onRetry: (err, retry9) => {
                  pools = [];
                  log.info(
                    { err },
                    `Failed request for page of pools from subgraph. Retry attempt: ${retry9}`
                  );
                }
              }
            );
          } while (pairsPage.length > 0);
          return pairs;
        };
        try {
          const getPoolsPromise = getPools();
          const timerPromise = timeout.set(this.timeout).then(() => {
            throw new Error(
              `Timed out getting pools from subgraph: ${this.timeout}`
            );
          });
          pools = await Promise.race([getPoolsPromise, timerPromise]);
          return;
        } catch (err) {
          throw err;
        } finally {
          timeout.clear();
        }
      },
      {
        retries: this.retries,
        onRetry: (err, retry9) => {
          if (this.rollback && blockNumber && _16.includes(err.message, "indexed up to")) {
            blockNumber = BigInt(blockNumber) - 10n;
            log.info(
              `Detected subgraph indexing error. Rolled back block number to: ${blockNumber}`
            );
          }
          pools = [];
          log.info(
            { err },
            `Failed to get pools from subgraph. Retry attempt: ${retry9}`
          );
        }
      }
    );
    const poolsSanitized = pools.filter((pool) => {
      return parseFloat(pool.trackedReservePLS) > threshold;
    }).map((pool) => {
      return {
        id: pool.id.toLowerCase(),
        token0: {
          id: pool.token0.id.toLowerCase()
        },
        token1: {
          id: pool.token1.id.toLowerCase()
        },
        supply: parseFloat(pool.totalSupply),
        reserve: parseFloat(pool.trackedReservePLS),
        reserveUSD: parseFloat(pool.reserveUSD)
      };
    });
    log.info(
      `Got ${pools.length} V1 pools from the subgraph. ${poolsSanitized.length} after filtering`
    );
    return poolsSanitized;
  }
};

// src/providers/v1/subgraph-provider-with-fallback.ts
var V1SubgraphProviderWithFallBacks = class {
  /**
   * Creates an instance of V1SubgraphProviderWithFallBacks.
   * @param fallbacks Ordered list of `IV1SubgraphProvider` to try to get pools from.
   */
  constructor(fallbacks) {
    this.fallbacks = fallbacks;
  }
  async getPools(tokenIn, tokenOut, providerConfig) {
    for (let i = 0; i < this.fallbacks.length; i++) {
      const provider = this.fallbacks[i];
      try {
        const pools = await provider.getPools(
          tokenIn,
          tokenOut,
          providerConfig
        );
        return pools;
      } catch (err) {
        log.info(`Failed to get subgraph pools for V1 from fallback #${i}`);
      }
    }
    throw new Error("Failed to get subgraph pools from any providers");
  }
};

// src/providers/v1/uri-subgraph-provider.ts
var V1URISubgraphProvider = class extends URISubgraphProvider {
};
var CachingV2PoolProvider = class {
  /**
   * Creates an instance of CachingV3PoolProvider.
   * @param chainId The chain id to use.
   * @param poolProvider The provider to use to get the pools when not in the cache.
   * @param cache Cache instance to hold cached pools.
   */
  constructor(chainId, poolProvider, cache) {
    this.chainId = chainId;
    this.poolProvider = poolProvider;
    this.cache = cache;
    this.POOL_KEY = (chainId, address) => `pool-${chainId}-${address}`;
  }
  async getPools(tokenPairs, providerConfig) {
    const poolAddressSet = /* @__PURE__ */ new Set();
    const poolsToGetTokenPairs = [];
    const poolsToGetAddresses = [];
    const poolAddressToPool = {};
    const blockNumber = await providerConfig?.blockNumber;
    for (const [tokenA, tokenB] of tokenPairs) {
      const { poolAddress, token0, token1 } = this.getPoolAddress(
        tokenA,
        tokenB
      );
      if (poolAddressSet.has(poolAddress)) {
        continue;
      }
      poolAddressSet.add(poolAddress);
      const cachedPool = await this.cache.get(
        this.POOL_KEY(this.chainId, poolAddress)
      );
      if (cachedPool) {
        if (!blockNumber || blockNumber && cachedPool.block == blockNumber) {
          poolAddressToPool[poolAddress] = cachedPool.pair;
          continue;
        }
      }
      poolsToGetTokenPairs.push([token0, token1]);
      poolsToGetAddresses.push(poolAddress);
    }
    log.info(
      {
        poolsFound: _16.map(
          Object.values(poolAddressToPool),
          (p) => p.token0.symbol + " " + p.token1.symbol
        ),
        poolsToGetTokenPairs: _16.map(
          poolsToGetTokenPairs,
          (t) => t[0].symbol + " " + t[1].symbol
        )
      },
      `Found ${Object.keys(poolAddressToPool).length} V2 pools already in local cache for block ${blockNumber}. About to get reserves for ${poolsToGetTokenPairs.length} pools.`
    );
    if (poolsToGetAddresses.length > 0) {
      const poolAccessor = await this.poolProvider.getPools(
        poolsToGetTokenPairs,
        providerConfig
      );
      for (const address of poolsToGetAddresses) {
        const pool = poolAccessor.getPoolByAddress(address);
        if (pool) {
          poolAddressToPool[address] = pool;
          await this.cache.set(this.POOL_KEY(this.chainId, address), {
            pair: pool,
            block: blockNumber
          });
        }
      }
    }
    return {
      getPool: (tokenA, tokenB) => {
        const { poolAddress } = this.getPoolAddress(tokenA, tokenB);
        return poolAddressToPool[poolAddress];
      },
      getPoolByAddress: (address) => poolAddressToPool[address],
      getAllPools: () => Object.values(poolAddressToPool)
    };
  }
  getPoolAddress(tokenA, tokenB) {
    return this.poolProvider.getPoolAddress(tokenA, tokenB);
  }
};

// src/providers/v2/caching-subgraph-provider.ts
var CachingV2SubgraphProvider2 = class {
  /**
   * Creates an instance of CachingV2SubgraphProvider.
   * @param chainId The chain id to use.
   * @param subgraphProvider The provider to use to get the subgraph pools when not in the cache.
   * @param cache Cache instance to hold cached pools.
   */
  constructor(chainId, subgraphProvider, cache) {
    this.chainId = chainId;
    this.subgraphProvider = subgraphProvider;
    this.cache = cache;
    this.SUBGRAPH_KEY = (chainId) => `subgraph-pools-v2-${chainId}`;
  }
  async getPools() {
    const cachedPools = await this.cache.get(this.SUBGRAPH_KEY(this.chainId));
    if (cachedPools) {
      return cachedPools;
    }
    const pools = await this.subgraphProvider.getPools();
    await this.cache.set(this.SUBGRAPH_KEY(this.chainId), pools);
    return pools;
  }
};
var V2QuoteProvider = class {
  /* eslint-disable @typescript-eslint/no-empty-function */
  constructor() {
  }
  /* eslint-enable @typescript-eslint/no-empty-function */
  async getQuotesManyExactIn(amountIns, routes) {
    return this.getQuotes(amountIns, routes, TradeType.EXACT_INPUT);
  }
  async getQuotesManyExactOut(amountOuts, routes) {
    return this.getQuotes(amountOuts, routes, TradeType.EXACT_OUTPUT);
  }
  async getQuotes(amounts, routes, tradeType) {
    const routesWithQuotes = [];
    const debugStrs = [];
    for (const route of routes) {
      const amountQuotes = [];
      let insufficientInputAmountErrorCount = 0;
      let insufficientReservesErrorCount = 0;
      for (const amount of amounts) {
        try {
          if (tradeType == TradeType.EXACT_INPUT) {
            let outputAmount = amount.wrapped;
            for (const pair of route.pairs) {
              const [outputAmountNew] = pair.getOutputAmount(outputAmount);
              outputAmount = outputAmountNew;
            }
            amountQuotes.push({
              amount,
              quote: outputAmount.quotient
            });
          } else {
            let inputAmount = amount.wrapped;
            for (let i = route.pairs.length - 1; i >= 0; i--) {
              const pair = route.pairs[i];
              [inputAmount] = pair.getInputAmount(inputAmount);
            }
            amountQuotes.push({
              amount,
              quote: inputAmount.quotient
            });
          }
        } catch (err) {
          if (err instanceof InsufficientInputAmountError) {
            insufficientInputAmountErrorCount = insufficientInputAmountErrorCount + 1;
            amountQuotes.push({ amount, quote: null });
          } else if (err instanceof InsufficientReservesError) {
            insufficientReservesErrorCount = insufficientReservesErrorCount + 1;
            amountQuotes.push({ amount, quote: null });
          } else {
            throw err;
          }
        }
      }
      if (insufficientInputAmountErrorCount > 0 || insufficientReservesErrorCount > 0) {
        debugStrs.push(
          `${[
            routeToString(route)
          ]} Input: ${insufficientInputAmountErrorCount} Reserves: ${insufficientReservesErrorCount} }`
        );
      }
      routesWithQuotes.push([route, amountQuotes]);
    }
    if (debugStrs.length > 0) {
      log.info({ debugStrs }, `Failed quotes for V2 routes`);
    }
    return {
      routesWithQuotes
    };
  }
};
var BASES_TO_CHECK_TRADES_AGAINST2 = {
  [ChainId.PULSECHAIN]: [
    WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN],
    eUSDC_PULSECHAIN,
    eUSDT_PULSECHAIN,
    eWBTC_PULSECHAIN,
    eDAI_PULSECHAIN,
    PLSX_PULSECHAIN,
    eHEX_PULSECHAIN,
    HEX_PULSECHAIN,
    INC_PULSECHAIN,
    WETH_PULSECHAIN
  ],
  [ChainId.PULSECHAIN_TESTNET]: [
    WRAPPED_NATIVE_CURRENCY[ChainId.PULSECHAIN_TESTNET],
    PLSX_TESTNET,
    INC_TESTNET,
    HEX_TESTNET,
    tDAI_TESTNET
  ]
};
var StaticV2SubgraphProvider = class {
  constructor(chainId) {
    this.chainId = chainId;
  }
  async getPools(tokenIn, tokenOut) {
    log.info("In static subgraph provider for V2");
    const bases = BASES_TO_CHECK_TRADES_AGAINST2[this.chainId];
    const basePairs = _16.flatMap(
      bases,
      (base) => bases.map((otherBase) => [base, otherBase])
    );
    if (tokenIn && tokenOut) {
      basePairs.push(
        [tokenIn, tokenOut],
        ...bases.map((base) => [tokenIn, base]),
        ...bases.map((base) => [tokenOut, base])
      );
    }
    const pairs = _16(basePairs).filter(
      (tokens) => Boolean(tokens[0] && tokens[1])
    ).filter(
      ([tokenA, tokenB]) => tokenA.address !== tokenB.address && !tokenA.equals(tokenB)
    ).value();
    const poolAddressSet = /* @__PURE__ */ new Set();
    const subgraphPools = _16(pairs).map(([tokenA, tokenB]) => {
      const poolAddress = PairV2.getAddress(tokenA, tokenB);
      if (poolAddressSet.has(poolAddress)) {
        return void 0;
      }
      poolAddressSet.add(poolAddress);
      const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA];
      return {
        id: poolAddress,
        liquidity: "100",
        token0: {
          id: token0.address
        },
        token1: {
          id: token1.address
        },
        supply: 100,
        reserve: 100,
        reserveUSD: 100
      };
    }).compact().value();
    return subgraphPools;
  }
};
var SUBGRAPH_URL_BY_CHAIN2 = {
  [ChainId.PULSECHAIN]: "https://graph.pulsechain.com/subgraphs/name/pulsechain/pulsexv2",
  [ChainId.PULSECHAIN_TESTNET]: "https://graph.v4.testnet.pulsechain.com/subgraphs/name/pulsechain/pulsexv2"
};
var threshold2 = 0.025;
var PAGE_SIZE2 = 1e3;
var V2SubgraphProvider2 = class {
  constructor(chainId, retries = 2, timeout = 36e4, rollback = true, pageSize = PAGE_SIZE2) {
    this.chainId = chainId;
    this.retries = retries;
    this.timeout = timeout;
    this.rollback = rollback;
    this.pageSize = pageSize;
    const subgraphUrl = SUBGRAPH_URL_BY_CHAIN2[this.chainId];
    if (!subgraphUrl) {
      throw new Error(`No subgraph url for chain id: ${this.chainId}`);
    }
    this.client = new GraphQLClient(subgraphUrl, { fetch });
  }
  async getPools(_tokenIn, _tokenOut, providerConfig) {
    let blockNumber = providerConfig?.blockNumber ? await providerConfig.blockNumber : void 0;
    const query2 = gql`
      query getPools($pageSize: Int!, $id: String) {
        pairs(
          first: $pageSize
          ${blockNumber ? `block: { number: ${blockNumber} }` : ``}
          where: { id_gt: $id }
        ) {
          id
          token0 { id, symbol }
          token1 { id, symbol }
          totalSupply
          trackedReservePLS
          reserveUSD
        }
      }
    `;
    let pools = [];
    log.info(
      `Getting V2 pools from the subgraph with page size ${this.pageSize}${providerConfig?.blockNumber ? ` as of block ${providerConfig?.blockNumber}` : ""}.`
    );
    await retry7(
      async () => {
        const timeout = new Timeout();
        const getPools = async () => {
          let lastId = "";
          let pairs = [];
          let pairsPage = [];
          do {
            await retry7(
              async () => {
                const poolsResult = await this.client.request(query2, {
                  pageSize: this.pageSize,
                  id: lastId
                });
                pairsPage = poolsResult.pairs;
                pairs = pairs.concat(pairsPage);
                lastId = pairs[pairs.length - 1].id;
              },
              {
                retries: this.retries,
                onRetry: (err, retry9) => {
                  pools = [];
                  log.info(
                    { err },
                    `Failed request for page of pools from subgraph. Retry attempt: ${retry9}`
                  );
                }
              }
            );
          } while (pairsPage.length > 0);
          return pairs;
        };
        try {
          const getPoolsPromise = getPools();
          const timerPromise = timeout.set(this.timeout).then(() => {
            throw new Error(
              `Timed out getting pools from subgraph: ${this.timeout}`
            );
          });
          pools = await Promise.race([getPoolsPromise, timerPromise]);
          return;
        } catch (err) {
          throw err;
        } finally {
          timeout.clear();
        }
      },
      {
        retries: this.retries,
        onRetry: (err, retry9) => {
          if (this.rollback && blockNumber && _16.includes(err.message, "indexed up to")) {
            blockNumber = BigInt(blockNumber) - 10n;
            log.info(
              `Detected subgraph indexing error. Rolled back block number to: ${blockNumber}`
            );
          }
          pools = [];
          log.info(
            { err },
            `Failed to get pools from subgraph. Retry attempt: ${retry9}`
          );
        }
      }
    );
    const poolsSanitized = pools.filter((pool) => {
      return parseFloat(pool.trackedReservePLS) > threshold2;
    }).map((pool) => {
      return {
        id: pool.id.toLowerCase(),
        token0: {
          id: pool.token0.id.toLowerCase()
        },
        token1: {
          id: pool.token1.id.toLowerCase()
        },
        supply: parseFloat(pool.totalSupply),
        reserve: parseFloat(pool.trackedReservePLS),
        reserveUSD: parseFloat(pool.reserveUSD)
      };
    });
    log.info(
      `Got ${pools.length} V2 pools from the subgraph. ${poolsSanitized.length} after filtering`
    );
    return poolsSanitized;
  }
};

// src/providers/v2/subgraph-provider-with-fallback.ts
var V2SubgraphProviderWithFallBacks = class {
  /**
   * Creates an instance of V2SubgraphProviderWithFallBacks.
   * @param fallbacks Ordered list of `IV2SubgraphProvider` to try to get pools from.
   */
  constructor(fallbacks) {
    this.fallbacks = fallbacks;
  }
  async getPools(tokenIn, tokenOut, providerConfig) {
    for (let i = 0; i < this.fallbacks.length; i++) {
      const provider = this.fallbacks[i];
      try {
        const pools = await provider.getPools(
          tokenIn,
          tokenOut,
          providerConfig
        );
        return pools;
      } catch (err) {
        log.info(`Failed to get subgraph pools for V2 from fallback #${i}`);
      }
    }
    throw new Error("Failed to get subgraph pools from any providers");
  }
};

// src/providers/v2/uri-subgraph-provider.ts
var V2URISubgraphProvider = class extends URISubgraphProvider {
};

export { ARB_GASINFO_ADDRESS, AlphaRouter, BaseQuoter, BlockConflictError, CACHE_SEED_TOKENS, CHAIN_IDS_LIST, CacheMode, CachedRoute, CachedRoutes, CachingGasStationProvider, CachingTokenListProvider, CachingTokenProviderWithFallback, CachingV1PoolProvider, CachingV1SubgraphProvider2 as CachingV1SubgraphProvider, CachingV2PoolProvider, CachingV2SubgraphProvider2 as CachingV2SubgraphProvider, ChainName, CurrencyAmount, DAI_ON, EIP1559GasPriceProvider, ETHGasStationInfoProvider2 as ETHGasStationInfoProvider, EthEstimateGasSimulator, ExtendedEther, FallbackTenderlySimulator, HEX_PULSECHAIN, HEX_TESTNET, ID_TO_CHAIN_ID, ID_TO_NETWORK_NAME, ID_TO_PROVIDER, IGasPriceProvider, IMetric, IMulticallProvider, INC_PULSECHAIN, INC_TESTNET, IOnChainGasModelFactory, IRouteCachingProvider2 as IRouteCachingProvider, IRouter, ISwapToRatio, IV1GasModelFactory, IV2GasModelFactory, LegacyGasPriceProvider, MAX_UINT160, MULTICALL2_ADDRESS, MapWithLowerCaseKey, MetricLogger, MetricLoggerUnit, MixedQuoter, MixedRoute2 as MixedRoute, MixedRouteWithValidQuote2 as MixedRouteWithValidQuote, NATIVE_CURRENCY, NATIVE_NAMES_BY_ID, NativeCurrencyName, NodeJSCache, OVM_GASPRICE_ADDRESS, OnChainGasPriceProvider, OnChainQuoteProvider, PERMIT2_ADDRESS, PLSX_PULSECHAIN, PLSX_TESTNET, PULSEX_MULTICALL_ADDRESSES, PULSEX_QUOTER_ADDRESSES, PULSEX_SWAP_ROUTER_ADDRESSES, ProviderBlockHeaderError, ProviderGasError, ProviderTimeoutError, QUOTER_V2_ADDRESSES, SUPPORTED_CHAINS, SimulationStatus, Simulator2 as Simulator, StaticGasPriceProvider, StaticV1SubgraphProvider, StaticV2SubgraphProvider, SuccessRateError, SwapRouterProvider, SwapToRatioStatus, SwapType, TOKEN_VALIDATOR_ADDRESSES, TO_PROTOCOL, TenderlySimulator, TokenProvider, TokenValidationResult, TokenValidatorProvider, UNIVERSAL_ROUTER_ADDRESS, URISubgraphProvider, USDC_ON, USDT_ON, UniswapMulticallProvider, V1PoolProvider, V1QuoteProvider, V1Quoter, V1Route, V1RouteWithValidQuote2 as V1RouteWithValidQuote, V1SubgraphProvider2 as V1SubgraphProvider, V1SubgraphProviderWithFallBacks, V1URISubgraphProvider, V1_SUPPORTED, V2PoolProvider, V2QuoteProvider, V2Quoter, V2Route, V2RouteWithValidQuote2 as V2RouteWithValidQuote, V2SubgraphProvider2 as V2SubgraphProvider, V2SubgraphProviderWithFallBacks, V2URISubgraphProvider, V2_SUPPORTED, V3_CORE_FACTORY_ADDRESSES, WETH9, WETH_PULSECHAIN, WNATIVE_ON, WRAPPED_NATIVE_CURRENCY, eDAI_PULSECHAIN, eHEX_PULSECHAIN, eUSDC_PULSECHAIN, eUSDT_PULSECHAIN, eWBTC_PULSECHAIN, log, metric, nativeOnChain, parseAmount, parseFeeAmount, poolToString, routeAmountToString, routeAmountsToString, routeToString, setGlobalLogger, setGlobalMetric, tDAI_TESTNET, unparseFeeAmount, usdGasTokensByChain };
